import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { EditEventModal } from './components/EditEventModal';
import { RejectModal } from './components/RejectModal';
import {
  MapPin,
  Calendar,
  LayoutGrid,
  LogIn,
  LogOut,
  ArrowLeft,
  X,
  Upload,
  Trash2,
  MousePointer2,
  ZoomIn,
  ZoomOut,
  Maximize,
  AlertCircle,
  Plus,
  Save,
  ShoppingCart,
  CheckCircle2,
  Database,
  Loader2,
  Image as ImageIcon,
  RefreshCw,
  Download,
  ShieldAlert,
  Bell,
  BellOff,
  Clock,
  FileText,
  Edit2,
  PenTool,
  Check,
  XCircle,
  User as UserIcon,
  ClipboardList,
  QrCode,
  Eye,
  EyeOff,
  ShoppingCart,
  ChevronDown,
  Mail,
  Building2,
  Settings as SettingsIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import QRCode from 'qrcode';
import { toast, Toaster } from 'sonner';
import { useCollectionPolling, useDocumentPolling } from './hooks/usePolling';
import { useEventsPolling } from './hooks/useEventsPolling';
import api from './lib/api-native';
import { auth } from './lib/auth-native';
import { getCurrentUserId, getCurrentUserEmail, getCurrentUserName } from './lib/auth-helpers';

// Tipi locali per autenticazione
interface User {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName?: string | null;
  photoURL?: string | null;
  role?: 'sponsor' | 'manager';
}

// Interface per notifiche
interface Notification {
  id: string;
  type: 'stand_request' | 'document_upload' | 'payment_confirmation' | 'expiry_warning';
  userId: string;
  standId?: string;
  standLabel?: string;
  message: string;
  read: boolean;
  createdAt: string;
}

// API Cloudflare helper functions
function safeJsonParse<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

// Parse SVG polygon points string into [x,y] array
function parseSvgPoints(points: string): [number, number][] {
  return points.trim().split(/\s+/).map(p => {
    const [x, y] = p.split(',').map(Number);
    return [x, y] as [number, number];
  }).filter(([x, y]) => !isNaN(x) && !isNaN(y));
}

// Get bounding box {minX,minY,maxX,maxY} from SVG points string
function getBoundingBox(points: string): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const pts = parseSvgPoints(points);
  if (pts.length === 0) return null;
  const xs = pts.map(p => p[0]);
  const ys = pts.map(p => p[1]);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

// Two stands are adjacent when their bounding boxes touch (gap ≤ 2px tolerance)
function areStandsAdjacent(a: any, b: any): boolean {
  const pointsA = a?.svg?.points;
  const pointsB = b?.svg?.points;
  if (!pointsA || !pointsB) return false;
  const boxA = getBoundingBox(pointsA);
  const boxB = getBoundingBox(pointsB);
  if (!boxA || !boxB) return false;
  const TOL = 2;
  const xOverlap = boxA.maxX + TOL >= boxB.minX && boxB.maxX + TOL >= boxA.minX;
  const yOverlap = boxA.maxY + TOL >= boxB.minY && boxB.maxY + TOL >= boxA.minY;
  return xOverlap && yOverlap;
}

// Bidirectional competitor conflict: new booker is in protected stand's list OR vice-versa
function isCompetitorConflict(newCompany: string, newCompetitorsList: string, protectedStand: any): boolean {
  const newCo = newCompany.trim().toLowerCase();
  const protCo = (protectedStand.company || '').trim().toLowerCase();
  if (!newCo || !protCo) return false;
  const protList = (protectedStand.competitors || '').toLowerCase();
  const newList = newCompetitorsList.toLowerCase();
  return protList.includes(newCo) || newList.includes(protCo);
}

// Normalize a stand record coming from the API: parse JSON-encoded fields
// (svg, options) so the rest of the code can use them as objects directly.
function normalizeStand(s: any): any {
  if (!s) return s;
  return {
    ...s,
    // snake_case → camelCase mapping
    eventId: s.eventId ?? s.event_id ?? null,
    contractorName: s.contractorName ?? s.contractor_name ?? null,
    sponsorId: s.sponsorId ?? s.sponsor_id ?? null,
    sponsorEmail: s.sponsorEmail ?? s.sponsor_email ?? null,
    sponsorName: s.sponsorName ?? s.sponsor_name ?? null,
    companyLogo: s.companyLogo ?? s.company_logo ?? null,
    originalPrice: s.originalPrice ?? s.original_price ?? null,
    managerRequests: s.managerRequests ?? s.manager_requests ?? null,
    competitorProtectionActive: s.competitorProtectionActive ?? s.competitor_protection_active ?? false,
    standTemplate: s.standTemplate ?? s.stand_template ?? null,
    customDimensions: s.customDimensions ?? (s.custom_dimensions ? safeJsonParse(s.custom_dimensions, null) : null),
    cartExpiresAt: s.cartExpiresAt ?? s.cart_expires_at ?? null,
    cartCreatedAt: s.cartCreatedAt ?? s.cart_created_at ?? null,
    reservedAt: s.reservedAt ?? s.reserved_at ?? null,
    documentsExpiresAt: s.documentsExpiresAt ?? s.documents_expires_at ?? null,
    documentsWarningSent: s.documentsWarningSent ?? s.documents_warning_sent ?? false,
    paymentExpiresAt: s.paymentExpiresAt ?? s.payment_expires_at ?? null,
    reminderSent: s.reminderSent ?? s.reminder_sent ?? false,
    gdprConsent: s.gdprConsent ?? s.gdpr_consent ?? false,
    gdprMarketingConsent: s.gdprMarketingConsent ?? s.gdpr_marketing_consent ?? false,
    positionX: s.positionX ?? s.position_x ?? 0,
    positionY: s.positionY ?? s.position_y ?? 0,
    documents: typeof s.documents === 'string' && s.documents ? safeJsonParse(s.documents, {}) : (s.documents ?? {}),
    svg: typeof s.svg === 'string' && s.svg ? safeJsonParse(s.svg, null) : s.svg,
    options: typeof s.options === 'string' && s.options ? safeJsonParse(s.options, []) : (Array.isArray(s.options) ? s.options : []),
  };
}

async function apiGet(endpoint: string) {
  const sessionToken = localStorage.getItem('session_token');
  const headers: HeadersInit = {};
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const res = await fetch(endpoint, { headers });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

async function apiPost(endpoint: string, data: Record<string, unknown>): Promise<unknown> {
  const sessionToken = localStorage.getItem('session_token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const res = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

async function apiPatch(endpoint: string, data: Record<string, unknown>): Promise<unknown> {
  const sessionToken = localStorage.getItem('session_token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const res = await fetch(endpoint, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    let bodyText = '';
    try { bodyText = await res.text(); } catch { /* ignore */ }
    throw new Error(`API Error: ${res.status} ${endpoint} → ${bodyText}`);
  }
  return res.json();
}

async function generateEventQRCode(eventId: string): Promise<string> {
  const eventUrl = `${window.location.origin}/?eventId=${eventId}`;
  return await QRCode.toDataURL(eventUrl);
}

async function apiDelete(endpoint: string) {
  const res = await fetch(endpoint, { method: 'DELETE' });
  if (!res.ok) {
    let bodyText = '';
    try { bodyText = await res.text(); } catch { /* ignore */ }
    throw new Error(`API Error: ${res.status} ${endpoint} → ${bodyText}`);
  }
  return res.json();
}

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface DatabaseErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
}

function handleDatabaseError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: DatabaseErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    operationType,
    path
  };
  console.error('Database Error:', error, errInfo);

  if (errInfo.error.includes('permission-denied') || errInfo.error.includes('Missing or insufficient permissions')) {
    toast.error("Errore di permessi Database. Verifica di essere loggato correttamente.");
    throw new Error(JSON.stringify(errInfo));
  } else {
    toast.error(`Errore Database: ${errInfo.error}`);
  }
}

async function testConnection() {
  try {
    await apiGet('/api/health');
    console.log('API connection successful');
  } catch (error) {
    console.error('API Connection failed:', error);
  }
}
testConnection();

const compressImage = (base64Str: string, maxWidth = 2048, maxHeight = 2048, quality = 0.8): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(base64Str);
        return;
      }
      // Fill with white background to prevent transparent PNGs from becoming black
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      // Compress to WebP to save space while maintaining high quality
      resolve(canvas.toDataURL('image/webp', quality));
    };
    img.onerror = (err) => reject(err);
  });
};

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, errorInfo: string }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorInfo: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorInfo: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      let displayMessage = "Si è verificato un errore imprevisto.";
      try {
        const parsed = JSON.parse(this.state.errorInfo);
        if (parsed.error) displayMessage = `Errore Database: ${parsed.error}`;
      } catch (e) {
        // Not a JSON error
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ops! Qualcosa è andato storto</h2>
            <p className="text-gray-600 mb-6">{displayMessage}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-all"
            >
              Ricarica la pagina
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Mock data based on the screenshot, adapted for medical events
type MedicalEvent = {
  id: string;
  title: string;
  venue: string;
  address: string;
  coords: [number, number];
  date: string;
  description: string;
  image: string;
  coverImage?: string;
  floorPlanImage?: string;
  floorPlanConfig?: any;
  qrCode?: string;
  managerEmails?: string;
  status?: 'draft' | 'published' | 'closed';
};

// Eventi mock rimossi - il sistema ora usa solo database D1
const courses: MedicalEvent[] = [];

type StandStatus = 
  | 'available'           // 🟢 Libero
  | 'pending'             // 🟡 Richiesta inviata, in attesa di revisione documenti
  | 'document_review'     // 🟡 Documenti caricati, Admin sta verificando
  | 'payment_pending'     // 🟠 Documenti OK, timer 96h per pagamento ATTIVO
  | 'payment_verification'// 🟡 Bonifico caricato, Admin verifica pagamento
  | 'occupied'            // 🟢 Pagamento confermato, stand assegnato
  | 'rejected'            // 🔴 Rifiutato (documenti o pagamento)
  | 'expired'             // ⚫ Scaduto timer pagamento

type StandDocument = {
  url: string;
  status: 'pending' | 'approved' | 'rejected';
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  name?: string;
};

type Stand = {
  id: string;
  label: string;
  price: number;
  status: StandStatus;
  company?: string;
  company_logo?: string;
  options?: string[];
  manager_requests?: string;
  competitors?: string;
  competitor_protection_active?: boolean;
  stand_template?: string;
  custom_dimensions?: { width: number; length: number } | null;
  stand_preview_image?: string;
  cartCreatedAt?: string | null;
  cartExpiresAt?: string | null;
  sponsorId?: string | null;
  sponsorEmail?: string | null;
  sponsorName?: string | null;
  reminderSent?: boolean;
  originalPrice?: number;
  documents?: {
    [key: string]: StandDocument;
  };
  contractorName?: string;
  gdprMarketingConsent?: boolean;
  gdprConsent?: boolean;
  
  // Timeline del flusso
  reservedAt?: string;           // Quando ha fatto la prenotazione iniziale
  documentsSubmittedAt?: string; // Quando ha caricato tutti i documenti obbligatori
  documentsApprovedAt?: string;  // Quando TU hai approvato i documenti ⭐ TIMER PARTE QUI
  documentsExpiresAt?: string;   // 7 giorni per caricare documenti da approval
  documentsWarningSent?: boolean; // Warning 24h prima inviato
  paymentExpiresAt?: string | null;     // documentsApprovedAt + 96 ore
  paymentReceiptUploadedAt?: string;
  paymentConfirmedAt?: string;

  svg: {
    points: string;
    text: [number, number];
  };
};

const Stand2x2Icon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Floor */}
    <path d="M20 70 L50 85 L80 70 L50 55 Z" fill="#f3f4f6" stroke="#9ca3af" />
    {/* Back Wall */}
    <path d="M20 70 L20 30 L50 45 L50 85 Z" fill="#e5e7eb" stroke="#9ca3af" />
    <path d="M50 85 L50 45 L80 30 L80 70 Z" fill="#d1d5db" stroke="#9ca3af" />
    {/* Counter */}
    <path d="M45 75 L60 82 L75 75 L60 68 Z" fill="#f9fafb" stroke="#6b7280" />
    <path d="M45 75 L45 65 L60 72 L60 82 Z" fill="#f3f4f6" stroke="#6b7280" />
    <path d="M60 82 L60 72 L75 65 L75 75 Z" fill="#e5e7eb" stroke="#6b7280" />
  </svg>
);

const Stand3x2Icon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Floor */}
    <path d="M10 70 L50 90 L90 70 L50 50 Z" fill="#f3f4f6" stroke="#9ca3af" />
    {/* Back Wall */}
    <path d="M10 70 L10 30 L90 30 L90 70 Z" fill="#e5e7eb" stroke="#9ca3af" />
    {/* Counter */}
    <path d="M55 75 L75 85 L90 77 L70 67 Z" fill="#f9fafb" stroke="#6b7280" />
    <path d="M55 75 L55 65 L75 75 L75 85 Z" fill="#f3f4f6" stroke="#6b7280" />
    <path d="M75 85 L75 75 L90 67 L90 77 Z" fill="#e5e7eb" stroke="#6b7280" />
    {/* Stools */}
    <circle cx="30" cy="75" r="4" fill="#6b7280" />
    <line x1="30" y1="75" x2="30" y2="85" stroke="#6b7280" />
    <circle cx="45" cy="75" r="4" fill="#6b7280" />
    <line x1="45" y1="75" x2="45" y2="85" stroke="#6b7280" />
  </svg>
);

const Stand6x2Icon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Floor */}
    <path d="M5 70 L50 90 L95 70 L50 50 Z" fill="#f3f4f6" stroke="#9ca3af" />
    {/* Long Wall */}
    <path d="M5 70 L5 35 L95 35 L95 70 Z" fill="#e5e7eb" stroke="#9ca3af" />
    {/* Counter */}
    <path d="M65 75 L85 85 L95 80 L75 70 Z" fill="#f9fafb" stroke="#6b7280" />
    <path d="M65 75 L65 65 L85 75 L85 85 Z" fill="#f3f4f6" stroke="#6b7280" />
    <path d="M85 85 L85 75 L95 70 L95 80 Z" fill="#e5e7eb" stroke="#6b7280" />
  </svg>
);

const Stand5x4Icon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Floor */}
    <path d="M10 60 L50 80 L90 60 L50 40 Z" fill="#f3f4f6" stroke="#9ca3af" />
    {/* L-Wall */}
    <path d="M10 60 L10 20 L50 40 L50 80 Z" fill="#e5e7eb" stroke="#9ca3af" />
    <path d="M50 80 L50 40 L90 20 L90 60 Z" fill="#d1d5db" stroke="#9ca3af" />
    {/* Arch */}
    <path d="M10 20 L10 10 L90 10 L90 20 Z" fill="#9ca3af" />
    {/* Counter */}
    <path d="M30 70 L45 77 L55 72 L40 65 Z" fill="#f9fafb" stroke="#6b7280" />
    <path d="M30 70 L30 62 L45 69 L45 77 Z" fill="#f3f4f6" stroke="#6b7280" />
    <path d="M45 77 L45 69 L55 64 L55 72 Z" fill="#e5e7eb" stroke="#6b7280" />
  </svg>
);

const StandCustomIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Floor grid */}
    <path d="M20 70 L50 85 L80 70 L50 55 Z" fill="#f3f4f6" stroke="#9ca3af" strokeDasharray="2 2" />
    <path d="M35 62.5 L65 77.5 M65 62.5 L35 77.5" stroke="#9ca3af" strokeDasharray="2 2" />
    {/* Measuring lines */}
    <path d="M15 65 L45 80" stroke="#3b82f6" strokeWidth="2" />
    <path d="M85 65 L55 80" stroke="#3b82f6" strokeWidth="2" />
    <text x="25" y="78" fontSize="8" fill="#3b82f6" transform="rotate(26.5 25 78)">m</text>
    <text x="75" y="78" fontSize="8" fill="#3b82f6" transform="rotate(-26.5 75 78)">m</text>
  </svg>
);

type StandConfigTemplate = {
  id: string;
  name: string;
  availableOptions: Option[];
  allowedStandTemplates: string[];
  templatePrices: { [key: string]: number };
};

const STANDS_TEMPLATES = [
  {
    id: '2x2',
    name: '2x2 – Spazio preallestito (4sqm)',
    description: 'Small compact booth with a single back wall and a small front reception counter.',
    icon: Stand2x2Icon,
    templateImage: '/Template/2x2.png'
  },
  {
    id: '3x2',
    name: '3x2 – Spazio preallestito (6sqm)',
    description: 'Medium booth with a wide back wall, a reception counter, and two tall stools.',
    icon: Stand3x2Icon,
    templateImage: '/Template/3x2.png'
  },
  {
    id: '6x2',
    name: '6x2 – Spazio preallestito (12sqm)',
    description: 'Wide booth with a very long back wall, a reception counter, and a stool.',
    icon: Stand6x2Icon,
    templateImage: '/Template/12sqm.png'
  },
  {
    id: '5x4',
    name: '5x4 – Spazio preallestito (20sqm)',
    description: 'Large premium booth with an L-shaped back wall, an overhead arch structure, a reception counter, a small meeting table with seating.',
    icon: Stand5x4Icon,
    templateImage: '/Template/20sqm.png'
  },
  {
    id: 'custom',
    name: 'Personalizzato (m x m)',
    description: 'Inserisci le dimensioni desiderate per il tuo stand.',
    icon: StandCustomIcon,
    templateImage: ''
  }
];

type Option = {
  name: string;
  price: number;
};

const DEFAULT_OPTIONS: Option[] = [
  { name: "Allacciamento elettrico (3kW)", price: 150 },
  { name: "Connessione Wi-Fi dedicata", price: 80 },
  { name: "Noleggio arredi (tavolo + 2 sedie)", price: 120 },
  { name: "Pulizia giornaliera", price: 50 },
  { name: "Hostess/Steward (1 persona)", price: 200 },
  { name: "Servizio catering (lunch box)", price: 35 }
];

const EVENT_DOCUMENT_TYPES = [
  { id: 'floor_plan', label: 'Planimetria Padiglione' },
  { id: 'tech_regulations', label: 'Regolamento Tecnico' },
  { id: 'setup_schedule', label: 'Orari Allestimento/Disallestimento' },
  { id: 'logistics_guide', label: 'Guida Logistica' },
  { id: 'form_a', label: 'Modulo A (Antincendio) - Vuoto' },
  { id: 'form_b', label: 'Modulo B (Impianto Elettrico) - Vuoto' }
];

const STAND_DOCUMENT_TYPES = [
  { id: 'participation_app', label: 'Domanda di Partecipazione', required: true },
  { id: 'privacy_notice', label: 'Informativa Privacy/GDPR', required: true },
  { id: 'tech_notes', label: 'Note Tecniche / Variazioni', required: false },
  { id: 'bank_receipt', label: 'Distinta Bonifico (CRO/TRN)', required: true },
  { id: 'stand_project', label: 'Progetto Stand (DWG/PDF)', required: false },
  { id: 'durc', label: 'DURC e Certificati Materiali', required: true },
  { id: 'form_a_filled', label: 'Modulo A (Antincendio) - Compilato', required: true },
  { id: 'form_b_filled', label: 'Modulo B (Impianto Elettrico) - Compilato', required: true }
];

const ADMIN_EMAILS = [
  "m.palazzo.summeet@gmail.com",
  "v.alberio@summeet.it",
  "j.alzati@summeet.it",
  "c.balia@summeet.it",
  "b.bardotti@summeet.it",
  "a.barluzzi@summeet.it",
  "e.bergamini@summeet.it",
  "m.bonfiglio@summeet.it",
  "g.bossi@summeet.it",
  "m.buccianti@summeet.it",
  "e.busnelli@summeet.it",
  "s.buzzi@summeet.it",
  "m.calveri@summeet.it",
  "m.cerfoglia@summeet.it",
  "b.coco@summeet.it",
  "l.colombi@summeet.it",
  "a.colombo@summeet.it",
  "c.cordless@summeet.it",
  "a.cotugno@summeet.it",
  "a.criminisi@summeet.it",
  "l.crivellari@summeet.it",
  "d.dagostino@summeet.it",
  "m.decarli@summeet.it",
  "e.difelice@summeet.it",
  "f.dimuro@summeet.it",
  "s.diroberto@summeet.it",
  "m.facciolati@summeet.it",
  "s.filippone@summeet.it",
  "s.fortunato@summeet.it",
  "l.franzetti@summeet.it",
  "b.frattini@summeet.it",
  "f.frontini@summeet.it",
  "l.gadioli@summeet.it",
  "g.gallazzi@summeet.it",
  "m.gambarini@summeet.it",
  "g.giannarelli@summeet.it",
  "a.livraga@summeet.it",
  "l.logistica@summeet.it",
  "g.lorenzon@summeet.it",
  "c.mafrici@summeet.it",
  "l.marazzi@summeet.it",
  "e.mascetti@summeet.it",
  "c.mercuri@summeet.it",
  "d.morabito@summeet.it",
  "f.moretti@summeet.it",
  "s.munari@summeet.it",
  "m.palazzo@summeet.it",
  "p.palermo@summeet.it",
  "l.pallucca@summeet.it",
  "g.parrilla@summeet.it",
  "a.pascoli@summeet.it",
  "m.passeri@summeet.it",
  "a.pavan@summeet.it",
  "p.pion@summeet.it",
  "g.raimondi@summeet.it",
  "e.ravelli@summeet.it",
  "f.riolo@summeet.it",
  "g.russo@summeet.it",
  "c.samperi@summeet.it",
  "l.silini@summeet.it",
  "a.silvestri@summeet.it",
  "g.torzullo@summeet.it",
  "t.traficante@summeet.it",
  "m.trovo@summeet.it",
  "i.ventimiglia@summeet.it",
  "j.vicenti@summeet.it",
  "a.zaffaroni@summeet.it"
];

const mockStands: Stand[] = [
  { 
    id: 'stand_1', 
    label: 'Stand 1', 
    price: 5582, 
    status: 'occupied', 
    company: 'Tech Solutions', 
    company_logo: 'https://picsum.photos/seed/tech/40/40',
    svg: { points: "74,93 149,107 121,266 40,250", text: [55, 163] } 
  },
  { 
    id: 'stand_2', 
    label: 'Stand 2', 
    price: 3123, 
    status: 'pending', 
    company: 'BioPharma Inc.',
    options: ['Allacciamento elettrico (3kW)', 'Pulizia giornaliera'],
    manager_requests: 'Vorremmo posizionare un totem all\'ingresso dello stand.',
    svg: { points: "149,107 300,127 288,286 121,266", text: [179, 203] } 
  },
  { 
    id: 'stand_3', 
    label: 'Stand 3', 
    price: 2400, 
    status: 'available', 
    svg: { points: "300,127 524,127 538,286 288,286", text: [360, 213] } 
  },
  { 
    id: 'stand_4', 
    label: 'Stand 4', 
    price: 4172, 
    status: 'available', 
    svg: { points: "524,127 673,108 699,267 538,286", text: [554, 203] } 
  },
  { 
    id: 'stand_5', 
    label: 'Stand 5', 
    price: 5955, 
    status: 'available', 
    svg: { points: "747,93 673,108 699,267 782,250", text: [685, 163] } 
  }
];

export default function AppWrapper() {
  return (
    <ErrorBoundary>
      <Toaster position="top-center" richColors />
      <App />
    </ErrorBoundary>
  );
}

// Helper component to fit map bounds to markers
function FitBounds({ events }: { events: any[] }) {
  const map = useMap();
  const lastEventsHashRef = useRef<string>('');

  useEffect(() => {
    // Prevent infinite loops - only fit bounds if events actually changed
    const eventsHash = JSON.stringify(events.map(e => e.coords));
    if (eventsHash === lastEventsHashRef.current) {
      return;
    }
    lastEventsHashRef.current = eventsHash;

    const validCoords = events
      .filter(e => e.coords && Array.isArray(e.coords) && e.coords.length === 2)
      .map(e => e.coords);

    if (validCoords.length > 0) {
      console.log('Fitting map bounds', validCoords);
      const bounds = L.latLngBounds(validCoords);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [events]); // Remove 'map' from dependencies to prevent loop
  return null;
}

function App() {
  const [events, setEvents] = useState(courses);
  const lastEventsJsonRef = useRef<string>(''); // Prevent re-renders when data hasn't changed
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showCartCreatedModal, setShowCartCreatedModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const handleBackToEvents = () => {
    setSelectedEventId(null);
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', window.location.pathname);
    }
  };

  const handleSelectEvent = (id: string) => {
    setSelectedEventId(id);
    if (typeof window !== 'undefined') {
      const newUrl = `${window.location.pathname}?eventId=${id}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
  };
  const [isAdmin, setIsAdmin] = useState(false);
  const isAdminRef = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [stands, setStands] = useState<Stand[]>(mockStands);
  const [sponsorStands, setSponsorStands] = useState<Stand[]>([]);
  const [configTemplates, setConfigTemplates] = useState<StandConfigTemplate[]>([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [bgUrl, setBgUrl] = useState('');
  const [availableOptions, setAvailableOptions] = useState<Option[]>(DEFAULT_OPTIONS);
  const [viewingStand, setViewingStand] = useState<Stand | null>(null);
  const [targetStandId, setTargetStandId] = useState<string | null>(null);
  const [showAdminEvents, setShowAdminEvents] = useState(false);
  const [showAdminRequests, setShowAdminRequests] = useState(false);
  const [notifActiveTab, setNotifActiveTab] = useState<'all' | 'unread'>('unread');
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [newEvent, setNewEvent] = useState({
    name: '',
    venue: '',
    address: '',
    coords: [45.4642, 9.1900] as [number, number],
    date: '',
    description: '',
    image: 'https://picsum.photos/seed/new/800/600',
    coverImage: '',
    floorPlanImage: '',
    managerEmails: ''
  });
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [managerRequests, setManagerRequests] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [allowedStandTemplates, setAllowedStandTemplates] = useState<string[]>(STANDS_TEMPLATES.map(t => t.id));
  const [templatePrices, setTemplatePrices] = useState<{ [key: string]: number }>(
    STANDS_TEMPLATES.reduce((acc, t) => ({ ...acc, [t.id]: 1000 }), {})
  );
  const [selectedTemplate, setSelectedTemplate] = useState(STANDS_TEMPLATES[0]);
  const [customWidth, setCustomWidth] = useState<number>(2);
  const [customLength, setCustomLength] = useState<number>(2);
  const [bgDimensions, setBgDimensions] = useState({ width: 800, height: 300 });
  const [lastCreatedEvent, setLastCreatedEvent] = useState<{id: string, name: string, qrCode: string} | null>(null);
  const [adminPendingStands, setAdminPendingStands] = useState<(Stand & { eventId: string })[]>([]);
  const [rejectingStand, setRejectingStand] = useState<Stand | null>(null);
  const [showAddBookedModal, setShowAddBookedModal] = useState(false);
  const [isDrawingBooked, setIsDrawingBooked] = useState(false);
  const [bookedStandConfig, setBookedStandConfig] = useState<{ label: string, company: string, price: number, options: string[], templateId: string, customWidth: number, customLength: number }>({ label: '', company: '', price: 1000, options: [], templateId: STANDS_TEMPLATES[0].id, customWidth: 2, customLength: 2 });
  const lastNotifTimeRef = useRef<string | null>(null);

  // Refs for tracking remote state to prevent infinite sync loops
  const lastRemoteOptionsRef = useRef<{ [eventId: string]: string }>({});
  const lastRemotePricesRef = useRef<{ [eventId: string]: string }>({});
  const lastRemoteTemplatesRef = useRef<{ [eventId: string]: string }>({});
  const isCurrentlyFetchingRef = useRef<boolean>(false);

  useEffect(() => {
    if (notifications.length > 0) {
      const newest = notifications[0];
      if (lastNotifTimeRef.current && newest.createdAt > lastNotifTimeRef.current && !newest.read) {
        toast.info(newest.message, {
          icon: <Bell className="text-blue-500 w-4 h-4" />,
          action: {
            label: 'Vedi',
            onClick: () => setShowNotifications(true)
          }
        });
      }
      lastNotifTimeRef.current = newest.createdAt;
    }
  }, [notifications]);

  useEffect(() => {
    if (bgUrl) {
      const img = new Image();
      img.onload = () => {
        setBgDimensions({ width: img.width, height: img.height });
      };
      img.src = bgUrl;
    }
  }, [bgUrl]);

  useEffect(() => {
    if (!allowedStandTemplates.includes(selectedTemplate.id)) {
      const firstAllowed = STANDS_TEMPLATES.find(t => allowedStandTemplates.includes(t.id));
      if (firstAllowed) setSelectedTemplate(firstAllowed);
    }
  }, [allowedStandTemplates, selectedTemplate]);
  // Auth Modal States
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [viewAsSponsor, setViewAsSponsor] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [eventFormData, setEventFormData] = useState({
    name: '',
    date: '',
    description: '',
    location: '',
    venue: ''
  });
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'forgot_password'>('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authCompanyName, setAuthCompanyName] = useState('');
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isLoggingOutRef = useRef(false);

  // Auth state con session storage locale (sostituisce Firebase)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const sessionToken = localStorage.getItem('session_token');

        if (!sessionToken) {
          setUser(null);
          setIsAuthReady(true);
          setIsAdmin(false);
          isAdminRef.current = false;
          return;
        }

        // Verifica sessione con API Cloudflare
        const authData = await apiGet('/api/auth/me');

        if (authData && authData.id) {
          const userData = {
            uid: authData.id,
            email: authData.email,
            emailVerified: true,
            displayName: authData.displayName || authData.email?.split('@')[0],
            photoURL: authData.photo_url || null,
            role: authData.role || 'sponsor'
          };

          setUser(prev => (prev?.uid === userData.uid && prev?.role === userData.role) ? prev : userData);
          setIsAuthReady(true);

          // Check admin via role (not email list)
          if (userData.role === 'manager') {
            setIsAdmin(true);
            isAdminRef.current = true;
            console.log('Admin user authenticated:', userData.email);
          } else {
            setIsAdmin(false);
            isAdminRef.current = false;
          }
        } else {
          // Sessione invalida
          localStorage.removeItem('session_token');
          setUser(null);
          setIsAuthReady(true);
          setIsAdmin(false);
          isAdminRef.current = false;
        }
      } catch (error) {
        // API auth non disponibile - fallback a no auth
        console.warn('Auth API unavailable:', error);
        setUser(null);
        setIsAuthReady(true);
        setIsAdmin(false);
        isAdminRef.current = false;
      }
    };

    checkAuth();
    const interval = setInterval(checkAuth, 60000); // Poll ogni minuto

    return () => clearInterval(interval);
  }, []);

  // Notifications listener
  useEffect(() => {
    if (!user || isLoggingOutRef.current) {
      setNotifications([]);
      return;
    }

    const userId = isAdminRef.current ? 'manager' : (user.uid || getCurrentUserId(auth, user));
    console.log('Notification polling started:', { userId, isAdmin: isAdminRef.current, userEmail: user.email });

    let timer: NodeJS.Timeout | null = null;
    let isMounted = true;

    const pollNotifications = async () => {
      if (!isMounted || isLoggingOutRef.current) return;

      try {
        const data = await apiGet(`/api/notifications?userId=${userId}`);
        if (!isMounted || isLoggingOutRef.current) return;

        const rawNotifs = Array.isArray(data) ? data : [];
        const notifs: Notification[] = rawNotifs
          .map((n: any) => ({
            ...n,
            read: n.read === 1 || n.read === true,
            createdAt: n.createdAt ?? n.created_at,
            relatedEventId: n.relatedEventId ?? n.related_event_id ?? null,
            relatedStandId: n.relatedStandId ?? n.related_stand_id ?? null,
            userId: n.userId ?? n.user_id ?? null,
          }))
          .sort((a: Notification, b: Notification) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        console.log('Notifications received', {
          total: notifs.length,
          unread: notifs.filter(n => !n.read).length
        });

        setNotifications(notifs);
      } catch (error) {
        if (isMounted && !isLoggingOutRef.current) {
          handleDatabaseError(error, OperationType.LIST, 'notifications');
        }
      }
    };

    // Prima chiamata immediata
    pollNotifications();

    // Polling ogni 5 secondi
    timer = setInterval(pollNotifications, 5000);

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [user?.uid]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    
    if (authMode === 'register' && !gdprConsent) {
      setAuthError("Devi acconsentire al trattamento dei dati (GDPR) per registrarti.");
      return;
    }

    setAuthLoading(true);

    try {
      if (authMode === 'register') {
        const result = await api.auth.register(authEmail, authPassword, authCompanyName);
        if (result.success && result.user) {
          setUser({
            uid: result.user.id,
            email: result.user.email,
            emailVerified: true,
            displayName: result.user.displayName,
            role: result.user.role
          });
        }
        toast.success('Registrazione completata con successo!');
        setShowAuthModal(false);
      } else if (authMode === 'login') {
        const result = await api.auth.login(authEmail, authPassword);

        if (result.success && result.user) {
          const loggedInUser: User = {
            uid: result.user.id,
            email: result.user.email,
            emailVerified: true,
            displayName: result.user.displayName,
            role: result.user.role
          };
          setUser(loggedInUser);

          // Force immediate admin role update
          if (loggedInUser.role === 'manager') {
            setIsAdmin(true);
            isAdminRef.current = true;
          } else {
            setIsAdmin(false);
            isAdminRef.current = false;
          }

          // Salva session token e user in localStorage per checkAuth al reload
          if (typeof window !== 'undefined') {
            // Il token è già gestito dal sistema auth nativo
            if (result.token) {
              localStorage.setItem('session_token', result.token);
            }

            // Salva anche user object
            localStorage.setItem('user', JSON.stringify(loggedInUser));
          }
        }

        toast.success('Accesso effettuato con successo!');
        setShowAuthModal(false);
      } else if (authMode === 'forgot_password') {
        await api.auth.resetPassword(authEmail);
        toast.success('Email di recupero password inviata!');
        setAuthMode('login');
      }
    } catch (error: unknown) {
      console.error('Authentication error:', error);
      if (error instanceof Error && 'code' in error) {
        const authError = error as { code: string; message: string };
        if (authError.code === 'auth/operation-not-allowed') {
          setAuthError('Metodo Email/Password non disponibile. Usa il provider di autenticazione configurato.');
        } else if (authError.code === 'auth/email-already-in-use') {
          setAuthError('Questa email è già registrata. Effettua il login.');
        } else if (authError.code === 'auth/invalid-credential' || authError.code === 'auth/wrong-password') {
          setAuthError('Password errata o credenziali non valide.');
        } else if (authError.code === 'auth/user-not-found') {
          setAuthError('Utente non registrato. Usa la scheda "Registrati" per creare il tuo account admin la prima volta.');
        } else if (authError.code === 'auth/weak-password') {
          setAuthError('La password deve essere di almeno 6 caratteri.');
        } else {
          setAuthError(authError.message || 'Si è verificato un errore.');
        }
      } else {
        setAuthError('Si è verificato un errore durante l\'autenticazione.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    // Previeni click multipli
    if (isLoggingOut) {
      console.log('Logout già in corso, ignoro click');
      return;
    }

    // Check se c'è token - se no, cleanup locale solo
    const hasToken = localStorage.getItem('session_token');
    if (!hasToken) {
      console.log('Nessun token presente, cleanup locale immediato');
      setUser(null);
      setIsAdmin(false);
      setShowAuthModal(false);
      setNotifications([]); // Ferma polling notifiche
      return;
    }

    setIsLoggingOut(true);
    isLoggingOutRef.current = true; // Flag per useEffect

    try {
      await api.auth.logout();
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // SEMPRE pulisci stato locale
      setNotifications([]); // Ferma polling notifiche PRIMA di setUser
      setUser(null); // Questo triggererà cleanup useEffect notifiche
      setIsAdmin(false);
      setShowAuthModal(false);
      setIsLoggingOut(false);

      // Piccolo delay per permettere cleanup useEffect
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 100);
    }
  };

  const handleEditEvent = (eventId: string) => {
    const eventToEdit = events.find(e => e.id === eventId);
    if (!eventToEdit) return;

    setEditingEventId(eventId);
    setEventFormData({
      name: eventToEdit.title || eventToEdit.name || '',
      date: eventToEdit.date || '',
      description: eventToEdit.description || '',
      location: eventToEdit.location || eventToEdit.venue || '',
      venue: eventToEdit.venue || eventToEdit.location || ''
    });
  };

  const handleSaveEvent = async () => {
    if (!editingEventId) return;

    try {
      await apiPatch(`/api/events/${editingEventId}`, {
        name: eventFormData.name,
        date: eventFormData.date,
        description: eventFormData.description,
        location: eventFormData.location,
        venue: eventFormData.venue
      });

      // Refresh events
      const response = await apiGet('/api/events');
      const transformedEvents = (Array.isArray(response) ? response : []).map((event: any) => ({
        ...event,
        title: event.name || event.name || '',
        coords: event.coords && Array.isArray(JSON.parse(event.coords))
          ? JSON.parse(event.coords)
          : [45.4408, 12.3155]
      }));

      setEvents(transformedEvents);

      setEditingEventId(null);
      toast.success('Evento aggiornato con successo!');
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Errore durante l\'aggiornamento dell\'evento');
    }
  };


  // Fetch Config Templates
  useEffect(() => {
    if (!isAuthReady || !isAdmin) return;

    let timer: NodeJS.Timeout | null = null;
    let isMounted = true;

    const pollTemplates = async () => {
      if (!isMounted) return;

      try {
        const data = await apiGet('/api/stand-templates');
        if (!isMounted) return;

        const templatesList = Array.isArray(data) ? data : [];
        setConfigTemplates(templatesList as StandConfigTemplate[]);
      } catch (error) {
        if (isMounted) {
          handleDatabaseError(error, OperationType.LIST, 'stand_config_templates');
        }
      }
    };

    pollTemplates();
    timer = setInterval(pollTemplates, 10000); // Admin data changes less frequently

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [isAuthReady, isAdmin]);

  // Fetch Events (no polling - events don't change every 5 seconds)
  useEffect(() => {
    if (!isAuthReady) return;

    let isMounted = true;

    const fetchEvents = async () => {
      if (!isMounted) return;

      try {
        const data = await apiGet('/api/events');
        if (!isMounted) return;

        const eventsList = Array.isArray(data) ? data : [];
        console.log("Fetched events:", eventsList);

        // Transform API data to match frontend format
        const transformedEvents = eventsList.map((event: any) => ({
          ...event,
          id: event.id,
          title: event.name || event.name || '',
          coords: event.coords && Array.isArray(JSON.parse(event.coords))
            ? JSON.parse(event.coords)
            : [45.4408, 12.3155], // Default Venice coordinates
          description: event.description || '',
          venue: event.venue || event.address || '',
          // Map database snake_case to frontend camelCase
          coverImage: event.cover_image || '',
          floorPlanImage: event.floor_plan_image || '',
          floorPlanConfig: event.floor_plan_config ? JSON.parse(event.floor_plan_config) : null,
          qrCode: event.qr_code || '',
          managerEmails: event.manager_emails || ''
        }));

        if (isMounted) {
          setEvents(transformedEvents);
        }
      } catch (error) {
        if (isMounted) {
          handleDatabaseError(error, OperationType.GET, 'events');
        }
      }
    };

    fetchEvents();

    return () => {
      isMounted = false;
    };
  }, [isAuthReady]);

  // Fetch Stands
  useEffect(() => {
    if (!isAuthReady || !selectedEventId) {
      setStands([]);
      return;
    }

    let timer: NodeJS.Timeout | null = null;
    let isMounted = true;

    const pollStands = async () => {
      if (!isMounted) return;

      try {
        const data = await apiGet(`/api/events/${selectedEventId}/stands`);
        if (!isMounted) return;

        const rawList = Array.isArray(data) ? data : [];
        const standsList = rawList.map(normalizeStand);
        setStands(standsList as Stand[]);

        // Check scadenze documenti (7 giorni)
        const now = new Date();
        standsList.forEach(async (stand: Stand) => {
          if (stand.status === 'document_review' && stand.documentsExpiresAt) {
            const expiryDate = new Date(stand.documentsExpiresAt);
            const hoursUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60);

            // Warning 24h prima (tra 24-48h)
            if (hoursUntilExpiry <= 48 && hoursUntilExpiry > 24 && !stand.documentsWarningSent) {
              // Notifica + mail warning
              await api.notifications.create({
                userId: stand.sponsorId || 'manager',
                type: 'documents_expiry_warning',
                message: `⚠️ Documenti scadono tra 24h! Carica i documenti per lo stand ${stand.label} entro ${expiryDate.toLocaleString('it-IT')}.`,
                read: false,
                relatedEventId: selectedEventId,
                relatedStandId: stand.id
              });

              await api.stands.update(stand.id, { documentsWarningSent: true });

              if (stand.sponsorEmail) {
                await apiPost('/api/mail', {
                  to: stand.sponsorEmail,
                  message: {
                    subject: `⚠️ ULTIMO AVVISO - Documenti Stand ${stand.label}`,
                    html: `<p>Ti rimangono <b>24 ore</b> per caricare i documenti!</p><p>Scadenza: ${expiryDate.toLocaleString('it-IT')}</p><p>Dopo scadenza la prenotazione verrà cancellata automaticamente.</p>`
                  }
                });
              }
            }

            // Scaduto (> 7 giorni) - cancella
            if (hoursUntilExpiry <= 0) {
              await api.stands.update(stand.id, {
                status: 'available',
                company: null,
                documents: {},
                sponsorId: null
              });

              // Mail cancellazione
              if (stand.sponsorEmail) {
                await apiPost('/api/mail', {
                  to: stand.sponsorEmail,
                  message: {
                    subject: `⏰ Prenotazione Cancellata — Stand ${stand.label}`,
                    text: `La tua prenotazione per lo stand ${stand.label} è stata cancellata per mancato caricamento documenti entro 7 giorni.`,
                    html: `<p>La tua prenotazione per lo stand <b>${stand.label}</b> è stata cancellata.</p><p>Motivo: documenti non caricati entro 7 giorni dalla proposta accettata.</p>`
                  }
                });
              }
            }
          }
        });
      } catch (error) {
        if (isMounted) {
          handleDatabaseError(error, OperationType.GET, `stands`);
        }
      }
    };

    pollStands();
    timer = setInterval(pollStands, 3000); // Stands change frequently

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [isAuthReady, selectedEventId]);

  // Fetch all stands for sponsor (for cart, independent of selectedEventId)
  useEffect(() => {
    if (!isAuthReady || !user?.uid || isAdminRef.current) {
      setSponsorStands([]);
      return;
    }

    let isMounted = true;
    let timer: NodeJS.Timeout | null = null;

    const pollSponsorStands = async () => {
      if (!isMounted) return;
      try {
        const data = await apiGet(`/api/stands?sponsorId=${user.uid}`);
        if (!isMounted) return;
        const list = Array.isArray(data) ? data.map(normalizeStand) : [];
        setSponsorStands(list as Stand[]);
        // Sync viewingStand with fresh data; if removed (rejected), fetch its current state
        setViewingStand(prev => {
          if (!prev) return null;
          const updated = (list as Stand[]).find(s => s.id === prev.id);
          if (updated) return updated;
          // Stand no longer in sponsor's list — may have been rejected
          // Trigger a fresh fetch asynchronously
          if (prev.status !== 'available') {
            apiGet(`/api/stands/${prev.id}`)
              .then(fresh => {
                if (fresh && isMounted) setViewingStand(normalizeStand(fresh));
              })
              .catch(() => {});
          }
          return prev;
        });
      } catch {}
    };

    pollSponsorStands();
    timer = setInterval(pollSponsorStands, 10000);

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [user?.uid, isAuthReady]);

  // Handle target stand from notification
  useEffect(() => {
    if (targetStandId && stands.length > 0) {
      console.log('🎯 Target stand detected:', targetStandId, 'Stands loaded:', stands.length);
      const stand = stands.find(s => s.id === targetStandId);
      if (stand) {
        console.log('✅ Found target stand:', stand.label);
        setViewingStand(stand);
        setTargetStandId(null);
      } else {
        console.log('❌ Target stand not found in current stands list');
      }
    }
  }, [stands, targetStandId]);

  // Fetch Event Config (bgUrl, options)
  useEffect(() => {
    if (!isAuthReady || !selectedEventId) return;
    isCurrentlyFetchingRef.current = true;

    let timer: NodeJS.Timeout | null = null;
    let isMounted = true;

    const pollEventConfig = async () => {
      if (!isMounted) return;

      try {
        const data = await apiGet(`/api/events/${selectedEventId}`);
        if (!isMounted) return;

        if (data && Object.keys(data).length > 0) {
          if (data.bgUrl !== undefined) setBgUrl(data.bgUrl);
          if (data.availableOptions !== undefined) {
            const options = data.availableOptions.map((opt: any) =>
              typeof opt === 'string' ? { name: opt, price: 0 } : opt
            );
            setAvailableOptions(options);
            lastRemoteOptionsRef.current[selectedEventId] = JSON.stringify(options);
          }
          if (data.allowedStandTemplates !== undefined) {
            setAllowedStandTemplates(data.allowedStandTemplates);
            lastRemoteTemplatesRef.current[selectedEventId] = JSON.stringify(data.allowedStandTemplates);
          }
          if (data.templatePrices !== undefined) {
            setTemplatePrices(data.templatePrices);
            lastRemotePricesRef.current[selectedEventId] = JSON.stringify(data.templatePrices);
          }
        }

        // Delay disabling fetch flag to ensure effects don't trigger on stale state
        setTimeout(() => {
          if (isMounted) isCurrentlyFetchingRef.current = false;
        }, 500);
      } catch (error) {
        if (isMounted) {
          isCurrentlyFetchingRef.current = false;
          handleDatabaseError(error, OperationType.GET, `events`);
        }
      }
    };

    pollEventConfig();
    timer = setInterval(pollEventConfig, 5000);

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [isAuthReady, selectedEventId]);

  // Sync Available Options to Database
  useEffect(() => {
    if (!isAdmin || !selectedEventId || !isAuthReady || isCurrentlyFetchingRef.current) return;
    
    const currentOptionsJson = JSON.stringify(availableOptions);
    if (!lastRemoteOptionsRef.current[selectedEventId]) return;
    if (currentOptionsJson === lastRemoteOptionsRef.current[selectedEventId]) return;

    const saveOptions = async () => {
      try {
        await apiPatch(`/api/events/${selectedEventId}`, {
          availableOptions
        });
        lastRemoteOptionsRef.current[selectedEventId] = currentOptionsJson;
      } catch (error) {
        console.error("Error saving availableOptions:", error);
      }
    };
    
    const timeout = setTimeout(saveOptions, 1500);
    return () => clearTimeout(timeout);
  }, [availableOptions, isAdmin, selectedEventId, isAuthReady]);

  // Sync Template Prices to Database
  useEffect(() => {
    if (!isAdmin || !selectedEventId || !isAuthReady || isCurrentlyFetchingRef.current) return;

    const currentPricesJson = JSON.stringify(templatePrices);
    if (!lastRemotePricesRef.current[selectedEventId]) return;
    if (currentPricesJson === lastRemotePricesRef.current[selectedEventId]) return;

    const saveTemplatePrices = async () => {
      try {
        await apiPatch(`/api/events/${selectedEventId}`, {
          templatePrices
        });
        lastRemotePricesRef.current[selectedEventId] = currentPricesJson;
      } catch (error) {
        console.error("Error saving templatePrices:", error);
      }
    };
    
    const timeout = setTimeout(saveTemplatePrices, 1500);
    return () => clearTimeout(timeout);
  }, [templatePrices, isAdmin, selectedEventId, isAuthReady]);

  // Sync Allowed Stand Templates to Database
  useEffect(() => {
    if (!isAdmin || !selectedEventId || !isAuthReady || isCurrentlyFetchingRef.current) return;

    const currentTemplatesJson = JSON.stringify(allowedStandTemplates);
    if (!lastRemoteTemplatesRef.current[selectedEventId]) return;
    if (currentTemplatesJson === lastRemoteTemplatesRef.current[selectedEventId]) return;

    const saveAllowedTemplates = async () => {
      try {
        await apiPatch(`/api/events/${selectedEventId}`, {
          allowedStandTemplates
        });
        lastRemoteTemplatesRef.current[selectedEventId] = currentTemplatesJson;
      } catch (error) {
        console.error("Error saving allowedStandTemplates:", error);
      }
    };
    
    const timeout = setTimeout(saveAllowedTemplates, 1500);
    return () => clearTimeout(timeout);
  }, [allowedStandTemplates, isAdmin, selectedEventId, isAuthReady]);

  // Global Expiration Check for Stands
  useEffect(() => {
    if (!isAuthReady) return;

    const checkExpirations = async () => {
      const now = new Date();

      // Get all events with their expired stands
      const eventsData = await apiGet('/api/events');

      for (const eventDoc of eventsData.events || eventsData) {
        try {
          // Get expired payment_pending stands for this event
          const expiredStands = await apiGet(`/api/events/${eventDoc.id}/stands?status=payment_pending&expired=true`);

          for (const stand of expiredStands.stands || expiredStands) {
            if (stand.paymentExpiresAt && new Date(stand.paymentExpiresAt) < now) {
              console.log(`[EXPIRATION] Stand ${stand.label} in event ${eventDoc.id} expired.`);

              // Reset stand via API
              await apiPatch(`/api/stands/${stand.id}`, {
                status: 'available',
                company: '',
                sponsorId: '',
                sponsorName: '',
                sponsorEmail: '',
                reservedAt: null,
                documentsApprovedAt: null,
                paymentExpiresAt: null,
                documents: {},
                options: [],
                price: 0
              });

              // Notifica sponsor
              if (stand.sponsorId) {
                await apiPost('/api/notifications', {
                  userId: stand.sponsorId,
                  type: 'reservation_expired',
                  message: `⚠️ La tua prenotazione per lo stand ${stand.label} è scaduta perché non abbiamo ricevuto il pagamento entro le 96 ore previste. Lo stand è tornato disponibile.`,
                  read: false,
                  relatedEventId: eventDoc.id
                });
              }
            }
          }
        } catch (error) {
          console.error(`Error processing event ${eventDoc.id}:`, error);
        }
      }
    };

    // Esegui ogni minuto
    const interval = setInterval(checkExpirations, 60000);
    checkExpirations(); // Esegui subito al caricamento
    
    return () => clearInterval(interval);
  }, [isAuthReady]);

  // Global Pending Stands Listener for Admin - OTTIMIZZATO con useEventsPolling
  const { pendingStands: adminPendingStandsData, isLoading: isAdminDashboardLoading, refresh: refreshAdminPolling } = useEventsPolling({
    enabled: isAuthReady && isAdmin,
    interval: 20000, // 20 seconds come suggerito da analyzer-events
    standStatusFilter: ['pending', 'document_review', 'payment_verification'],
    onStandsChange: (stands) => {
      setAdminPendingStands(stands.map(normalizeStand));
    },
    onError: (error) => {
      console.error('Error polling admin dashboard data:', error);
    }
  });

  // Funzione seedDatabase rimossa - tasto "Popola Database" eliminato
  const handleDeleteEvent = async (id: string) => {
    try {
      await apiDelete(`/api/events/${id}`);
      if (selectedEventId === id) handleBackToEvents();
      toast.success("Evento eliminato con successo.");
    } catch (error) {
      handleDatabaseError(error, OperationType.DELETE, `events`);
    }
  };

  const saveConfigTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Inserisci un nome per il template.");
      return;
    }
    try {
      const templateData = {
        name: newTemplateName.trim(),
        availableOptions,
        allowedStandTemplates,
        templatePrices
      };
      await apiPost('/api/stand-templates', templateData);
      toast.success("Template salvato con successo!");
      setShowSaveTemplateModal(false);
      setNewTemplateName('');
    } catch (error) {
      handleDatabaseError(error, OperationType.CREATE, 'stand_config_templates');
    }
  };

  const loadConfigTemplate = async (templateId: string) => {
    const template = configTemplates.find(t => t.id === templateId);
    if (!template) return;
    
    try {
      if (selectedEventId) {
        await apiPatch(`/api/events/${selectedEventId}`, {
          availableOptions: template.availableOptions,
          allowedStandTemplates: template.allowedStandTemplates,
          templatePrices: template.templatePrices
        });
      }
      setAvailableOptions(template.availableOptions);
      setAllowedStandTemplates(template.allowedStandTemplates);
      setTemplatePrices(template.templatePrices);
      toast.success("Template caricato con successo!");
    } catch (error) {
      handleDatabaseError(error, OperationType.UPDATE, `events`);
    }
  };
  
  const deleteConfigTemplate = async (templateId: string) => {
    try {
      await apiDelete(`/api/templates/${templateId}`);
      toast.success("Template eliminato con successo.");
    } catch (error) {
      handleDatabaseError(error, OperationType.DELETE, `stand_config_templates/${templateId}`);
    }
  };

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Genera ID temporaneo per QR code
      const tempEventId = `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const qrCodeBase64 = await generateEventQRCode(tempEventId);

      const result = await api.events.create({
        id: tempEventId,
        name: newEvent.name,
        venue: newEvent.venue,
        address: newEvent.address,
        coords: newEvent.coords,
        date: newEvent.date,
        description: newEvent.description,
        image: newEvent.coverImage,
        coverImage: newEvent.coverImage || '',
        floorPlanImage: newEvent.floorPlanImage || '',
        floorPlanConfig: null,
        managerEmails: newEvent.managerEmails || '',
        status: 'draft',
        qrCode: qrCodeBase64,
        createdBy: user?.uid || 'admin'
      });
      setNewEvent({
        name: '',
        venue: '',
        address: '',
        coords: [45.4642, 9.1900],
        date: '',
        description: '',
        image: 'https://picsum.photos/seed/new/800/600',
        coverImage: '',
        floorPlanImage: '',
        managerEmails: ''
      });
      setLastCreatedEvent({ id: tempEventId, name: newEvent.name, qrCode: qrCodeBase64 });
      toast.success("Evento creato con successo!");
    } catch (error) {
      handleDatabaseError(error, OperationType.CREATE, 'events');
    }
  };

  const selectedEvent = events.find(c => c.id === selectedEventId);

  // Carica piantina quando cambia evento selezionato
  useEffect(() => {
    console.log('🔍 Floor Plan Debug:');
    console.log('- selectedEventId:', selectedEventId);
    console.log('- selectedEvent:', selectedEvent);
    console.log('- floorPlanImage type:', typeof selectedEvent?.floorPlanImage);
    console.log('- floorPlanImage length:', selectedEvent?.floorPlanImage?.length || 0);

    if (selectedEvent?.floorPlanImage) {
      console.log('✅ Setting bgUrl');
      setBgUrl(selectedEvent.floorPlanImage);
    } else {
      console.log('❌ No floorPlanImage, clearing bgUrl');
      setBgUrl('');
    }
  }, [selectedEventId, selectedEvent?.floorPlanImage]);

  const handleRejectStand = async (reason: string) => {
    if (!selectedEventId || !rejectingStand) return;
    const rejectedStandId = rejectingStand.id;
    const rejectedSponsorId = rejectingStand.sponsorId;
    const rejectedSponsorEmail = rejectingStand.sponsorEmail;
    const rejectedLabel = rejectingStand.label;
    try {
      await apiPatch(`/api/stands/${rejectedStandId}`, {
        status: 'available',
        company: null,
        company_logo: null,
        options: [],
        manager_requests: null,
        competitors: null,
        competitor_protection_active: false,
        sponsorId: null,
        sponsorEmail: null,
        sponsorName: null,
        documents: {}
      });

      const standNotifs = notifications.filter(n => {
        const sid = (n as any).related_stand_id || (n as any).relatedStandId;
        return sid === rejectedStandId;
      });
      await Promise.all(standNotifs.map(n => apiDelete(`/api/notifications/${n.id}`).catch(() => null)));

      setNotifications(prev => prev.filter(n => {
        const sid = (n as any).related_stand_id || (n as any).relatedStandId;
        return sid !== rejectedStandId;
      }));
      setAdminPendingStands(prev => prev.filter(s => s.id !== rejectedStandId));

      if (rejectedSponsorId && rejectedSponsorId !== 'anonymous') {
        const msg = `❌ La tua richiesta per lo stand ${rejectedLabel} è stata rifiutata. Lo stand è tornato disponibile.${reason ? `\nMotivazione: ${reason}` : ''}`;
        await api.notifications.create({
          userId: rejectedSponsorId,
          type: 'reservation_rejected',
          message: msg,
          read: false,
          relatedEventId: selectedEventId,
          relatedStandId: rejectedStandId
        });
      }

      if (rejectedSponsorEmail && selectedEvent) {
        const replyTo = selectedEvent.managerEmails ? selectedEvent.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';
        const reasonHtml = reason ? `<p><strong>Motivazione:</strong> ${reason}</p>` : '';
        await apiPost('/api/mail', {
          to: rejectedSponsorEmail,
          replyTo,
          message: {
            subject: `Richiesta Stand Rifiutata — Stand ${rejectedLabel}`,
            text: `La tua richiesta per lo stand ${rejectedLabel} è stata rifiutata.${reason ? ` Motivazione: ${reason}` : ''} Per ulteriori informazioni puoi contattarci rispondendo a questa email.`,
            html: `<p>La tua richiesta per lo stand <b>${rejectedLabel}</b> è stata rifiutata.</p>${reasonHtml}<p>Per ulteriori informazioni puoi contattarci rispondendo a questa email.</p>`
          }
        });
      }

      try {
        const managerNotifs = await apiGet('/api/notifications?userId=manager');
        const relatedNotif = (managerNotifs as any[]).find((n: any) =>
          n.type === 'new_reservation' &&
          (n.related_stand_id || n.relatedStandId) === rejectedStandId
        );
        if (relatedNotif) {
          await apiPatch(`/api/notifications/${relatedNotif.id}`, { read: true });
        }
      } catch {}

      try { refreshAdminPolling?.(); } catch {}
      toast.success('Stand rifiutato con successo.');
    } catch (error) {
      console.error('Error rejecting stand:', error);
      toast.error('Errore durante il rifiuto dello stand.');
    }
  };

  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#676767] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 font-sans">
      {/* Top Header & Navigation */}
      <header className="bg-white/80 backdrop-blur-lg shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 gap-4">
            {/* Logo & Left Nav */}
            <div className="flex items-center gap-4 md:gap-6 lg:gap-8 flex-shrink-0">
              <img 
                src="https://fad.summeet.it/pluginfile.php/1/core_admin/logo/0x200/1774257344/LOGO-%404x.png" 
                alt="Summeet Logo" 
                className="h-8 md:h-10 object-contain"
                referrerPolicy="no-referrer"
              />
            </div>
            
            {/* Right Nav */}
            <div className="flex items-center space-x-4 sm:space-x-6">
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setViewAsSponsor(false);
                      setShowAdminRequests(true);
                    }}
                    className="relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5 transition-all text-sm"
                  >
                    <ClipboardList className="w-4 h-4" />
                    <span className="hidden sm:inline">Gestione Richieste</span>
                    <span className="sm:hidden">Richieste</span>
                    {adminPendingStands.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce border-2 border-white">
                        {adminPendingStands.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setViewAsSponsor(false);
                      setShowAdminEvents(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-700 to-gray-600 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-gray-700/25 hover:-translate-y-0.5 transition-all text-sm"
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="hidden sm:inline">Gestisci Eventi</span>
                    <span className="sm:hidden">Eventi</span>
                  </button>
                </div>
              )}
              
              {user ? (
                <div className="flex items-center gap-3 relative">
                  <div className="relative">
                    <button 
                      onClick={() => setShowNotifications(!showNotifications)}
                      className="p-2 text-gray-600 hover:text-[#676767] transition-colors relative"
                    >
                      <Bell className="w-5 h-5" />
                      {notifications.filter(n => !n.read).length > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white animate-pulse">
                          {notifications.filter(n => !n.read).length}
                        </span>
                      )}
                    </button>
                    
                    {showNotifications && (
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 bg-slate-50">
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="font-bold text-[#676767]">Notifiche</h3>
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  const unread = notifications.filter(n => !n.read);
                                  if (unread.length === 0) return;
                                  // Optimistic update
                                  setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                                  try {
                                    await apiPost('/api/notifications/batch-read', {
                                      notificationIds: unread.map(n => n.id)
                                    });
                                    toast.success("Tutte le notifiche segnate come lette");
                                  } catch (e) {
                                    console.error('batch-read failed:', e);
                                    toast.error("Errore nell'aggiornamento delle notifiche");
                                  }
                                }}
                                className="text-[10px] uppercase font-bold text-gray-500 hover:text-[#676767]"
                              >
                                Svuota Badge
                              </button>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={async () => {
                                  if (notifications.length === 0) return;
                                  const ids = notifications.map(n => n.id);
                                  // Optimistic clear
                                  setNotifications([]);
                                  await Promise.all(ids.map(id => apiDelete(`/api/notifications/${id}`).catch(() => null)));
                                  toast.success("Notifiche eliminate");
                                }}
                                className="text-[10px] uppercase font-bold text-red-400 hover:text-red-600"
                              >
                                Cancella
                              </button>
                            </div>
                          </div>
                          <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button 
                              onClick={() => setNotifActiveTab('all')}
                              className={`flex-1 py-1 text-[10px] font-bold rounded-md transition-all ${notifActiveTab === 'all' ? 'bg-white text-[#676767] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              Tutte ({notifications.length})
                            </button>
                            <button 
                              onClick={() => setNotifActiveTab('unread')}
                              className={`flex-1 py-1 text-[10px] font-bold rounded-md transition-all ${notifActiveTab === 'unread' ? 'bg-white text-[#676767] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              Non Lette ({notifications.filter(n => !n.read).length})
                            </button>
                          </div>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          {notifications.filter(n => notifActiveTab === 'all' ? true : !n.read).length === 0 ? (
                            <div className="p-8 text-center bg-slate-50">
                              <BellOff className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm text-gray-500 font-medium tracking-tight">
                                {notifActiveTab === 'all' ? 'Nessuna notifica' : 'Nessuna nuova notifica'}
                              </p>
                            </div>
                          ) : (
                            notifications.filter(n => notifActiveTab === 'all' ? true : !n.read).map(notif => (
                              <div 
                                key={notif.id} 
                                className={`p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer relative group ${
                                  (notif.type as string) === 'reservation_rejected' ? (!notif.read ? 'bg-red-50' : 'bg-white') :
                                  (notif.type as string) === 'competitor_attempt' ? (!notif.read ? 'bg-amber-50' : 'bg-white') :
                                  (!notif.read ? 'bg-blue-50' : 'bg-white')
                                }`}
                                onClick={async () => {
                                  console.log('👆 Notification clicked:', notif);

                                  if (!notif.read) {
                                    // Optimistic update locale
                                    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                                    try {
                                      await apiPatch(`/api/notifications/${notif.id}`, { read: true });
                                      // Aggiorna stato locale immediatamente
                                      setNotifications(prev => prev.map(n =>
                                        n.id === notif.id ? { ...n, read: true } : n
                                      ));
                                    } catch (e) {
                                      console.error("Failed to mark notification as read:", e);
                                    }
                                  }
                                  
                                  if (notif.relatedEventId) {
                                    // Reset and clear UI conflicts
                                    setShowNotifications(false);
                                    
                                    if (isAdmin) {
                                      setViewAsSponsor(false);
                                      // Se è una notifica inerente a una richiesta (per admin), apriamo il pannello richieste
                                      if (notif.type === 'new_reservation' || notif.type === 'documents_submitted' || notif.type === 'payment_receipt_uploaded') {
                                        setShowAdminRequests(true);
                                      }
                                    }
                                    
                                    // 1. Switch event if needed
                                    const eventChanged = selectedEventId !== notif.relatedEventId;
                                    if (eventChanged) {
                                      console.log('🔄 Switching event to:', notif.relatedEventId);
                                      handleSelectEvent(notif.relatedEventId);
                                    }
                                    
                                    // 2. Handle stand targeting
                                    if (notif.relatedStandId) {
                                      if (isAdmin) {
                                        // Admin: apri direttamente pannello gestione stand
                                        console.log('👨‍💼 Admin opening stand panel:', notif.relatedStandId);
                                        const stand = stands.find(s => s.id === notif.relatedStandId);
                                        if (stand) {
                                          setViewingStand(stand);
                                        } else {
                                          // Stand non caricato, usa target mechanism
                                          setTargetStandId(notif.relatedStandId);
                                        }
                                      } else {
                                        // Sponsor: meccanismo standard
                                        if (!eventChanged) {
                                          const stand = stands.find(s => s.id === notif.relatedStandId);
                                          if (stand) {
                                            setViewingStand(stand);
                                            return;
                                          }
                                        }
                                        setTargetStandId(notif.relatedStandId);
                                      }
                                    }
                                  } else if (isAdmin) {
                                    // General admin notification without specific event
                                    setShowNotifications(false);
                                    setViewAsSponsor(false);
                                    setShowAdminRequests(true);
                                  } else if (notif.actionLink) {
                                    console.log("🔗 Navigate via actionLink", notif.actionLink);
                                  }
                                }}
                              >
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    // Optimistic remove
                                    setNotifications(prev => prev.filter(n => n.id !== notif.id));
                                    try {
                                      await apiDelete(`/api/notifications/${notif.id}`);
                                      toast.success("Notifica eliminata");
                                    } catch (err) {
                                      console.error('Failed to delete notification:', err);
                                    }
                                  }}
                                  className="absolute top-2 right-2 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                                {(() => {
                                  const ntype = notif.type as string;
                                  if (ntype === 'reservation_rejected') {
                                    const [baseMsg, reasonPart] = notif.message.split('\nMotivazione: ');
                                    return (
                                      <div>
                                        <div className="flex items-start gap-2 mb-1">
                                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                          <p className="text-sm text-red-700 font-medium leading-relaxed">{baseMsg.replace('❌ ', '')}</p>
                                        </div>
                                        {reasonPart && (
                                          <div className="ml-6 mt-1.5 p-2 bg-red-100 rounded-lg border border-red-200">
                                            <p className="text-[10px] font-black text-red-800 uppercase tracking-wider mb-0.5">Motivazione admin</p>
                                            <p className="text-xs text-red-700 leading-relaxed">{reasonPart}</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                  if (ntype === 'competitor_attempt') return (
                                    <div className="flex items-start gap-2 mb-1">
                                      <ShieldAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                      <p className="text-sm text-amber-700 font-medium leading-relaxed">{notif.message}</p>
                                    </div>
                                  );
                                  return <p className="text-sm text-gray-800 font-medium leading-relaxed">{notif.message}</p>;
                                })()}
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date((notif as any).created_at || notif.createdAt).toLocaleString('it-IT')}
                                </p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Cart icon (sponsor only) */}
                  {!isAdmin && (() => {
                    const sponsorCartStands = sponsorStands.filter(s =>
                      ['pending', 'document_review', 'payment_pending', 'payment_verification'].includes(s.status)
                    );
                    if (sponsorCartStands.length === 0) return null;
                    return (
                      <div className="relative">
                        <button
                          onClick={() => { setShowCart(!showCart); setShowProfileMenu(false); setShowNotifications(false); }}
                          className="relative p-2 text-gray-500 hover:text-[#676767] transition-colors"
                          aria-label="Carrello"
                        >
                          <ShoppingCart className="w-5 h-5" />
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                            {sponsorCartStands.length}
                          </span>
                        </button>
                        {showCart && (
                          <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                            <div className="p-4 border-b border-gray-100 bg-amber-50">
                              <h3 className="font-bold text-amber-900 flex items-center gap-2">
                                <ShoppingCart className="w-4 h-4" /> Il Tuo Carrello
                              </h3>
                              <p className="text-[11px] text-amber-700 mt-1">{sponsorCartStands.length} stand in lavorazione</p>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                              {sponsorCartStands.map(s => {
                                const statusLabel =
                                  s.status === 'pending' ? { text: 'In attesa di approvazione', color: 'text-blue-600 bg-blue-50' } :
                                  s.status === 'document_review' ? { text: 'Documenti in verifica', color: 'text-amber-700 bg-amber-50' } :
                                  s.status === 'payment_pending' ? { text: 'Carica la distinta di bonifico', color: 'text-orange-700 bg-orange-50' } :
                                  s.status === 'payment_verification' ? { text: 'Bonifico in verifica', color: 'text-yellow-700 bg-yellow-50' } :
                                  { text: s.status, color: 'text-gray-600 bg-gray-50' };
                                const opts = Array.isArray(s.options) ? s.options : [];
                                return (
                                  <button
                                    key={s.id}
                                    onClick={() => {
                                      setShowCart(false);
                                      const standEventId = s.eventId || s.event_id;
                                      if (standEventId && selectedEventId !== standEventId) {
                                        setSelectedEventId(standEventId);
                                      }
                                      setViewingStand(s);
                                    }}
                                    className="w-full text-left p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors"
                                  >
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <div>
                                        <p className="text-sm font-bold text-gray-900">Stand {s.label}</p>
                                        <p className="text-[11px] text-gray-500">{events.find(e => e.id === (s.eventId || s.event_id))?.name || ''}</p>
                                      </div>
                                      <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusLabel.color}`}>
                                        {statusLabel.text}
                                      </span>
                                    </div>
                                    {opts.length > 0 && (
                                      <p className="text-[10px] text-gray-500 mt-1 truncate">+ {opts.join(', ')}</p>
                                    )}
                                    {(s.price || 0) > 0 && (
                                      <p className="text-[11px] font-bold text-[#676767] mt-1">{(s.price || 0).toLocaleString('it-IT')} €</p>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Profile menu */}
                  <div className="relative">
                    <button
                      onClick={() => { setShowProfileMenu(!showProfileMenu); setShowCart(false); setShowNotifications(false); }}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#676767] text-white flex items-center justify-center font-bold text-sm">
                        {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="hidden md:block text-left">
                        <p className="text-xs font-bold text-gray-900 leading-tight">{user.displayName || user.email?.split('@')[0]}</p>
                        <p className="text-[10px] text-gray-500 leading-tight">{user.email}</p>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showProfileMenu && (
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 bg-slate-50">
                          <p className="text-sm font-bold text-gray-900">{user.displayName || user.email?.split('@')[0]}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          <span className={`inline-block mt-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isAdmin ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {isAdmin ? 'Event Manager' : 'Sponsor'}
                          </span>
                        </div>
                        <div className="py-1">
                          <div className="px-4 py-2.5 text-[11px] flex items-center gap-2 text-gray-600">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate">{user.email}</span>
                          </div>
                          {!isAdmin && (() => {
                            const sponsorCartStands = sponsorStands.filter(s =>
                              ['pending', 'document_review', 'payment_pending', 'payment_verification', 'occupied'].includes(s.status)
                            );
                            return (
                              <button
                                onClick={() => { setShowProfileMenu(false); setShowCart(true); }}
                                className="w-full px-4 py-2.5 text-xs flex items-center justify-between gap-2 text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <span className="flex items-center gap-2">
                                  <ShoppingCart className="w-3.5 h-3.5 text-gray-400" />
                                  Le mie prenotazioni
                                </span>
                                <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                  {sponsorCartStands.length}
                                </span>
                              </button>
                            );
                          })()}
                        </div>
                        <div className="border-t border-gray-100">
                          <button
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            className="w-full px-4 py-3 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                          >
                            <LogOut className="w-3.5 h-3.5" />
                            {isLoggingOut ? 'Uscita in corso...' : 'Esci'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <button 
                    onClick={() => {
                      setAuthMode('login');
                      setGdprConsent(false);
                      setShowAuthModal(true);
                    }}
                    className="flex items-center text-gray-600 hover:text-[#676767] font-medium transition-colors"
                  >
                    <LogIn className="w-5 h-5 sm:mr-2" />
                    <span className="text-sm hidden sm:inline">Login</span>
                  </button>
                  <button 
                    onClick={() => {
                      setAuthMode('register');
                      setGdprConsent(false);
                      setShowAuthModal(true);
                    }}
                    className="bg-[#676767] text-white px-4 sm:px-5 py-2 rounded-lg font-medium hover:bg-[#1d2127] transition-colors shadow-sm text-sm"
                  >
                    Registrati
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Mobile Nav (Eventi) - Removed as requested */}
        </div>
      </header>

      {/* Page Title Section */}
      {!selectedEvent && (
        <div className="mb-8">
          <img
            src="/summeet_head.png"
            alt="Summeet — Scopri gli eventi disponibili e prenota il tuo stand"
            className="w-full object-cover"
          />
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {selectedEvent ? (
          <EventDetail
            event={selectedEvent}
            onBack={handleBackToEvents}
            isAdmin={isAdmin}
            user={user}
            setShowAuthModal={setShowAuthModal}
            stands={stands}
            setStands={setStands}
            bgUrl={bgUrl}
            setBgUrl={setBgUrl}
            bgDimensions={bgDimensions}
            setBgDimensions={setBgDimensions}
            availableOptions={availableOptions}
            setAvailableOptions={setAvailableOptions}
            selectedEventId={selectedEventId}
            companyName={companyName}
            setCompanyName={setCompanyName}
            companyLogo={companyLogo}
            setCompanyLogo={setCompanyLogo}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            managerRequests={managerRequests}
            setManagerRequests={setManagerRequests}
            competitors={competitors}
            setCompetitors={setCompetitors}
            allowedStandTemplates={allowedStandTemplates}
            setAllowedStandTemplates={setAllowedStandTemplates}
            templatePrices={templatePrices}
            setTemplatePrices={setTemplatePrices}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            configTemplates={configTemplates}
            loadConfigTemplate={loadConfigTemplate}
            deleteConfigTemplate={deleteConfigTemplate}
            showSaveTemplateModal={showSaveTemplateModal}
            setShowSaveTemplateModal={setShowSaveTemplateModal}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            saveConfigTemplate={saveConfigTemplate}
            gdprConsent={gdprConsent}
            setGdprConsent={setGdprConsent}
            customWidth={customWidth}
            setCustomWidth={setCustomWidth}
            customLength={customLength}
            setCustomLength={setCustomLength}
            viewingStand={viewingStand}
            setViewingStand={setViewingStand}
            viewAsSponsor={viewAsSponsor}
            setViewAsSponsor={setViewAsSponsor}
            setEvents={setEvents}
            setRejectingStand={setRejectingStand}
            notifications={notifications}
          />
        ) : (
          <div className="space-y-8">
            {/* Map Section */}
            <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-200 h-[400px] z-0 relative overflow-hidden">
              <MapContainer 
                center={[41.9028, 12.4964]} 
                zoom={5} 
                className="w-full h-full rounded-lg z-0"
                scrollWheelZoom={false}
                minZoom={4}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds events={events} />
                <MarkerClusterGroup
                  chunkedLoading
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                >
                  {events.map(course => (
                      <Marker 
                        key={course.id} 
                        position={course.coords}
                        eventHandlers={{
                          click: () => handleSelectEvent(course.id),
                        }}
                      >
                        <Popup>
                          <div className="text-center min-w-[150px]">
                            <strong className="block text-[#676767] mb-1 text-sm">{course.title}</strong>
                            <span className="text-xs text-gray-600 block mb-2">{course.location}</span>
                            <button 
                              onClick={() => handleSelectEvent(course.id)}
                              className="text-xs bg-[#676767] text-white px-3 py-1.5 rounded w-full hover:bg-[#0d336b] transition-colors"
                            >
                              Vedi Dettagli
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>

            {/* Event Grid */}
            {events.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun evento disponibile</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Al momento non ci sono eventi in programma. Torna a trovarci presto per scoprire i prossimi eventi!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((course) => (
              <div key={course.id} className="bg-white rounded-xl overflow-hidden shadow-sm flex flex-col hover:shadow-md transition-shadow border border-gray-100">
                {/* Card Image & Title */}
              <div className="relative h-48 sm:h-56">
                <img
                  src={course.cover_image}
                  alt={course.name}
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <h2 className="absolute bottom-4 left-5 right-5 text-white font-bold text-xl leading-snug">
                  {course.title}
                </h2>
              </div>

              {/* Card Details */}
              <div className="p-6 flex flex-col flex-grow">
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-3 text-[#ffa064]" />
                    {course.location}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-3 text-[#ffa064]" />
                    {course.date}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <LayoutGrid className="w-4 h-4 mr-3 text-[#ffa064]" />
                    {course.stands}
                  </div>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  {course.description}
                </p>

                {/* Admin Edit Button */}
                {isAdmin && (
                  <button
                    onClick={() => handleEditEvent(course.id)}
                    className="mb-3 w-full bg-amber-500 text-white py-2 rounded-lg font-medium hover:bg-amber-600 transition-colors shadow-sm text-sm flex items-center justify-center"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Modifica Evento
                  </button>
                )}

                <button
                  onClick={() => handleSelectEvent(course.id)}
                  className="mt-auto w-full bg-[#676767] text-white py-3 rounded-lg font-bold hover:bg-[#1d2127] transition-colors shadow-sm text-base"
                >
                  Vedi Stand
                </button>
              </div>
            </div>
            ))}
            </div>
            )}
          </div>
        )}
      </main>

      {/* Admin Events Management Modal */}
      {isAdmin && showAdminEvents && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowAdminEvents(false)}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#676767]">Gestione Eventi</h2>
            </div>

            {lastCreatedEvent ? (
              <div className="bg-green-50 border border-green-100 rounded-2xl p-8 text-center mb-8 animate-in zoom-in-95 duration-300">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Evento Creato!</h3>
                <p className="text-green-700 mb-6">L'evento "{lastCreatedEvent.name}" è stato creato correttamente.</p>
                
                <div className="bg-white p-6 rounded-xl border border-green-200 inline-block mb-6 shadow-sm">
                  <img src={lastCreatedEvent.qrCode} alt="QR Code" className="w-48 h-48 mx-auto mb-4" />
                  <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mb-3">QR Code dell'Evento</p>
                  <a 
                    href={lastCreatedEvent.qrCode} 
                    download={`qrcode-${lastCreatedEvent.name.replace(/\s+/g, '-').toLowerCase()}.png`}
                    className="flex items-center justify-center gap-2 w-full py-2 bg-[#676767] text-white rounded-lg text-sm font-bold hover:bg-[#0d336d] transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Scarica QR Code
                  </a>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button 
                    onClick={() => setLastCreatedEvent(null)}
                    className="px-6 py-2 bg-white border border-green-200 text-green-700 rounded-lg font-bold hover:bg-green-100 transition-all text-sm"
                  >
                    Crea un altro evento
                  </button>
                  <button 
                    onClick={() => {
                      setShowAdminEvents(false);
                      setLastCreatedEvent(null);
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all text-sm shadow-md shadow-green-900/10"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            ) : (
              <>
                <form onSubmit={handleAddEvent} className="space-y-4 mb-8 bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h3 className="font-semibold text-slate-700 mb-2">Aggiungi Nuovo Evento</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Titolo</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#676767] outline-none"
                    value={newEvent.name}
                    onChange={e => setNewEvent({...newEvent, name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Luogo</label>
                  <input 
                    type="text"
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#676767] outline-none"
                    value={newEvent.venue}
                    onChange={e => setNewEvent({...newEvent, venue: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Data</label>
                  <input 
                    type="text" 
                    placeholder="es: 15-20 Giugno 2026"
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#676767] outline-none"
                    value={newEvent.date}
                    onChange={e => setNewEvent({...newEvent, date: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase">Descrizione</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#676767] outline-none"
                    value={newEvent.description}
                    onChange={e => setNewEvent({...newEvent, description: e.target.value})}
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase flex justify-between">
                    Email Event Manager (Ricezione Notifiche)
                    <span className="text-[10px] text-slate-400 normal-case font-normal">Separate da virgola</span>
                  </label>
                  <input 
                    type="text" 
                    placeholder="es: espositore@summeet.it, admin@summeet.it"
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#676767] outline-none"
                    value={newEvent.managerEmails || ''}
                    onChange={e => setNewEvent({...newEvent, managerEmails: e.target.value})}
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase flex justify-between">
                    Grafica Copertina
                    <span className="text-[10px] text-slate-400 normal-case font-normal">Dimensioni consigliate: 1200x600 px</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex-grow">
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = async (event) => {
                              const base64 = event.target?.result as string;
                              const compressed = await compressImage(base64, 1200, 600, 0.9);
                              setNewEvent({...newEvent, image: compressed, coverImage: compressed});
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                        id="event-cover-upload"
                      />
                      <label 
                        htmlFor="event-cover-upload"
                        className="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-slate-200 rounded-xl hover:border-[#676767] hover:bg-blue-50 cursor-pointer transition-all group"
                      >
                        <ImageIcon className="w-5 h-5 text-slate-400 group-hover:text-[#676767]" />
                        <span className="text-sm font-medium text-slate-600 group-hover:text-[#676767]">
                          {newEvent.coverImage ? 'Cambia Copertina' : 'Carica Immagine Copertina'}
                        </span>
                      </label>
                    </div>
                    {newEvent.coverImage && (
                      <div className="w-20 h-12 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                        <img src={newEvent.coverImage} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button type="submit" className="w-full bg-[#676767] text-white py-3 rounded-lg font-bold hover:bg-[#0d336d] transition-all shadow-lg active:scale-[0.98]">
                Crea Evento
              </button>
            </form>

            <div className="space-y-3">
              <h3 className="font-semibold text-slate-700 mb-2">Eventi Esistenti</h3>
              {events.length === 0 ? (
                <p className="text-center py-8 text-slate-400 italic">Nessun evento presente.</p>
              ) : (
                events.map(ev => (
                  <div key={ev.id} className="flex items-center justify-between p-4 bg-white border rounded-xl hover:border-[#676767] transition-all group shadow-sm">
                    {eventToDelete === ev.id ? (
                      <div className="flex-grow flex items-center justify-between animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-red-50 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800">Sei sicuro di voler eliminare questo evento?</p>
                            <p className="text-xs text-slate-500">L'azione è irreversibile.</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => setEventToDelete(null)}
                            className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-all"
                          >
                            Annulla
                          </button>
                          <button 
                            onClick={() => {
                              handleDeleteEvent(ev.id);
                              setEventToDelete(null);
                            }}
                            className="px-3 py-1.5 text-xs font-bold bg-red-500 text-white hover:bg-red-600 rounded-lg transition-all shadow-sm"
                          >
                            Elimina
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <h4 className="font-bold text-slate-800">{ev.name}</h4>
                          <p className="text-sm text-slate-500">{ev.location} • {ev.date}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          {ev.qrCode && (
                            <div className="flex items-center gap-2">
                              <img src={ev.qrCode} alt="Event QR Code" className="w-12 h-12 rounded-md border border-slate-200" />
                              <a 
                                href={ev.qrCode} 
                                download={`qrcode-${ev.name.replace(/\s+/g, '-').toLowerCase()}.png`}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                title="Scarica QR Code"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            </div>
                          )}
                          <button 
                            onClick={() => setEventToDelete(ev.id)}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-all"
                            title="Elimina Evento"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )}

  {/* Admin Requests Management Modal */}
  {isAdmin && showAdminRequests && (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
        <button 
          onClick={() => setShowAdminRequests(false)}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#676767]">Gestione Richieste Prenotazione</h2>
          <span className="bg-[#676767] text-white px-3 py-1 rounded-full text-xs font-bold">
            {adminPendingStands.length} in attesa
          </span>
        </div>

        {adminPendingStands.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Ottimo lavoro! Non ci sono richieste in attesa.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 mb-4 italic">Queste sono le richieste di prenotazione che richiedono la tua approvazione documenti o verifica pagamento.</p>
            {adminPendingStands.map(stand => (
              <div key={stand.id} className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm hover:border-[#676767] transition-all group">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#676767] font-bold text-xl">
                      {stand.label}
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-lg">{stand.company}</p>
                      <p className="text-sm text-slate-500 font-medium">
                        {events.find(e => e.id === stand.eventId)?.name || 'Evento Sconosciuto'}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-bold">
                          <FileText className="w-3 h-3" />
                          {Object.values(stand.documents || {}).filter(d => d.status === 'approved').length} / {STAND_DOCUMENT_TYPES.filter(d => d.required).length} Doc obbligatori
                        </span>
                        {stand.status === 'payment_verification' && (
                          <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-bold">
                            <Clock className="w-3 h-3" />
                            Pagamento da verificare
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleSelectEvent(stand.eventId);
                      // Fix: ensure price is set from template if missing
                      const standWithPrice = stand.price ? stand : { ...stand, price: (configTemplates.find(t => t.name === selectedEventId)?.defaultPrice || 0) };
                      setViewingStand(standWithPrice);
                      setShowAdminRequests(false);
                    }}
                    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#0d336d] transition-all shadow-md shadow-blue-900/10 active:scale-95"
                  >
                    Gestisci
                    <MousePointer2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100] overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden my-8">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-bold text-[#676767]">
                {authMode === 'login' ? 'Accedi' : authMode === 'register' ? 'Registrati' : 'Recupera Password'}
              </h2>
              <button onClick={() => setShowAuthModal(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              {authError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl">
                  {authError}
                </div>
              )}

              <form onSubmit={handleEmailAuth} className="space-y-4">
                {authMode === 'register' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Azienda</label>
                    <input
                      type="text"
                      required
                      value={authCompanyName}
                      onChange={(e) => setAuthCompanyName(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#676767] focus:border-transparent transition-all outline-none"
                      placeholder="La tua azienda S.p.A."
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email aziendale</label>
                  <input
                    type="email"
                    required
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#676767] focus:border-transparent transition-all outline-none"
                    placeholder="tua@azienda.com"
                  />
                </div>
                
                {authMode !== 'forgot_password' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={authPassword}
                        onChange={(e) => setAuthPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#676767] focus:border-transparent transition-all outline-none"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                )}

                {authMode === 'register' && (
                  <div className="flex items-start gap-3 mt-4">
                    <input 
                      type="checkbox" 
                      required
                      id="gdpr-consent"
                      checked={gdprConsent}
                      onChange={(e) => setGdprConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 text-[#676767] rounded border-gray-300 focus:ring-[#676767]"
                    />
                    <label htmlFor="gdpr-consent" className="text-xs text-gray-600 leading-relaxed">
                      Dichiaro di aver letto l'<a href="/Template/Summeet_Informativa_Privacy_ECM.pdf" target="_blank" rel="noopener noreferrer" className="text-[#676767] underline hover:text-[#1d2127]">Informativa sulla Privacy</a> e acconsento al trattamento dei miei dati personali in conformità con il Regolamento (UE) 2016/679 (GDPR) per le finalità di registrazione e gestione dell'account.
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-colors disabled:opacity-50 mt-6"
                >
                  {authLoading ? 'Attendere...' : authMode === 'login' ? 'Accedi' : authMode === 'register' ? 'Registrati' : 'Invia Link'}
                </button>
              </form>

              <div className="mt-6 text-center text-sm text-gray-600">
                {authMode === 'login' ? (
                  <>
                    Non hai un account?{' '}
                    <button onClick={() => setAuthMode('register')} className="text-[#676767] font-bold hover:underline">Registrati</button>
                    <br />
                    <button onClick={() => setAuthMode('forgot_password')} className="text-gray-500 hover:text-gray-700 mt-2 text-xs">Password dimenticata?</button>
                  </>
                ) : authMode === 'register' ? (
                  <>
                    Hai già un account?{' '}
                    <button onClick={() => setAuthMode('login')} className="text-[#676767] font-bold hover:underline">Accedi</button>
                  </>
                ) : (
                  <button onClick={() => setAuthMode('login')} className="text-[#676767] font-bold hover:underline">Torna al Login</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      <EditEventModal
        editingEventId={editingEventId}
        setEditingEventId={setEditingEventId}
        event={events.find(e => e.id === editingEventId) || null}
        onSave={async (eventId, eventData) => {
          await apiPatch(`/api/events/${eventId}`, eventData);
          const response = await apiGet('/api/events');
          const transformedEvents = (Array.isArray(response) ? response : []).map((event: any) => ({
            ...event,
            title: event.name || '',
            coords: event.coords && Array.isArray(JSON.parse(event.coords))
              ? JSON.parse(event.coords)
              : [45.4408, 12.3155]
          }));
          setEvents(transformedEvents);
          setEditingEventId(null);
          toast.success('Evento aggiornato con successo!');
        }}
      />

      {/* Cart Created Modal — spiegazione del flusso allo sponsor */}
      {showCartCreatedModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowCartCreatedModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <ShoppingCart className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Richiesta inviata!</h3>
              <p className="text-xs text-gray-500">Ecco cosa succede ora</p>
            </div>
            <div className="space-y-3 mb-5">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</div>
                <div>
                  <p className="text-xs font-bold text-blue-900">Carica i documenti richiesti</p>
                  <p className="text-[11px] text-blue-700 mt-0.5">Trovi lo stand nel <b>carrello</b> in alto a destra. Hai <b>7 giorni</b> per caricare tutti i documenti obbligatori.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-6 h-6 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">2</div>
                <div>
                  <p className="text-xs font-bold text-amber-900">L'admin approva la richiesta</p>
                  <p className="text-[11px] text-amber-700 mt-0.5">Riceverai una <b>email di conferma</b>. Da quel momento avrai 96 ore per il pagamento.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">3</div>
                <div>
                  <p className="text-xs font-bold text-green-900">Carica la distinta del bonifico</p>
                  <p className="text-[11px] text-green-700 mt-0.5">Sempre dal <b>carrello</b>, carica la distinta. L'admin la verifica e lo stand è tuo!</p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-5">
              <p className="text-[11px] text-red-700 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Se non carichi tutti i documenti entro <b>7 giorni</b>, la richiesta sarà cancellata automaticamente. Riceverai una email di promemoria 24h prima della scadenza.</span>
              </p>
            </div>
            <button
              onClick={() => setShowCartCreatedModal(false)}
              className="w-full px-4 py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-all text-sm"
            >
              Ho capito, procedi
            </button>
          </div>
        </div>
      )}

      {/* Reject Stand Modal */}
      {rejectingStand && (
        <RejectModal
          stand={rejectingStand}
          eventId={selectedEventId}
          event={selectedEvent}
          onReject={(reason) => {
            handleRejectStand(reason);
            setRejectingStand(null);
          }}
          onClose={() => setRejectingStand(null)}
        />
      )}

      {/* Add Booked Stand Modal */}
      {showAddBookedModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100] overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden my-8">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-bold text-[#676767]">Aggiungi Stand Prenotato</h2>
              <button onClick={() => setShowAddBookedModal(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={(e) => handleAddBookedSubmit(e, false)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Etichetta Stand</label>
                  <input
                    type="text"
                    value={bookedStandConfig.label}
                    onChange={(e) => setBookedStandConfig({...bookedStandConfig, label: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#676767] focus:border-transparent transition-all outline-none"
                    placeholder={`es. Stand ${stands.length + 1}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Sponsor (Azienda)</label>
                  <input
                    type="text"
                    required
                    value={bookedStandConfig.company}
                    onChange={(e) => setBookedStandConfig({...bookedStandConfig, company: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#676767] focus:border-transparent transition-all outline-none"
                    placeholder="Nome dell'azienda sponsor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Prezzo (€)</label>
                  <input
                    type="number"
                    required
                    value={bookedStandConfig.price}
                    onChange={(e) => setBookedStandConfig({...bookedStandConfig, price: Number(e.target.value)})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#676767] focus:border-transparent transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Template Stand</label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-2">
                    {STANDS_TEMPLATES.filter(t => allowedStandTemplates.includes(t.id)).map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setBookedStandConfig({...bookedStandConfig, templateId: template.id})}
                        className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all ${
                          bookedStandConfig.templateId === template.id
                            ? 'border-[#676767] bg-blue-50/50'
                            : 'border-gray-100 hover:border-gray-200 bg-white'
                        }`}
                      >
                        <template.icon className={`w-8 h-8 mb-1 ${bookedStandConfig.templateId === template.id ? 'text-[#676767]' : 'text-gray-400'}`} />
                        <span className={`text-[10px] font-bold text-center leading-tight ${bookedStandConfig.templateId === template.id ? 'text-[#676767]' : 'text-gray-600'}`}>
                          {template.name}
                        </span>
                      </button>
                    ))}
                    {STANDS_TEMPLATES.filter(t => allowedStandTemplates.includes(t.id)).length === 0 && (
                      <div className="col-span-2 p-3 bg-amber-50 border border-amber-100 rounded-xl text-center">
                        <p className="text-xs text-amber-700 font-medium">Nessun template disponibile.</p>
                      </div>
                    )}
                  </div>
                  {bookedStandConfig.templateId === 'custom' && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-xl space-y-2">
                      <label className="block text-xs font-bold text-[#676767] uppercase tracking-wider">Dimensioni Personalizzate</label>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-xs text-slate-600 mb-1">Larghezza (m)</label>
                          <input
                            type="number"
                            min="1"
                            value={bookedStandConfig.customWidth}
                            onChange={(e) => setBookedStandConfig({...bookedStandConfig, customWidth: Math.max(1, Number(e.target.value))})}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#676767] outline-none text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-slate-600 mb-1">Lunghezza (m)</label>
                          <input
                            type="number"
                            min="1"
                            value={bookedStandConfig.customLength}
                            onChange={(e) => setBookedStandConfig({...bookedStandConfig, customLength: Math.max(1, Number(e.target.value))})}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#676767] outline-none text-sm"
                          />
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 text-right">
                        Area totale: <span className="font-bold text-[#676767]">{bookedStandConfig.customWidth * bookedStandConfig.customLength} mq</span>
                      </div>
                    </div>
                  )}
                </div>

                {availableOptions.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Opzioni Aggiuntive</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                      {availableOptions.map(option => (
                        <label key={option.name} className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${bookedStandConfig.options.includes(option.name) ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200' : 'bg-white border-gray-100 hover:border-gray-200'}`}>
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={bookedStandConfig.options.includes(option.name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBookedStandConfig({...bookedStandConfig, options: [...bookedStandConfig.options, option.name]});
                                } else {
                                  setBookedStandConfig({...bookedStandConfig, options: bookedStandConfig.options.filter(o => o !== option.name)});
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">{option.name}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-500">+{option.price}€</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 mt-6">
                  <button
                    type="submit"
                    className="w-full py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-colors"
                  >
                    Aggiungi Default (Quadrato)
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleAddBookedSubmit(e, true)}
                    className="w-full py-3 bg-[#e67e22] text-white rounded-xl font-bold hover:bg-[#d35400] transition-colors flex items-center justify-center gap-2"
                  >
                    <MousePointer2 className="w-5 h-5" />
                    Disegna sulla Mappa
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative">
            <button
              onClick={() => setShowSaveTemplateModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-[#676767] mb-4 flex items-center gap-2">
              <Save className="w-5 h-5" />
              Salva Template
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Salva le opzioni e i prezzi correnti come template riutilizzabile per altri eventi.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome Template</label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="Es: Configurazione Base 2026"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#676767] outline-none text-sm"
                  autoFocus
                />
              </div>
              <button
                onClick={saveConfigTemplate}
                className="w-full py-2 bg-[#676767] text-white rounded-lg font-bold hover:bg-[#1d2127] transition-colors"
              >
                Salva Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



const StatusTrafficLight = ({ stand }: { stand: Stand }) => {
  // Calcolo tempo residuo per pagamento
  const getTimeRemaining = () => {
    if (!stand.paymentExpiresAt) return null;
    const expiry = new Date(stand.paymentExpiresAt).getTime();
    const now = Date.now();
    const diff = expiry - now;
    
    if (diff <= 0) return { expired: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { expired: false, hours, minutes };
  };
  
  const timeRemaining = getTimeRemaining();
  
  // Stato semaforo documenti
  const getDocumentsStatus = () => {
    const requiredDocs = STAND_DOCUMENT_TYPES.filter(d => d.required);
    const uploaded = requiredDocs.filter(d => stand.documents?.[d.id]?.url);
    const approved = requiredDocs.filter(d => stand.documents?.[d.id]?.status === 'approved');
    
    if (approved.length === requiredDocs.length) return { color: 'green', text: 'Approvati' };
    if (uploaded.length === requiredDocs.length && stand.status === 'document_review') return { color: 'yellow', text: 'In verifica' };
    if (uploaded.length > 0) return { color: 'yellow', text: `${uploaded.length}/${requiredDocs.length} caricati` };
    return { color: 'red', text: 'Da caricare' };
  };
  
  // Stato semaforo pagamento
  const getPaymentStatus = () => {
    if (stand.status === 'occupied' && stand.paymentConfirmedAt) return { color: 'green', text: 'Pagamento confermato' };
    if (stand.status === 'payment_verification') return { color: 'yellow', text: 'Bonifico in verifica' };
    if (stand.status === 'payment_pending') {
      if (timeRemaining?.expired) return { color: 'red', text: 'Scaduto' };
      return { color: 'orange', text: `${timeRemaining?.hours}h ${timeRemaining?.minutes}m`, isCountdown: true };
    }
    if (stand.status === 'document_review' || stand.status === 'pending') {
      return { color: 'gray', text: 'In attesa approvazione documenti', disabled: true };
    }
    return { color: 'gray', text: 'Non disponibile', disabled: true };
  };
  
  const docStatus = getDocumentsStatus();
  const payStatus = getPaymentStatus();
  
  return (
    <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
      <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" /> Checklist Partecipazione
      </h4>
      
      {/* Semaforo 1: Documenti */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${
            docStatus.color === 'green' ? 'bg-green-500' :
            docStatus.color === 'yellow' ? 'bg-yellow-500 animate-pulse' :
            'bg-red-500'
          }`} />
          <div>
            <p className="text-sm font-medium text-gray-800">Documenti Tecnici</p>
            <p className={`text-xs ${docStatus.color === 'gray' ? 'text-gray-400' : 'text-gray-500'}`}>
              {docStatus.text}
            </p>
          </div>
        </div>
        {docStatus.color === 'green' && <Check className="w-5 h-5 text-green-500" />}
      </div>
      
      {/* Semaforo 2: Pagamento */}
      <div className={`flex items-center justify-between p-3 bg-white rounded-lg border ${payStatus.disabled ? 'opacity-60' : ''}`}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${
            payStatus.color === 'green' ? 'bg-green-500' :
            payStatus.color === 'yellow' ? 'bg-yellow-500 animate-pulse' :
            payStatus.color === 'orange' ? 'bg-orange-500 animate-pulse' :
            payStatus.color === 'red' ? 'bg-red-500' :
            'bg-gray-300'
          }`} />
          <div>
            <p className="text-sm font-medium text-gray-800">Pagamento</p>
            <p className={`text-xs ${payStatus.isCountdown ? 'text-orange-600 font-bold' : 'text-gray-500'}`}>
              {payStatus.text}
            </p>
          </div>
        </div>
        {payStatus.color === 'green' && <Check className="w-5 h-5 text-green-500" />}
        {payStatus.isCountdown && <Clock className="w-4 h-4 text-orange-500 animate-pulse" />}
      </div>
      
      {/* Alert scadenza */}
      {payStatus.isCountdown && timeRemaining && !timeRemaining.expired && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-orange-700">
            <strong>Attenzione:</strong> Il timer scadrà il {new Date(stand.paymentExpiresAt!).toLocaleString('it-IT')}. 
            Dopo la scadenza, la prenotazione verrà annullata e lo stand tornerà disponibile.
          </p>
        </div>
      )}
      
      {timeRemaining?.expired && stand.status === 'payment_pending' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700 font-medium">
            Tempo scaduto. La prenotazione è stata annullata. Contatta l'organizzatore per verificare la disponibilità.
          </p>
        </div>
      )}
    </div>
  );
};

function EventDetail({ 
  event, 
  onBack, 
  isAdmin,
  user,
  setShowAuthModal,
  stands,
  setStands,
  bgUrl,
  setBgUrl,
  bgDimensions,
  setBgDimensions,
  availableOptions,
  setAvailableOptions,
  selectedEventId,
  companyName,
  setCompanyName,
  companyLogo,
  setCompanyLogo,
  selectedOptions,
  setSelectedOptions,
  managerRequests,
  setManagerRequests,
  competitors,
  setCompetitors,
  allowedStandTemplates,
  setAllowedStandTemplates,
  templatePrices,
  setTemplatePrices,
  selectedTemplate,
  setSelectedTemplate,
  configTemplates,
  loadConfigTemplate,
  deleteConfigTemplate,
  showSaveTemplateModal,
  setShowSaveTemplateModal,
  newTemplateName,
  setNewTemplateName,
  saveConfigTemplate,
  gdprConsent,
  setGdprConsent,
  customWidth,
  setCustomWidth,
  customLength,
  setCustomLength,
  viewingStand,
  setViewingStand,
  viewAsSponsor,
  setViewAsSponsor,
  setEvents,
  setRejectingStand,
  notifications
}: {
  event: any,
  onBack: () => void,
  isAdmin: boolean,
  user: User | null,
  setShowAuthModal: React.Dispatch<React.SetStateAction<boolean>>,
  stands: Stand[],
  setStands: React.Dispatch<React.SetStateAction<Stand[]>>,
  bgUrl: string | null,
  setBgUrl: React.Dispatch<React.SetStateAction<string | null>>,
  bgDimensions: { width: number, height: number },
  setBgDimensions: React.Dispatch<React.SetStateAction<{ width: number, height: number }>>,
  availableOptions: Option[],
  setAvailableOptions: React.Dispatch<React.SetStateAction<Option[]>>,
  selectedEventId: string | null,
  companyName: string,
  setCompanyName: React.Dispatch<React.SetStateAction<string>>,
  companyLogo: string,
  setCompanyLogo: React.Dispatch<React.SetStateAction<string>>,
  selectedOptions: string[],
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>,
  managerRequests: string,
  setManagerRequests: React.Dispatch<React.SetStateAction<string>>,
  competitors: string,
  setCompetitors: React.Dispatch<React.SetStateAction<string>>,
  allowedStandTemplates: string[],
  setAllowedStandTemplates: React.Dispatch<React.SetStateAction<string[]>>,
  templatePrices: { [key: string]: number },
  setTemplatePrices: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>,
  selectedTemplate: any,
  setSelectedTemplate: React.Dispatch<React.SetStateAction<any>>,
  configTemplates: StandConfigTemplate[],
  loadConfigTemplate: (id: string) => Promise<void>,
  deleteConfigTemplate: (id: string) => Promise<void>,
  showSaveTemplateModal: boolean,
  setShowSaveTemplateModal: React.Dispatch<React.SetStateAction<boolean>>,
  newTemplateName: string,
  setNewTemplateName: React.Dispatch<React.SetStateAction<string>>,
  saveConfigTemplate: () => Promise<void>,
  gdprConsent: boolean,
  setGdprConsent: React.Dispatch<React.SetStateAction<boolean>>,
  customWidth: number,
  setCustomWidth: React.Dispatch<React.SetStateAction<number>>,
  customLength: number,
  setCustomLength: React.Dispatch<React.SetStateAction<number>>,
  viewingStand: Stand | null,
  setViewingStand: React.Dispatch<React.SetStateAction<Stand | null>>,
  viewAsSponsor: boolean,
  setViewAsSponsor: React.Dispatch<React.SetStateAction<boolean>>,
  setEvents: React.Dispatch<React.SetStateAction<any[]>>,
  notifications: Notification[]
}) {
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [editedTitle, setEditedTitle] = useState(event.name);
  const [editedLocation, setEditedLocation] = useState(event.location);
  const parseEventDateRange = (dateStr: string) => {
    if (dateStr.includes(' - ')) {
      const [start, end] = dateStr.split(' - ');
      return { start, end };
    }
    return { start: dateStr, end: '' };
  };
  const dateRange = parseEventDateRange(event.date);
  const [editedDateStart, setEditedDateStart] = useState(dateRange.start);
  const [editedDateEnd, setEditedDateEnd] = useState(dateRange.end);
  const [editedDescription, setEditedDescription] = useState(event.description);
  const [editedImage, setEditedImage] = useState(event.cover_image);
  
  const effectiveIsAdmin = isAdmin && !viewAsSponsor;

  const handleRegenerateQRCode = async () => {
    try {
      const qrCodeBase64 = await generateEventQRCode(event.id);
      await apiPatch(`/api/events/${event.id}`, { qrCode: qrCodeBase64 });
      toast.success("QR Code generato e salvato correttamente!");
    } catch (error) {
      console.error("Error generating QR code:", error);
      toast.error("Errore durante la generazione del QR Code.");
    }
  };

  useEffect(() => {
    setEditedTitle(event.name);
    setEditedLocation(event.location);
    const dateRange = parseEventDateRange(event.date);
    setEditedDateStart(dateRange.start);
    setEditedDateEnd(dateRange.end);
    setEditedDescription(event.description);
    setEditedImage(event.cover_image);
  }, [event]);
  
  const handleSendAutoReport = async () => {
    if (!user?.email) {
      toast.error('Devi essere loggato per ricevere il report.');
      return;
    }
    try {
      const replyToEmail = event.managerEmails ? event.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';
      const userEmail = user.email;

      await apiPost('/api/mail', {
        to: userEmail,
        replyTo: replyToEmail,
        message: {
          subject: `Report Evento: ${event.name}`,
          text: `Inviato report dettagliato per l'evento ${event.name}.`,
          html: `<p>Inviato report dettagliato per l'evento <b>${event.name}</b>.</p>`
        }
      });
      toast.success(`Report inviato con successo a ${userEmail}!`);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Errore nell'invio del report.");
    }
  };

  const handleSaveHeader = async () => {
    try {
      if (editedTitle !== event.name) {
        await apiPatch(`/api/events/${event.id}`, {
          name: editedTitle,
        });
      }

      await apiPatch(`/api/events/${event.id}`, {
        name: editedTitle,
        location: editedLocation,
        date: editedDateEnd ? `${editedDateStart} - ${editedDateEnd}` : editedDateStart,
        description: editedDescription,
        coverImage: editedImage
      });

      // Refresh events state to show updated image
      setEvents(prevEvents =>
        prevEvents.map(ev =>
          ev.id === event.id
            ? { ...ev, name: editedTitle, location: editedLocation, date: editedDateEnd ? `${editedDateStart} - ${editedDateEnd}` : editedDateStart, description: editedDescription, cover_image: editedImage }
            : ev
        )
      );

      setIsEditingHeader(false);
      toast.success("Informazioni evento aggiornate!");
    } catch (error) {
      console.error("Error updating event header:", error);
      toast.error("Errore durante l'aggiornamento.");
    }
  };

  const handleSendReportToSponsors = async () => {
    const sponsorEmails = Array.from(new Set(stands
      .filter(s => s.status !== 'available' && s.sponsorEmail)
      .map(s => s.sponsorEmail as string)));
    
    if (sponsorEmails.length === 0) {
      toast.info('Nessuno sponsor registrato per questo evento.');
      return;
    }

    try {
      const replyToEmail = event.managerEmails ? event.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';

      for (const email of sponsorEmails) {
        await apiPost('/api/mail-queue', {
          to: email,
          replyTo: replyToEmail,
          message: {
            subject: `Report Evento: ${event.name}`,
            text: `Inviato report aggiornato per l'evento ${event.name}.`,
            html: `<p>Inviato report aggiornato per l'evento <b>${event.name}</b>.</p>`
          }
        });
      }
      toast.success(`Report inviato con successo a ${sponsorEmails.length} sponsor!`);
    } catch (error) {
      console.error("Error sending emails to sponsors:", error);
      toast.error("Errore durante l'invio delle email.");
    }
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-[#676767] font-medium transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Torna agli eventi
        </button>
        <div className="flex flex-wrap gap-2">
          {effectiveIsAdmin && (
            <button 
              onClick={() => isEditingHeader ? handleSaveHeader() : setIsEditingHeader(true)}
              className={`text-sm ${isEditingHeader ? 'bg-green-600 hover:bg-green-700' : 'bg-[#ffa064] hover:bg-[#e88a4d]'} text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2`}
            >
              {isEditingHeader ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              {isEditingHeader ? 'Salva Modifiche' : 'Modifica Head'}
            </button>
          )}
          {isEditingHeader && (
            <button 
              onClick={() => {
                setIsEditingHeader(false);
                setEditedTitle(event.name);
                setEditedLocation(event.location);
                const dateRange = parseEventDateRange(event.date);
                setEditedDateStart(dateRange.start);
                setEditedDateEnd(dateRange.end);
                setEditedDescription(event.description);
                setEditedImage(event.cover_image);
              }}
              className="text-sm bg-gray-100 text-gray-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors shadow-sm flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Annulla
            </button>
          )}
          {effectiveIsAdmin && (
            <button 
              onClick={handleSendReportToSponsors}
              className="text-sm bg-[#676767] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#1d2127] transition-colors shadow-sm flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Invia Report agli Sponsor
            </button>
          )}
          {user && (
            <button 
              onClick={handleSendAutoReport}
              className="text-sm bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Ricevi Report via Email
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="h-72 sm:h-96 lg:h-[400px] relative">
          <img src={isEditingHeader ? editedImage : event.cover_image} alt={event.name} className="w-full h-full object-contain bg-gray-100" referrerPolicy="no-referrer" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/35 to-transparent"></div>
          {isEditingHeader ? (
            <div className="absolute bottom-6 left-6 right-6 space-y-3">
              <input 
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="w-full bg-white/20 backdrop-blur-md border border-white/30 text-white font-black text-3xl sm:text-4xl tracking-tight p-2 rounded-lg outline-none focus:ring-2 focus:ring-[#ffa064]"
                placeholder="Titolo Evento"
              />
              <div className="flex bg-white/10 backdrop-blur-md border border-white/20 p-2 rounded items-center">
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = async (event) => {
                        try {
                          const base64 = event.target?.result as string;
                          const compressed = await compressImage(base64, 1200, 600, 0.9);
                          setEditedImage(compressed);
                          toast.success('Immagine copertina caricata con successo!');
                        } catch (error) {
                          console.error('Error processing cover image:', error);
                          toast.error('Errore durante il caricamento dell\'immagine.');
                        }
                      };
                      reader.onerror = () => {
                        toast.error('Errore durante il caricamento dell\'immagine.');
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full text-white/80 text-xs outline-none"
                />
              </div>
            </div>
          ) : (
            <h1 className="absolute bottom-6 left-6 right-6 text-white font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {event.name}
            </h1>
          )}
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-6 mb-6">
            <div className="flex items-center text-gray-700">
              <MapPin className="w-5 h-5 mr-2 text-[#ffa064]" /> 
              {isEditingHeader ? (
                <input 
                  type="text"
                  value={editedLocation}
                  onChange={(e) => setEditedLocation(e.target.value)}
                  className="p-1 border rounded text-sm outline-none focus:ring-1 focus:ring-[#ffa064]"
                  placeholder="Luogo"
                />
              ) : (
                event.location
              )}
            </div>
            <div className="flex items-center text-gray-700">
              <Calendar className="w-5 h-5 mr-2 text-[#ffa064]" />
              {isEditingHeader ? (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={editedDateStart}
                    onChange={(e) => setEditedDateStart(e.target.value)}
                    className="p-1 border rounded text-sm outline-none focus:ring-1 focus:ring-[#ffa064]"
                  />
                  <span className="text-gray-500">al</span>
                  <input
                    type="date"
                    value={editedDateEnd}
                    onChange={(e) => setEditedDateEnd(e.target.value)}
                    className="p-1 border rounded text-sm outline-none focus:ring-1 focus:ring-[#ffa064]"
                  />
                </div>
              ) : (
                event.date
              )}
            </div>
            <div className="flex items-center text-gray-700">
              <LayoutGrid className="w-5 h-5 mr-2 text-[#ffa064]" /> 
              <span className="font-bold mr-1">{stands.length}</span> {stands.length === 1 ? 'Stand' : 'Stand'} inseriti
            </div>
          </div>
          {isEditingHeader ? (
            <textarea 
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              className="w-full p-3 border rounded-lg text-gray-600 leading-relaxed mb-6 outline-none focus:ring-2 focus:ring-[#ffa064] min-h-[100px]"
              placeholder="Descrizione dell'evento..."
            />
          ) : (
            <p className="text-gray-600 leading-relaxed mb-6">
              {event.description}
            </p>
          )}

          {effectiveIsAdmin && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h4 className="text-sm font-bold text-gray-900 mb-2">Email Event Manager (Notifiche)</h4>
              <p className="text-xs text-gray-500 mb-3">Inserisci le email degli Event Manager separandole da virgola. Saranno usate per inviare notifiche relative a questo evento.</p>
              <div className="flex items-center gap-2 max-w-xl">
                <input 
                  type="text" 
                  className="flex-1 p-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#676767] outline-none"
                  placeholder="es. nome@dominio.it, manager@dominio.it"
                  defaultValue={event.managerEmails || ''}
                  onBlur={async (e) => {
                    const newEmails = e.target.value.trim();
                    if (newEmails !== (event.managerEmails || '')) {
                      try {
                        await apiPatch(`/api/events/${event.id}`, { managerEmails: newEmails });
                        toast.success("Email aggiornate con successo!");
                      } catch (error) {
                        console.error('Error updating manager emails:', error);
                        toast.error("Errore durante l'aggiornamento.");
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {effectiveIsAdmin && (
            <div className="mt-6 pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center gap-6">
              {event.qrCode ? (
                <>
                  <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                    <img src={event.qrCode} alt="Event QR Code" className="w-32 h-32" />
                  </div>
                  <div className="text-center sm:text-left flex-1">
                    <h4 className="text-sm font-bold text-gray-900 mb-1">QR Code dell'Evento</h4>
                    <p className="text-xs text-gray-500 mb-4">Usa questo codice per permettere agli sponsor di accedere direttamente a questo evento.</p>
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                      <a 
                        href={event.qrCode} 
                        download={`qrcode-${event.name.replace(/\s+/g, '-').toLowerCase()}.png`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#676767] text-white rounded-lg text-xs font-bold hover:bg-[#0d336d] transition-all"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Scarica
                      </a>
                      <button 
                        onClick={handleRegenerateQRCode}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Rigenera
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full text-center sm:text-left p-6 flex flex-col sm:flex-row items-center justify-between border border-dashed border-gray-300 rounded-xl bg-gray-50">
                  <div className="mb-4 sm:mb-0">
                    <h4 className="text-sm font-bold text-gray-900 mb-1">QR Code Mancante</h4>
                    <p className="text-xs text-gray-500">Questo evento non ha un QR Code assegnato nel database o è obsoleto.</p>
                  </div>
                  <button 
                    onClick={handleRegenerateQRCode}
                    className="inline-flex flex-shrink-0 items-center gap-2 px-5 py-2.5 bg-[#676767] text-white rounded-lg text-sm font-bold hover:bg-[#0d336d] transition-all"
                  >
                    <QrCode className="w-4 h-4" />
                    Genera codice QR
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {user ? (
        <StandMap
          isAdmin={isAdmin}
          user={user}
          viewAsSponsor={viewAsSponsor}
          setViewAsSponsor={setViewAsSponsor}
          stands={stands}
          setStands={setStands}
          bgUrl={bgUrl}
          setBgUrl={setBgUrl}
          bgDimensions={bgDimensions}
          setBgDimensions={setBgDimensions}
          availableOptions={availableOptions}
          setAvailableOptions={setAvailableOptions}
          selectedEventId={selectedEventId}
          companyName={companyName}
          setCompanyName={setCompanyName}
          companyLogo={companyLogo}
          setCompanyLogo={setCompanyLogo}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          managerRequests={managerRequests}
          setManagerRequests={setManagerRequests}
          competitors={competitors}
          setCompetitors={setCompetitors}
          allowedStandTemplates={allowedStandTemplates}
          setAllowedStandTemplates={setAllowedStandTemplates}
          templatePrices={templatePrices}
          setTemplatePrices={setTemplatePrices}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          configTemplates={configTemplates}
          loadConfigTemplate={loadConfigTemplate}
          deleteConfigTemplate={deleteConfigTemplate}
          showSaveTemplateModal={showSaveTemplateModal}
          setShowSaveTemplateModal={setShowSaveTemplateModal}
          newTemplateName={newTemplateName}
          setNewTemplateName={setNewTemplateName}
          saveConfigTemplate={saveConfigTemplate}
          gdprConsent={gdprConsent}
          setGdprConsent={setGdprConsent}
          customWidth={customWidth}
          setCustomWidth={setCustomWidth}
          customLength={customLength}
          setCustomLength={setCustomLength}
          viewingStand={viewingStand}
          setViewingStand={setViewingStand}
          event={event}
          setRejectingStand={setRejectingStand}
          notifications={notifications}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <ShieldAlert className="w-16 h-16 text-[#676767] mx-auto mb-4 opacity-20" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Accesso Riservato</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Per visualizzare la planimetria e prenotare uno stand, è necessario effettuare l'accesso o registrarsi.
          </p>
          <button 
            onClick={() => setShowAuthModal(true)}
            className="px-8 py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-all flex items-center mx-auto"
          >
            <LogIn className="w-5 h-5 mr-2" />
            Accedi o Registrati
          </button>
        </div>
      )}

    </div>
  );
}

function StandMap({
  isAdmin,
  user,
  stands,
  setStands,
  bgUrl,
  setBgUrl,
  bgDimensions,
  setBgDimensions,
  availableOptions,
  setAvailableOptions,
  selectedEventId,
  companyName,
  setCompanyName,
  companyLogo,
  setCompanyLogo,
  selectedOptions,
  setSelectedOptions,
  managerRequests,
  setManagerRequests,
  competitors,
  setCompetitors,
  allowedStandTemplates,
  setAllowedStandTemplates,
  templatePrices,
  setTemplatePrices,
  selectedTemplate,
  setSelectedTemplate,
  configTemplates,
  loadConfigTemplate,
  deleteConfigTemplate,
  showSaveTemplateModal,
  setShowSaveTemplateModal,
  newTemplateName,
  setNewTemplateName,
  saveConfigTemplate,
  gdprConsent,
  setGdprConsent,
  customWidth,
  setCustomWidth,
  customLength,
  setCustomLength,
  viewingStand,
  setViewingStand,
  event,
  viewAsSponsor,
  setViewAsSponsor,
  setRejectingStand,
  notifications
}: {
  isAdmin: boolean,
  user: User | null,
  viewAsSponsor: boolean,
  setViewAsSponsor: (v: boolean) => void,
  stands: Stand[],
  setStands: React.Dispatch<React.SetStateAction<Stand[]>>,
  bgUrl: string | null,
  setBgUrl: React.Dispatch<React.SetStateAction<string | null>>,
  bgDimensions: { width: number, height: number },
  setBgDimensions: React.Dispatch<React.SetStateAction<{ width: number, height: number }>>,
  availableOptions: Option[],
  setAvailableOptions: React.Dispatch<React.SetStateAction<Option[]>>,
  selectedEventId: string | null,
  companyName: string,
  setCompanyName: React.Dispatch<React.SetStateAction<string>>,
  companyLogo: string,
  setCompanyLogo: React.Dispatch<React.SetStateAction<string>>,
  selectedOptions: string[],
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>,
  managerRequests: string,
  setManagerRequests: React.Dispatch<React.SetStateAction<string>>,
  competitors: string,
  setCompetitors: React.Dispatch<React.SetStateAction<string>>,
  allowedStandTemplates: string[],
  setAllowedStandTemplates: React.Dispatch<React.SetStateAction<string[]>>,
  templatePrices: { [key: string]: number },
  setTemplatePrices: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>,
  selectedTemplate: any,
  setSelectedTemplate: React.Dispatch<React.SetStateAction<any>>,
  configTemplates: StandConfigTemplate[],
  loadConfigTemplate: (id: string) => Promise<void>,
  deleteConfigTemplate: (id: string) => Promise<void>,
  showSaveTemplateModal: boolean,
  setShowSaveTemplateModal: React.Dispatch<React.SetStateAction<boolean>>,
  newTemplateName: string,
  setNewTemplateName: React.Dispatch<React.SetStateAction<string>>,
  saveConfigTemplate: () => Promise<void>,
  gdprConsent: boolean,
  setGdprConsent: React.Dispatch<React.SetStateAction<boolean>>,
  customWidth: number,
  setCustomWidth: React.Dispatch<React.SetStateAction<number>>,
  customLength: number,
  setCustomLength: React.Dispatch<React.SetStateAction<number>>,
  viewingStand: Stand | null,
  setViewingStand: React.Dispatch<React.SetStateAction<Stand | null>>,
  event: any,
  setRejectingStand: React.Dispatch<React.SetStateAction<Stand | null>>,
  notifications: Notification[]
}) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPolygon, setCurrentPolygon] = useState<[number, number][]>([]);
  const [editingStandId, setEditingStandId] = useState<string | null>(null);
  const [redrawingStandId, setRedrawingStandId] = useState<string | null>(null);
  const [copiedStand, setCopiedStand] = useState<Stand | null>(null);
  const [reservingStand, setReservingStand] = useState<Stand | null>(null);
  const [newPrice, setNewPrice] = useState<number>(0);
  const [protectionActive, setProtectionActive] = useState(false);
  const [newOptionText, setNewOptionText] = useState('');
  const [newOptionPrice, setNewOptionPrice] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteBgConfirm, setShowDeleteBgConfirm] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [isEditingRequest, setIsEditingRequest] = useState(false);
  const effectiveIsAdmin = isAdmin && !viewAsSponsor;
  const [lastConfig, setLastConfig] = useState<{ price: number, status: 'available'|'occupied'|'pending' }>({ price: 1000, status: 'available' });
  const [draggingVertex, setDraggingVertex] = useState<{ standId: string, index: number } | null>(null);
  const [draggingStand, setDraggingStand] = useState<{ standId: string, startX: number, startY: number, originalPoints: [number, number][] } | null>(null);
  // Set to true at the end of a drag so the synthetic click that follows
  // mouseup doesn't toggle selection unintentionally.
  const justDraggedRef = useRef(false);
  const [standToDelete, setStandToDelete] = useState<string | null>(null);
  const [gdprMarketingConsent, setGdprMarketingConsent] = useState(false);
  const [contractorNameInput, setContractorNameInput] = useState('');
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [manualWidth, setManualWidth] = useState(2);
  const [manualLength, setManualLength] = useState(2);
  const [selectedStandId, setSelectedStandId] = useState<string | null>(null);

  useEffect(() => {
    if (viewingStand) {
      setProtectionActive(viewingStand.competitor_protection_active || false);
      setNewPrice(viewingStand.price || 0);
      setContractorNameInput(viewingStand.contractorName || '');
    }
  }, [viewingStand]);

  // Clear stand selection when switching events
  useEffect(() => {
    setSelectedStandId(null);
  }, [selectedEventId]);

  // Stand count sync removed - no longer stored in events table

  const clearAllStands = async () => {
    if (!selectedEventId) return;
    console.log("Clearing stands and bgUrl.");
    try {
      for (const stand of stands) {
        await apiDelete(`/api/stands/${stand.id}`);
      }
      await apiPatch(`/api/events/${selectedEventId}`, { floorPlanImage: '' });
      setShowClearAllConfirm(false);
    } catch (error) {
      console.error("Error clearing stands:", error);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    try {
      // Upload to R2 storage instead of base64
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', `floor-plans/${selectedEventId}`);

      const response = await fetch('/api/upload-image', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const uploadResult = await response.json();
      const r2Url = uploadResult.url;

      if (r2Url) {
        if (selectedEventId) {
          try {
            await apiPatch(`/api/events/${selectedEventId}`, { floorPlanImage: r2Url });
            // Automatically clear stands when a new plan is uploaded to avoid misalignment
            for (const stand of stands) {
              await apiDelete(`/api/stands/${stand.id}`);
            }
            toast.success("Piantina aggiornata con successo!");
          } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, `events`);
          }
        }
      } else {
        toast.error("Impossibile elaborare l'immagine. Riprova.");
      }
    } catch (error) {
      handleDatabaseError(error, OperationType.WRITE, `events`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddStand = async () => {
    if (!selectedEventId) return;
    
    // Assumi 1 metro = 20 pixels visivamente sulla mappa per la generazione iniziale
    const wPx = manualWidth * 20;
    const lPx = manualLength * 20;

    const newStandData = {
      label: 'Nuovo',
      price: lastConfig.price || 1000,
      status: 'available',
      custom_dimensions: { width: manualWidth, length: manualLength },
      svg: {
        points: `10,10 ${10+wPx},10 ${10+wPx},${10+lPx} 10,${10+lPx}`,
        text: [10 + wPx/2 - 10, 10 + lPx/2 + 5]
      }
    };
    try {
      const result = await api.stands.create({
        ...newStandData,
        event_id: selectedEventId,
        id: `stand-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      });
      setEditingStandId((result as any).id);
    } catch (error) {
      handleDatabaseError(error, OperationType.CREATE, `stands`);
    }
  };

  const handleAddBookedSubmit = async (e: React.FormEvent, drawOnMap: boolean) => {
    e.preventDefault();
    setShowAddBookedModal(false);
    
    if (drawOnMap) {
      setIsDrawing(true);
      setIsDrawingBooked(true);
      setEditingStandId(null);
      toast.info("Clicca sulla mappa per disegnare lo stand prenotato.");
    } else {
      if (!selectedEventId) return;
      const newStandData = {
        label: bookedStandConfig.label || `Stand ${stands.length + 1}`,
        price: bookedStandConfig.price,
        status: 'occupied',
        company: bookedStandConfig.company,
        options: bookedStandConfig.options,
        templateId: bookedStandConfig.templateId,
        custom_dimensions: bookedStandConfig.templateId === 'custom' ? { width: bookedStandConfig.customWidth, length: bookedStandConfig.customLength } : null,
        svg: {
          points: "10,10 50,10 50,50 10,50",
          text: [20, 30]
        }
      };
      try {
        const standId = `stand-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        await api.stands.create({
          ...newStandData,
          event_id: selectedEventId,
          id: standId
        });
        setEditingStandId(standId);
        toast.success("Stand prenotato aggiunto con successo!");
        setBookedStandConfig({ label: '', company: '', price: 1000, options: [], templateId: STANDS_TEMPLATES[0].id, customWidth: 2, customLength: 2 });
      } catch (error) {
        handleDatabaseError(error, OperationType.CREATE, `stands`);
      }
    }
  };

  const updateStand = async (id: string, updates: Partial<Stand>) => {
    if (!selectedEventId) return;
    try {
      await apiPatch(`/api/stands/${id}`, updates);
    } catch (error) {
      handleDatabaseError(error, OperationType.UPDATE, `stands/${id}`);
    }
  };

  const deleteStand = async (id: string) => {
    if (!selectedEventId) return;
    try {
      await api.stands.delete(id);
      if (editingStandId === id) setEditingStandId(null);
      if (selectedStandId === id) setSelectedStandId(null);
    } catch (error) {
      handleDatabaseError(error, OperationType.DELETE, `stands/${id}`);
    }
  };

  // Keyboard handler: Delete/Backspace deletes selected stand, Escape deselects
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!effectiveIsAdmin || !selectedStandId) return;
      const target = e.target as HTMLElement | null;
      if (target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      )) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const stand = stands.find(s => s.id === selectedStandId);
        const label = stand?.label || selectedStandId;
        if (window.confirm(`Eliminare lo stand "${label}"? L'operazione non è reversibile.`)) {
          deleteStand(selectedStandId);
          setSelectedStandId(null);
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedStandId(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedStandId, stands, effectiveIsAdmin]);

  const duplicateStand = async (standToDuplicate: Stand) => {
    if (!selectedEventId || !standToDuplicate.svg || !standToDuplicate.svg.points) return;

    // Offset the points slightly so it's visible
    const offset = 20;
    const newPoints = standToDuplicate.svg.points.split(' ').map(point => {
      const parts = point.split(',');
      if (parts.length === 2) {
        const [x, y] = parts.map(Number);
        if (!isNaN(x) && !isNaN(y)) {
          return `${x + offset},${y + offset}`;
        }
      }
      return point;
    }).join(' ');

    const newTextX = (standToDuplicate.svg.text?.[0] || 0) + offset;
    const newTextY = (standToDuplicate.svg.text?.[1] || 0) + offset;

    const newStandData: any = {
      label: `${standToDuplicate.label} (Copia)`,
      price: standToDuplicate.price,
      status: 'available',
      svg: {
        points: newPoints,
        text: [newTextX, newTextY]
      }
    };

    if (standToDuplicate.custom_dimensions) {
      newStandData.custom_dimensions = standToDuplicate.custom_dimensions;
    }
    if ((standToDuplicate as any).originalPrice ?? (standToDuplicate as any).original_price) {
      newStandData.originalPrice = (standToDuplicate as any).originalPrice ?? (standToDuplicate as any).original_price;
    }
    if ((standToDuplicate as any).standTemplate ?? (standToDuplicate as any).stand_template) {
      newStandData.standTemplate = (standToDuplicate as any).standTemplate ?? (standToDuplicate as any).stand_template;
    }
    if ((standToDuplicate as any).options) {
      newStandData.options = (standToDuplicate as any).options;
    }

    try {
      const result = await api.stands.create({
        ...newStandData,
        event_id: selectedEventId,
        id: `stand-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      });
      setEditingStandId((result as any).id);
      toast.success('Stand duplicato con successo!');
    } catch (error) {
      handleDatabaseError(error, OperationType.CREATE, `stands`);
      toast.error('Errore durante la duplicazione dello stand.');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!effectiveIsAdmin) return;
      
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (editingStandId) {
          const standToCopy = stands.find(s => s.id === editingStandId);
          if (standToCopy) {
            setCopiedStand(standToCopy);
            toast.success(`Stand "${standToCopy.label}" copiato.`);
          }
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (copiedStand) {
          duplicateStand(copiedStand);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [effectiveIsAdmin, editingStandId, stands, copiedStand, selectedEventId]);

  const editingStand = stands.find(s => s.id === editingStandId);

  const handleStandClick = (stand: Stand) => {
    if (effectiveIsAdmin) {
      if (isDrawing || draggingStand) return; // Don't select stands while drawing or dragging
      setEditingStandId(stand.id);
    } else {
      if (stand.status === 'available') {
        setReservingStand({ ...stand, price: 0 });
        setCompanyName('');
        setGdprConsent(false);
        setGdprMarketingConsent(false);
        if (stand.custom_dimensions) {
          setCustomWidth(stand.custom_dimensions.width);
          setCustomLength(stand.custom_dimensions.length);
          const customTemplate = STANDS_TEMPLATES.find(t => t.id === 'custom');
          if (customTemplate) setSelectedTemplate(customTemplate);
        } else {
          setCustomWidth(2);
          setCustomLength(2);
          const firstAllowed = STANDS_TEMPLATES.find(t => allowedStandTemplates.includes(t.id) && t.id !== 'custom');
          if (firstAllowed) setSelectedTemplate(firstAllowed);
        }
      } else {
        setViewingStand(stand);
      }
    }
  };

  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!effectiveIsAdmin || !isDrawing) return;
    
    const svg = e.currentTarget;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    
    // Get the transformation matrix to convert screen coordinates to SVG coordinates
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    
    const svgP = pt.matrixTransform(ctm.inverse());
    
    setCurrentPolygon([...currentPolygon, [Math.round(svgP.x), Math.round(svgP.y)]]);
  };

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!effectiveIsAdmin) return;
    
    const svg = e.currentTarget;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    
    const svgP = pt.matrixTransform(ctm.inverse());
    
    if (draggingVertex) {
      const stand = stands.find(s => s.id === draggingVertex.standId);
      if (!stand || !stand.svg || !stand.svg.points) return;

      const points = stand.svg.points.split(' ').map(p => p.split(',').map(Number));
      points[draggingVertex.index] = [Math.round(svgP.x), Math.round(svgP.y)];
      
      // Recalculate center
      let minX = points[0][0], maxX = points[0][0];
      let minY = points[0][1], maxY = points[0][1];
      
      points.forEach(p => {
        if (p[0] < minX) minX = p[0];
        if (p[0] > maxX) maxX = p[0];
        if (p[1] < minY) minY = p[1];
        if (p[1] > maxY) maxY = p[1];
      });
      
      const centerX = Math.round(minX + (maxX - minX) / 2);
      const centerY = Math.round(minY + (maxY - minY) / 2);
      
      // Optimistic local update only — no network call during drag.
      // The PATCH is sent once on mouseup.
      const newPointsStr = points.map(p => `${p[0]},${p[1]}`).join(' ');
      setStands(prev => prev.map(s =>
        s.id === draggingVertex.standId
          ? { ...s, svg: { ...(s.svg ?? {}), points: newPointsStr, text: [centerX, centerY] } }
          : s
      ));
    } else if (draggingStand) {
      const dx = svgP.x - draggingStand.startX;
      const dy = svgP.y - draggingStand.startY;
      
      const newPoints = draggingStand.originalPoints.map(p => [
        Math.round(p[0] + dx),
        Math.round(p[1] + dy)
      ]);
      
      // Recalculate center
      let minX = newPoints[0][0], maxX = newPoints[0][0];
      let minY = newPoints[0][1], maxY = newPoints[0][1];
      
      newPoints.forEach(p => {
        if (p[0] < minX) minX = p[0];
        if (p[0] > maxX) maxX = p[0];
        if (p[1] < minY) minY = p[1];
        if (p[1] > maxY) maxY = p[1];
      });
      
      const centerX = Math.round(minX + (maxX - minX) / 2);
      const centerY = Math.round(minY + (maxY - minY) / 2);
      
      // Optimistic local update only — no network call during drag.
      // The PATCH is sent once on mouseup.
      const newPointsStr = newPoints.map(p => `${p[0]},${p[1]}`).join(' ');
      setStands(prev => prev.map(s =>
        s.id === draggingStand.standId
          ? { ...s, svg: { ...(s.svg ?? {}), points: newPointsStr, text: [centerX, centerY] } }
          : s
      ));
    }
  };

  const handleSvgMouseUp = async () => {
    // Track whether this mouseup actually ended a drag, so the upcoming
    // synthetic `click` event on the polygon doesn't toggle selection.
    const wasDragging = !!(draggingVertex || draggingStand);

    // Persist the final position once, after the user releases the mouse.
    if (draggingVertex) {
      const stand = stands.find(s => s.id === draggingVertex.standId);
      if (stand && stand.svg) {
        try {
          await apiPatch(`/api/stands/${stand.id}`, {
            svg: {
              points: stand.svg.points,
              text: stand.svg.text
            }
          });
          toast.success('Stand aggiornato');
        } catch (error) {
          console.error('Failed to update stand:', error);
          // Revert on error
          const updatedStands = await apiGet(`/api/events/${selectedEventId}/stands`);
          setStands(Array.isArray(updatedStands) ? updatedStands.map(normalizeStand) : []);
        }
      }
      setDraggingVertex(null);
    }
    if (draggingStand) {
      const stand = stands.find(s => s.id === draggingStand.standId);
      if (stand && stand.svg) {
        try {
          await apiPatch(`/api/stands/${stand.id}`, {
            svg: {
              points: stand.svg.points,
              text: stand.svg.text
            }
          });
          toast.success('Stand spostato');
        } catch (error) {
          console.error('Failed to update stand:', error);
          // Revert on error
          const updatedStands = await apiGet(`/api/events/${selectedEventId}/stands`);
          setStands(Array.isArray(updatedStands) ? updatedStands.map(normalizeStand) : []);
        }
      }
      setDraggingStand(null);
    }

    if (wasDragging) {
      justDraggedRef.current = true;
    }
  };

  const handlePolygonMouseDown = (e: React.MouseEvent, stand: Stand) => {
    if (!effectiveIsAdmin || isDrawing || draggingVertex) return;
    e.stopPropagation();
    
    const svg = (e.currentTarget as SVGElement).closest('svg');
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm || !stand.svg || !stand.svg.points) return;
    const svgP = pt.matrixTransform(ctm.inverse());

    const points = stand.svg.points.split(' ').map(p => p.split(',').map(Number) as [number, number]);
    setDraggingStand({
      standId: stand.id,
      startX: svgP.x,
      startY: svgP.y,
      originalPoints: points
    });
    setEditingStandId(stand.id);
  };

  const finishDrawing = async () => {
    if (currentPolygon.length < 3) {
      toast.error("Disegna almeno 3 punti per formare un poligono.");
      return;
    }
    
    // Calculate center for text
    let minX = currentPolygon[0][0], maxX = currentPolygon[0][0];
    let minY = currentPolygon[0][1], maxY = currentPolygon[0][1];
    
    currentPolygon.forEach(p => {
      if (p[0] < minX) minX = p[0];
      if (p[0] > maxX) maxX = p[0];
      if (p[1] < minY) minY = p[1];
      if (p[1] > maxY) maxY = p[1];
    });
    
    const centerX = Math.round(minX + (maxX - minX) / 2);
    const centerY = Math.round(minY + (maxY - minY) / 2);

    const svgData = {
      points: currentPolygon.map(p => `${p[0]},${p[1]}`).join(' '),
      text: [centerX, centerY]
    };

    if (redrawingStandId) {
      try {
        await apiPatch(`/api/stands/${redrawingStandId}`, {
          svg: svgData
        });
        setEditingStandId(redrawingStandId);
        setRedrawingStandId(null);
        setCurrentPolygon([]);
        setIsDrawing(false);
        toast.success("Stand ridisegnato con successo!");
      } catch (error) {
        handleDatabaseError(error, OperationType.UPDATE, `events/${selectedEventId}/stands/${redrawingStandId}`);
      }
      return;
    }

    const newStandData = isDrawingBooked ? {
      label: bookedStandConfig.label || `Stand ${stands.length + 1}`,
      price: bookedStandConfig.price,
      status: 'occupied',
      company: bookedStandConfig.company,
      options: bookedStandConfig.options,
      templateId: bookedStandConfig.templateId,
      custom_dimensions: bookedStandConfig.templateId === 'custom' ? { width: bookedStandConfig.customWidth, length: bookedStandConfig.customLength } : null,
      svg: svgData
    } : {
      label: `Stand ${stands.length + 1}`,
      price: lastConfig.price,
      status: lastConfig.status,
      svg: svgData
    };
    
    try {
      const standId = `stand-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      await api.stands.create({
        ...newStandData,
        event_id: selectedEventId!,
        id: standId
      });
      setEditingStandId(standId);
      setCurrentPolygon([]);
      setIsDrawing(false);
      if (isDrawingBooked) {
        setIsDrawingBooked(false);
        toast.success("Stand prenotato disegnato con successo!");
        setBookedStandConfig({ label: '', company: '', price: 1000, options: [], templateId: STANDS_TEMPLATES[0].id, customWidth: 2, customLength: 2 });
      }
    } catch (error) {
      handleDatabaseError(error, OperationType.CREATE, `stands`);
    }
  };

  const cancelDrawing = () => {
    setCurrentPolygon([]);
    setIsDrawing(false);
    setIsDrawingBooked(false);
    setRedrawingStandId(null);
  };

  const handleConfirmReservation = async () => {
    if (!companyName.trim()) {
      toast.error("Inserisci il nome dell'azienda.");
      return;
    }
    if (!gdprConsent) {
      toast.error("Devi acconsentire al trattamento dei dati (GDPR) per procedere.");
      return;
    }
    if (reservingStand && selectedEventId) {
      // Parse event date to check if it's > 30 days away
      const parseItalianDate = (dateStr: string) => {
        // Try ISO format first (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return new Date(dateStr);
        }

        const months: { [key: string]: number } = {
          'gennaio': 0, 'febbraio': 1, 'marzo': 2, 'aprile': 3, 'maggio': 4, 'giugno': 5,
          'luglio': 6, 'agosto': 7, 'settembre': 8, 'ottobre': 9, 'novembre': 10, 'dicembre': 11
        };
        const parts = dateStr.toLowerCase().split(' ');
        if (parts.length >= 3) {
          const day = parseInt(parts[0]);
          const month = months[parts[1]];
          const year = parseInt(parts[2]);
          if (!isNaN(day) && month !== undefined && !isNaN(year)) {
            return new Date(year, month, day);
          }
        }
        return new Date(); // Fallback
      };

      const eventStartDateStr = event.date.split(' - ')[0];
      const eventStartDate = parseItalianDate(eventStartDateStr);
      const now = new Date();
      const daysUntilEvent = (eventStartDate.getTime() - now.getTime()) / (1000 * 3600 * 24);

      if (daysUntilEvent < 30) {
        toast.error("Non è possibile prenotare lo stand tramite carrello a meno di 30 giorni dall'evento.");
        return;
      }

      // When a sponsor books, it goes to 'pending' (revision) status
      try {
        // --- Competitor protection check ---
        // Blocca la prenotazione se uno stand adiacente è occupato e protetto
        // e c'è un conflitto bidirezionale con la lista competitor
        const occupiedProtected = stands.filter(s =>
          s.competitor_protection_active && s.status === 'occupied' && s.id !== reservingStand.id
        );
        for (const protStand of occupiedProtected) {
          if (areStandsAdjacent(reservingStand, protStand) && isCompetitorConflict(companyName, competitors, protStand)) {
            toast.error(
              `Prenotazione bloccata: lo stand ${reservingStand.label} è adiacente allo stand ${protStand.label} (${protStand.company}) che ha la protezione competitor attiva.`,
              { duration: 8000 }
            );
            // Avvisa il competitor protetto
            if (protStand.sponsorId) {
              await api.notifications.create({
                userId: protStand.sponsorId,
                type: 'competitor_attempt',
                message: `⚠️ L'azienda "${companyName}" ha tentato di prenotare lo stand ${reservingStand.label}, adiacente al tuo stand ${protStand.label}. La prenotazione è stata bloccata dalla protezione competitor.`,
                read: false,
                relatedEventId: selectedEventId,
                relatedStandId: protStand.id
              });
            }
            return;
          }
        }
        // --- Fine competitor protection check ---

        const basePrice = reservingStand.price || 0;
        let templatePrice = templatePrices[selectedTemplate.id] || 0;
        if (selectedTemplate.id === 'custom') {
          templatePrice = (templatePrices['custom'] || 0) * (customWidth * customLength);
        }
        const finalBasePrice = basePrice + templatePrice;

        const cartCreatedAt = new Date();

        await apiPatch(`/api/stands/${reservingStand.id}`, { 
          status: 'pending', 
          price: finalBasePrice,
          originalPrice: basePrice,
          company: companyName,
          company_logo: companyLogo || `https://picsum.photos/seed/${companyName}/100/100`,
          options: selectedOptions,
          manager_requests: managerRequests,
          competitors: competitors,
          stand_template: selectedTemplate.id,
          custom_dimensions: selectedTemplate.id === 'custom' ? { width: customWidth, length: customLength } : null,
          reservedAt: cartCreatedAt.toISOString(),
          sponsorId: user?.uid || getCurrentUserId(auth, user) || 'anonymous',
          sponsorEmail: user?.email || getCurrentUserEmail(auth, user) || 'anonymous',
          sponsorName: auth.currentUser?.displayName || companyName,
          reminderSent: false,
          gdprConsent,
          gdprMarketingConsent
        });
        
        // Add notification for Event Manager
        await api.notifications.create({
          userId: 'manager',
          type: 'new_reservation',
          message: `Nuova richiesta di prenotazione per lo stand ${reservingStand.label} da ${companyName}.`,
          read: false,
          relatedEventId: selectedEventId,
          relatedStandId: reservingStand.id
        });

        // Add notification for Sponsor
        if (auth.currentUser) {
          const replyToEmail = event.managerEmails ? event.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';
          
          await api.notifications.create({
            userId: auth.currentUser.uid,
            type: 'cart_created',
            message: `Hai aggiunto lo stand ${reservingStand.label} al carrello. Procedi al caricamento dei documenti.`,
            read: false,
            relatedEventId: selectedEventId,
            relatedStandId: reservingStand.id
          });
          
          await apiPost('/api/mail', {
            to: auth.currentUser.email,
            replyTo: replyToEmail,
            message: {
              subject: `Conferma inserimento a carrello - Stand ${reservingStand.label}`,
              text: `Hai aggiunto lo stand ${reservingStand.label} al carrello. Procedi al caricamento dei documenti necessari nella piattaforma.`,
              html: `<p>Hai aggiunto lo stand <b>${reservingStand.label}</b> al carrello. Procedi al caricamento dei documenti necessari nella piattaforma.</p>`
            }
          });
        }

        // Send Email to Event Managers
        const managerEmailsList = event.managerEmails ? event.managerEmails.split(',').map((e: string) => e.trim()).filter(Boolean) : [];
        if (managerEmailsList.length > 0) {
          const replyToEmail = event.managerEmails ? event.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';
          for (const email of managerEmailsList) {
            await apiPost('/api/mail-queue', {
              to: email,
              replyTo: replyToEmail,
              message: {
                subject: `Nuova Prenotazione Stand: ${reservingStand.label}`,
                text: `Lo sponsor ${companyName} ha prenotato lo stand ${reservingStand.label}.`,
                html: `<p>Lo sponsor <b>${companyName}</b> ha prenotato lo stand <b>${reservingStand.label}</b>.</p>`
              }
            });
          }
        }
        
        setReservingStand(null);
        setViewingStand({
          ...reservingStand,
          status: 'pending',
          price: finalBasePrice,
          originalPrice: basePrice,
          company: companyName,
          company_logo: companyLogo || `https://picsum.photos/seed/${companyName}/100/100`,
          options: selectedOptions,
          manager_requests: managerRequests,
          competitors: competitors,
          stand_template: selectedTemplate.id,
          custom_dimensions: selectedTemplate.id === 'custom' ? { width: customWidth, length: customLength } : null,
          reservedAt: cartCreatedAt.toISOString(),
          sponsorId: user?.uid || getCurrentUserId(auth, user) || 'anonymous',
          sponsorEmail: user?.email || getCurrentUserEmail(auth, user) || 'anonymous',
          sponsorName: auth.currentUser?.displayName || companyName,
          reminderSent: false,
          gdprConsent,
          gdprMarketingConsent
        } as Stand);
        setCompanyName('');
        setCompanyLogo('');
        setSelectedOptions([]);
        setManagerRequests('');
        setCompetitors('');
        setSelectedTemplate(STANDS_TEMPLATES[0]);
        setCustomWidth(2);
        setCustomLength(2);
        setGdprMarketingConsent(false);
        setGdprConsent(false);
        // setShowCartCreatedModal(true); // Temporarily disabled - scope issue
        toast.success('Richiesta inviata con successo! Lo stand è ora in fase di revisione.');
      } catch (error) {
        handleDatabaseError(error, OperationType.UPDATE, `events/${selectedEventId}/stands/${reservingStand.id}`);
      }
    }
  };

  const handleStandDocumentUpload = async (docTypeId: string, file: File) => {
    if (!selectedEventId || !viewingStand) return;

    setIsUploadingDoc(true);
    try {
      // 1. Upload file to R2 via /api/upload-image
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', `documents/${viewingStand.id}`);

      const sessionToken = localStorage.getItem('session_token');
      const uploadRes = await fetch('/api/upload-image', {
        method: 'POST',
        headers: sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {},
        body: formData
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.error || `Upload fallito (${uploadRes.status})`);
      }

      const uploadData = await uploadRes.json();
      const downloadUrl: string = uploadData.data?.url;
      if (!downloadUrl) throw new Error('URL non ricevuto da upload');

      // 2. Aggiorna Database con metadati documento
      const docData = {
        url: downloadUrl,
        status: 'pending' as const,
        uploadedAt: new Date().toISOString(),
        name: file.name,
        size: file.size,
        mimeType: file.type
      };
      
      const updatedDocuments = {
        ...(viewingStand.documents || {}),
        [docTypeId]: docData
      };
      await api.stands.update(viewingStand.id, {
        documents: updatedDocuments
      });
      
      // 3. Se è il bonifico e siamo in payment_pending, notifica admin
      if (docTypeId === 'bank_receipt' && viewingStand.status === 'payment_pending') {
        await api.stands.update(viewingStand.id, {
          status: 'payment_verification',
          paymentReceiptUploadedAt: new Date().toISOString()
        });

        // Notifica in-app admin
        await api.notifications.create({
          userId: 'manager',
          type: 'payment_receipt_uploaded',
          message: `📄 ${viewingStand.company} ha caricato la distinta di bonifico per lo stand ${viewingStand.label}. Verifica e conferma.`,
          read: false,
          relatedEventId: selectedEventId,
          relatedStandId: viewingStand.id
        });

        // Email a tutti i manager dell'evento + ordini@summeet.it (sempre in copia)
        const recipients: string[] = ['ordini@summeet.it'];
        if (event?.managerEmails) {
          const managers = (event.managerEmails as string).split(',').map((e: string) => e.trim()).filter(Boolean);
          recipients.unshift(...managers);
        }
        for (const to of recipients) {
          await apiPost('/api/mail', {
            to,
            replyTo: viewingStand.sponsorEmail || 'noreply@summeet.it',
            message: {
              subject: `Bonifico caricato — Stand ${viewingStand.label}`,
              text: `${viewingStand.company} ha caricato la distinta di bonifico per lo stand ${viewingStand.label}. Accedi alla piattaforma per verificare e confermare il pagamento.`,
              html: `<p><strong>${viewingStand.company}</strong> ha caricato la distinta di bonifico per lo stand <strong>${viewingStand.label}</strong>.</p><p>Accedi alla piattaforma per verificare e confermare il pagamento.</p>`
            }
          });
        }
      }

      // 4. Se sono tutti i documenti obbligatori e siamo in pending, passa a document_review
      if (viewingStand.status === 'pending') {
        const requiredDocs = STAND_DOCUMENT_TYPES.filter(d => d.required);
        const uploadedRequired = requiredDocs.every(d => {
          if (d.id === docTypeId) return true;
          return viewingStand.documents?.[d.id]?.url;
        });

        if (uploadedRequired) {
          await api.stands.update(viewingStand.id, {
            status: 'document_review',
            documentsSubmittedAt: new Date().toISOString()
          });

          // Notifica in-app admin
          await api.notifications.create({
            userId: 'manager',
            type: 'documents_submitted',
            message: `📋 ${viewingStand.company} ha caricato tutti i documenti per lo stand ${viewingStand.label}. Procedi con la verifica.`,
            read: false,
            relatedEventId: selectedEventId,
            relatedStandId: viewingStand.id
          });

          // Email a tutti i manager dell'evento
          if (event?.managerEmails) {
            const managers = (event.managerEmails as string).split(',').map((e: string) => e.trim()).filter(Boolean);
            for (const mgr of managers) {
              await apiPost('/api/mail', {
                to: mgr,
                replyTo: viewingStand.sponsorEmail || 'noreply@summeet.it',
                message: {
                  subject: `Documenti pronti per verifica — Stand ${viewingStand.label}`,
                  text: `${viewingStand.company} ha caricato tutti i documenti richiesti per lo stand ${viewingStand.label}. Accedi alla piattaforma per effettuare la verifica.`,
                  html: `<p><strong>${viewingStand.company}</strong> ha caricato tutti i documenti richiesti per lo stand <strong>${viewingStand.label}</strong>.</p><p>Accedi alla piattaforma per effettuare la verifica e procedere con il pagamento.</p>`
                }
              });
            }
          }
        }
      }
      
      toast.success(`Documento "${STAND_DOCUMENT_TYPES.find(d => d.id === docTypeId)?.label}" caricato con successo.`);
      
      // Aggiorna stato locale
      setViewingStand(prev => {
        if (!prev) return null;
        const newDocs = { ...(prev.documents || {}), [docTypeId]: docData };
        const requiredDocs = STAND_DOCUMENT_TYPES.filter(d => d.required);
        const uploadedRequired = requiredDocs.every(d => newDocs[d.id]?.url);
        
        return {
          ...prev,
          documents: newDocs,
          status: prev.status === 'pending' && uploadedRequired ? 'document_review' : prev.status
        };
      });
      
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Errore durante il caricamento. Riprova.');
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const handleApproveStandDocument = async (docTypeId: string) => {
    if (!selectedEventId || !viewingStand || !viewingStand.documents?.[docTypeId]) return;
    try {
      // Gestisci documents safely
      const currentDocs = viewingStand.documents || {};
      const currentDocData = currentDocs[docTypeId] || {};
      const updatedDoc = { ...currentDocData, status: 'approved' as const };

      const newDocs = { ...currentDocs, [docTypeId]: updatedDoc };

      await api.stands.update(viewingStand.id, {
        documents: newDocs
      });
      setViewingStand(prev => prev ? {
        ...prev,
        documents: newDocs
      } : null);
      toast.success('Documento approvato.');
    } catch (error) {
      handleDatabaseError(error, OperationType.UPDATE, `events/${selectedEventId}/stands/${viewingStand.id}`);
    }
  };

  const handleApproveDocuments = async (standId: string) => {
    if (!selectedEventId || !viewingStand) return;
    
    try {
      // standId is already defined
      const now = new Date();
      const paymentDeadline = new Date(now.getTime() + 96 * 60 * 60 * 1000); // +96 ore
      
      // 1. Aggiorna stato stand e attiva timer
      const updatedDocs = { ...(viewingStand.documents || {}) };
      Object.keys(updatedDocs).forEach(key => {
        if (updatedDocs[key]) {
          updatedDocs[key] = { ...updatedDocs[key], status: 'approved' as const };
        }
      });

      await api.stands.update(viewingStand.id, {
        status: 'payment_pending',
        documentsApprovedAt: now.toISOString(),
        paymentExpiresAt: paymentDeadline.toISOString(),
        documents: updatedDocs
      });
      
      // 2. Notifica allo sponsor
      if (viewingStand.sponsorId) {
        await api.notifications.create({
          userId: viewingStand.sponsorId,
          type: 'documents_approved_payment_required',
          message: `✅ I tuoi documenti per lo stand ${viewingStand.label} sono stati approvati. Hai 96 ore per completare il pagamento (scadenza: ${paymentDeadline.toLocaleString('it-IT')}).`,
          read: false,
          relatedEventId: selectedEventId,
          relatedStandId: standId,
          actionLink: `/event/${selectedEventId}/stand/${standId}`
        });
      }
      
      // 3. Log email (da integrare con servizio email reale)
      if (viewingStand.sponsorEmail) {
        const replyToEmail = event.managerEmails ? event.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';
        
        await apiPost('/api/mail', {
          to: viewingStand.sponsorEmail,
          replyTo: replyToEmail,
          message: {
            subject: `Documenti Approvati - Procedi al Pagamento (Stand ${viewingStand.label})`,
            text: `I tuoi documenti per lo stand ${viewingStand.label} sono stati approvati. Hai 96 ore di tempo per completare il pagamento (scadenza: ${paymentDeadline.toLocaleString('it-IT')}).`,
            html: `<p>I tuoi documenti per lo stand <b>${viewingStand.label}</b> sono stati approvati.</p><p>Hai 96 ore di tempo per completare il pagamento e caricare la distinta sulla piattaforma (scadenza: <b>${paymentDeadline.toLocaleString('it-IT')}</b>).</p>`
          }
        });
      }
      
      toast.success('Documenti approvati! Timer pagamento attivato (96 ore).');
      setViewingStand(prev => prev ? { 
        ...prev, 
        status: 'payment_pending', 
        documentsApprovedAt: now.toISOString(),
        paymentExpiresAt: paymentDeadline.toISOString(),
        documents: updatedDocs
      } : null);
      
    } catch (error) {
      handleDatabaseError(error, OperationType.UPDATE, `events/${selectedEventId}/stands/${standId}`);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedEventId || !viewingStand) return;
    
    try {
      // viewingStand.id is used directly
      
      // 1. Aggiorna a "occupied" definitivo
      await api.stands.update(viewingStand.id, {
        status: 'occupied',
        paymentConfirmedAt: new Date().toISOString()
      });
      
      // 2. Notifica in-app sponsor
      if (viewingStand.sponsorId) {
        await api.notifications.create({
          userId: viewingStand.sponsorId,
          type: 'payment_confirmed_stand_assigned',
          message: `🎉 Pagamento confermato! Lo stand ${viewingStand.label} è ufficialmente tuo. Riceverai a breve la fattura elettronica e i dettagli per l'allestimento.`,
          read: false,
          relatedEventId: selectedEventId,
          relatedStandId: viewingStand.id,
          actionLink: `/event/${selectedEventId}/stand/${viewingStand.id}/confirmation`
        });
      }

      // 3. Email sponsor: pagamento confermato
      if (viewingStand.sponsorEmail) {
        const replyTo = event?.managerEmails?.split(',')[0]?.trim() || 'noreply@summeet.it';
        await apiPost('/api/mail', {
          to: viewingStand.sponsorEmail,
          replyTo,
          message: {
            subject: `🎉 Pagamento confermato — Stand ${viewingStand.label} è tuo!`,
            text: `Complimenti! Il pagamento per lo stand ${viewingStand.label} è stato confermato. Lo stand è ufficialmente assegnato a ${viewingStand.company}. Riceverai i dettagli per l'allestimento.`,
            html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
              <div style="background:#676767;color:#fff;padding:24px;text-align:center"><h1 style="margin:0">Summeet</h1></div>
              <div style="padding:24px;background:#f9fafb">
                <h2 style="color:#16a34a">🎉 Stand assegnato ufficialmente!</h2>
                <p>Ciao <strong>${viewingStand.company}</strong>,</p>
                <p>Il pagamento per lo stand <strong>${viewingStand.label}</strong> è stato verificato e confermato.</p>
                <p>Lo stand è ufficialmente vostro. Riceverete a breve:</p>
                <ul>
                  <li>La fattura elettronica</li>
                  <li>I dettagli per l'allestimento</li>
                  <li>Il calendario degli ingressi</li>
                </ul>
                <p>Per qualsiasi necessità rispondete a questa email o contattate il gestore dell'evento.</p>
              </div>
              <div style="padding:16px;text-align:center;font-size:12px;color:#6b7280">Per assistenza: ${replyTo}</div>
            </div>`
          }
        });
      }

      // 4. Log fatturazione (placeholder SDI)
      console.log(`[SDI TRIGGER] Fattura per ${viewingStand.company} - Stand ${viewingStand.label} - Importo: €${viewingStand.price}`);
      
      toast.success('Pagamento confermato! Stand assegnato definitivamente.');
      setViewingStand(null);
      
    } catch (error) {
      handleDatabaseError(error, OperationType.UPDATE, `events/${selectedEventId}/stands/${viewingStand.id}`);
    }
  };

  const handleRejectStandDocument = async (docTypeId: string) => {
    if (!selectedEventId || !viewingStand || !viewingStand.documents?.[docTypeId]) return;
    try {
      // Gestisci documents safely
      const currentDocs = viewingStand.documents || {};
      const currentDocData = currentDocs[docTypeId] || {};
      const updatedDoc = { ...currentDocData, status: 'rejected' as const };

      const newDocs = { ...currentDocs, [docTypeId]: updatedDoc };

      await api.stands.update(viewingStand.id, {
        documents: newDocs
      });
      setViewingStand(prev => prev ? {
        ...prev,
        documents: newDocs
      } : null);

      // Notifica sponsor del rifiuto documento
      if (viewingStand.sponsorId) {
        await api.notifications.create({
          userId: viewingStand.sponsorId,
          type: 'document_rejected',
          message: `Il documento ${docTypeId} per lo stand ${viewingStand.label} è stato rifiutato. Carica una versione corretta.`,
          read: false,
          relatedEventId: selectedEventId,
          relatedStandId: viewingStand.id
        });
      }

      toast.success('Documento rifiutato.');
    } catch (error) {
      console.error('Error rejecting document:', error);
      toast.error('Errore durante il rifiuto del documento.');
    }
  };

  const handleRejectStand = async (reason: string) => {
    if (!selectedEventId || !viewingStand) return;
    const rejectedStandId = viewingStand.id;
    const rejectedSponsorId = viewingStand.sponsorId;
    const rejectedSponsorEmail = viewingStand.sponsorEmail;
    const rejectedLabel = viewingStand.label;
    try {
      await updateStand(rejectedStandId, {
        status: 'available',
        company: null,
        company_logo: null,
        options: [],
        manager_requests: null,
        competitors: null,
        competitor_protection_active: false,
        sponsorId: null,
        sponsorEmail: null,
        sponsorName: null,
        documents: {}
      });

      // Remove admin notifications tied to this stand
      const standNotifs = notifications.filter(n => {
        const sid = (n as any).related_stand_id || (n as any).relatedStandId;
        return sid === rejectedStandId;
      });
      await Promise.all(standNotifs.map(n => apiDelete(`/api/notifications/${n.id}`).catch(() => null)));

      // Optimistic update: rimuovi subito notifiche e stand dalla UI
      setNotifications(prev => prev.filter(n => {
        const sid = (n as any).related_stand_id || (n as any).relatedStandId;
        return sid !== rejectedStandId;
      }));
      setAdminPendingStands(prev => prev.filter(s => s.id !== rejectedStandId));

      // Notify sponsor (sempre, anche se sponsorId è anonymous)
      if (rejectedSponsorId && rejectedSponsorId !== 'anonymous') {
        const msg = `❌ La tua richiesta per lo stand ${rejectedLabel} è stata rifiutata. Lo stand è tornato disponibile.${reason ? `\nMotivazione: ${reason}` : ''}`;
        await api.notifications.create({
          userId: rejectedSponsorId,
          type: 'reservation_rejected',
          message: msg,
          read: false,
          relatedEventId: selectedEventId,
          relatedStandId: rejectedStandId
        });
      }

      // Email allo sponsor
      if (rejectedSponsorEmail && event) {
        const replyTo = event.managerEmails ? event.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';
        const reasonHtml = reason ? `<p><strong>Motivazione:</strong> ${reason}</p>` : '';
        await apiPost('/api/mail', {
          to: rejectedSponsorEmail,
          replyTo,
          message: {
            subject: `Richiesta Stand Rifiutata — Stand ${rejectedLabel}`,
            text: `La tua richiesta per lo stand ${rejectedLabel} è stata rifiutata.${reason ? ` Motivazione: ${reason}` : ''} Per ulteriori informazioni puoi contattarci rispondendo a questa email.`,
            html: `<p>La tua richiesta per lo stand <b>${rejectedLabel}</b> è stata rifiutata.</p>${reasonHtml}<p>Per ulteriori informazioni puoi contattarci rispondendo a questa email.</p>`
          }
        });
      }

      // Rimuovi notifica "new_reservation" dal manager (mark as read)
      try {
        const managerNotifs = await apiGet('/api/notifications?userId=manager');
        const relatedNotif = managerNotifs.find((n: any) =>
          n.type === 'new_reservation' &&
          (n.related_stand_id || n.relatedStandId) === rejectedStandId
        );
        if (relatedNotif) {
          await apiPatch(`/api/notifications/${relatedNotif.id}`, { read: true });
        }
      } catch {}

      // Forza refresh polling admin per aggiornare la dashboard immediatamente
      try { refreshAdminPolling?.(); } catch {}
      setViewingStand(null);
      toast.success(`❌ La tua richiesta per lo stand ${rejectedLabel} è stata rifiutata. Lo stand è tornato disponibile.`);
    } catch (error) {
      console.error('Error rejecting stand:', error);
      toast.error('Errore durante il rifiuto della prenotazione.');
    }
  };

  const generateParticipationApp = () => {
    if (!viewingStand || !event) return;
    const optionsList = (Array.isArray(viewingStand.options) ? viewingStand.options : []).join(', ') || 'Nessuna';
    const content = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8"><title>Domanda di Partecipazione</title>
<style>body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;padding:20px}h1{color:#676767;border-bottom:2px solid #676767;padding-bottom:10px}table{width:100%;border-collapse:collapse;margin:20px 0}td{padding:8px 12px;border:1px solid #ddd}td:first-child{font-weight:bold;width:40%;background:#f8f9fa}.sig{margin-top:60px;border-top:1px solid #333;padding-top:10px}@media print{button{display:none}}</style>
</head><body>
<h1>Domanda di Partecipazione</h1>
<p><strong>Evento:</strong> ${event.name}</p><p><strong>Data:</strong> ${event.date}</p><p><strong>Luogo:</strong> ${event.location}</p>
<table>
<tr><td>Ragione Sociale</td><td>${viewingStand.company || ''}</td></tr>
<tr><td>Stand Richiesto</td><td>${viewingStand.label}</td></tr>
<tr><td>Prezzo Base Stand</td><td>${(viewingStand.price || 0).toLocaleString('it-IT')} €</td></tr>
<tr><td>Template Stand</td><td>${viewingStand.stand_template || 'N/D'}</td></tr>
<tr><td>Opzioni Aggiuntive</td><td>${optionsList}</td></tr>
<tr><td>Richieste Speciali</td><td>${viewingStand.manager_requests || 'Nessuna'}</td></tr>
${viewingStand.contractorName ? `<tr><td>Appaltatore Tecnico</td><td>${viewingStand.contractorName}</td></tr>` : ''}
</table>
<p style="margin-top:30px">Il/La sottoscritto/a, in qualità di legale rappresentante dell'azienda sopra indicata, chiede di partecipare all'evento nella veste di espositore, dichiarando di accettare integralmente il Regolamento dell'Evento e le condizioni contrattuali.</p>
<div class="sig"><p>Luogo e Data: ___________________________</p><p style="margin-top:30px">Firma e Timbro del Legale Rappresentante: ___________________________</p></div>
<br><button onclick="window.print()" style="padding:10px 20px;background:#676767;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px">Stampa / Salva come PDF</button>
</body></html>`;
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const generateStandSummary = () => {
    if (!viewingStand || !event) return;
    const optionsList = (Array.isArray(viewingStand.options) ? viewingStand.options : []).join('</li><li>') || 'Nessuna';
    const content = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8"><title>Riepilogo Assegnazione Stand</title>
<style>body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;padding:20px}h1{color:#676767;border-bottom:2px solid #676767;padding-bottom:10px}.badge{display:inline-block;background:#d1fae5;color:#065f46;padding:6px 16px;border-radius:20px;font-weight:bold;font-size:14px}table{width:100%;border-collapse:collapse;margin:20px 0}td{padding:10px 14px;border:1px solid #ddd}td:first-child{font-weight:bold;width:40%;background:#f8f9fa}@media print{button{display:none}}</style>
</head><body>
<h1>Riepilogo Assegnazione Stand</h1>
<span class="badge">✅ ASSEGNAZIONE CONFERMATA</span>
<br><br>
<p><strong>Evento:</strong> ${event.name}</p><p><strong>Data Evento:</strong> ${event.date}</p><p><strong>Luogo:</strong> ${event.location}</p>
<table>
<tr><td>Azienda Espositrice</td><td><strong>${viewingStand.company || ''}</strong></td></tr>
<tr><td>Stand Assegnato</td><td>${viewingStand.label}</td></tr>
<tr><td>Prezzo Finale Approvato</td><td><strong>${newPrice.toLocaleString('it-IT')} €</strong></td></tr>
<tr><td>Opzioni Incluse</td><td><ul style="margin:0;padding-left:16px"><li>${optionsList}</li></ul></td></tr>
${viewingStand.competitor_protection_active ? '<tr><td>Protezione Competitor</td><td>✅ Attiva</td></tr>' : ''}
${viewingStand.contractorName ? `<tr><td>Appaltatore Tecnico</td><td>${viewingStand.contractorName}</td></tr>` : ''}
<tr><td>Data Approvazione</td><td>${new Date().toLocaleDateString('it-IT')}</td></tr>
</table>
<p style="background:#eff6ff;border-left:4px solid #676767;padding:12px 16px;border-radius:4px">L'assegnazione dello stand è ufficialmente confermata. Vi preghiamo di presentarvi all'ingresso dell'area espositiva con copia di questo documento il giorno dell'allestimento.</p>
<br><button onclick="window.print()" style="padding:10px 20px;background:#676767;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px">Stampa / Salva come PDF</button>
</body></html>`;
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <LayoutGrid className="w-5 h-5 text-[#676767] mr-2" />
          <h2 className="text-xl font-bold text-[#676767]">Mappa degli Stand</h2>
          {effectiveIsAdmin && (
            <span className="ml-3 px-2 py-0.5 bg-orange-50 text-[#e67e22] text-[10px] font-black uppercase tracking-widest rounded border border-orange-100">
              Admin Mode
            </span>
          )}
          <span className="ml-3 px-2 py-0.5 bg-blue-50 text-[#676767] text-xs font-bold rounded-full border border-blue-100">
            {stands.length} Totali
          </span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider transition-colors ${!viewAsSponsor ? 'text-[#e67e22]' : 'text-gray-400'}`}>Admin</span>
              <button
                onClick={() => setViewAsSponsor(!viewAsSponsor)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  viewAsSponsor ? 'bg-[#676767]' : 'bg-[#e67e22]'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                    viewAsSponsor ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-[10px] font-bold uppercase tracking-wider transition-colors ${viewAsSponsor ? 'text-[#676767]' : 'text-gray-400'}`}>Sponsor</span>
            </div>
            {effectiveIsAdmin && stands.some(s => s.status === 'pending') && (
              <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 animate-pulse" title="Richieste in Revisione">
                <AlertCircle className="w-3 h-3" />
                {stands.filter(s => s.status === 'pending').length}
              </span>
            )}
          </div>
        )}
      </div>
      <p className="text-gray-500 text-sm mb-6">
        {effectiveIsAdmin ? "Modifica la planimetria e la disposizione degli stand." : "Clicca su uno stand disponibile per prenotarlo."}
      </p>
      
      {effectiveIsAdmin && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#676767] mb-1">Planimetria Evento (Sfondo)</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex gap-2">
                {bgUrl.startsWith('data:image') ? (
                  <div className="flex-1 px-3 py-2 border border-green-300 bg-green-50 text-green-700 rounded-md shadow-sm sm:text-sm flex items-center justify-between overflow-hidden relative">
                    <span className="truncate font-medium">✅ Immagine caricata</span>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setShowDeleteBgConfirm(true)} 
                        className="text-red-500 hover:text-red-700 p-1 hover:bg-red-100 rounded-full transition-colors" 
                        title="Rimuovi immagine"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      {showDeleteBgConfirm && (
                        <div className="absolute top-full mt-1 right-0 w-48 bg-white border border-red-200 rounded-lg shadow-xl p-3 z-50 animate-in fade-in slide-in-from-top-1">
                          <p className="text-xs text-gray-700 mb-2">Eliminare l'immagine?</p>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => {
                                setBgUrl('');
                                setShowDeleteBgConfirm(false);
                              }}
                              className="flex-1 bg-red-600 text-white text-[10px] py-1 rounded hover:bg-red-700"
                            >
                              Sì
                            </button>
                            <button 
                              onClick={() => setShowDeleteBgConfirm(false)}
                              className="flex-1 bg-gray-100 text-gray-700 text-[10px] py-1 rounded hover:bg-gray-200"
                            >
                              No
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex gap-2">
                    <input 
                      type="text" 
                      value={bgUrl}
                      onChange={(e) => setBgUrl(e.target.value)}
                      placeholder="Inserisci URL piantina (es: https://...)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#676767] focus:border-[#676767] sm:text-sm"
                    />
                    {bgUrl && !bgUrl.startsWith('data:image') && (
                      <div className="relative">
                        <button 
                          onClick={() => setShowDeleteBgConfirm(true)}
                          className="px-3 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors h-full"
                          title="Cancella URL"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        
                        {showDeleteBgConfirm && (
                          <div className="absolute top-full mt-1 right-0 w-48 bg-white border border-red-200 rounded-lg shadow-xl p-3 z-50 animate-in fade-in slide-in-from-top-1">
                            <p className="text-xs text-gray-700 mb-2">Cancellare l'URL?</p>
                            <div className="flex gap-2">
                              <button 
                                onClick={() => {
                                  setBgUrl('');
                                  setShowDeleteBgConfirm(false);
                                }}
                                className="flex-1 bg-red-600 text-white text-[10px] py-1 rounded hover:bg-red-700"
                              >
                                Sì
                              </button>
                              <button 
                                onClick={() => setShowDeleteBgConfirm(false)}
                                className="flex-1 bg-gray-100 text-gray-700 text-[10px] py-1 rounded hover:bg-gray-200"
                              >
                                No
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <button
                      type="button"
                      disabled={isUploading}
                      className="px-4 py-2 bg-[#676767] text-white border border-transparent rounded-md shadow-sm text-sm font-medium hover:bg-[#1d2127] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#676767] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap transition-colors"
                    >
                      {isUploading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Elaborazione...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          {bgUrl ? 'Aggiorna Piantina' : 'Carica Piantina'}
                        </>
                      )}
                    </button>
                  </div>
                  
                  {bgUrl && (
                    <div className="relative">
                      <button
                        onClick={() => setShowDeleteBgConfirm(true)}
                        className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-md shadow-sm text-sm font-medium hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-2 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Cancella</span>
                      </button>
                      
                      {showDeleteBgConfirm && (
                        <div className="absolute top-full mt-2 left-0 w-48 bg-white border border-red-200 rounded-lg shadow-xl p-3 z-50">
                          <p className="text-xs text-gray-700 mb-2">Eliminare l'immagine?</p>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => {
                                setBgUrl('');
                                setShowDeleteBgConfirm(false);
                              }}
                              className="flex-1 bg-red-600 text-white text-[10px] py-1 rounded"
                            >
                              Sì
                            </button>
                            <button 
                              onClick={() => setShowDeleteBgConfirm(false)}
                              className="flex-1 bg-gray-100 text-gray-700 text-[10px] py-1 rounded"
                            >
                              No
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {effectiveIsAdmin && (stands.length > 0 || bgUrl) && (
                    <div className="relative">
                      <button
                        onClick={() => setShowClearAllConfirm(true)}
                        className="px-4 py-2 bg-orange-50 text-orange-600 border border-orange-200 rounded-md shadow-sm text-sm font-medium hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 flex items-center gap-2 transition-colors"
                        title="Elimina tutti gli stand e la piantina per ricominciare da zero"
                      >
                        <X className="w-4 h-4" />
                        <span className="hidden sm:inline">Svuota Mappa</span>
                      </button>
                      
                      {showClearAllConfirm && (
                        <div className="absolute top-full mt-2 right-0 w-64 bg-white border border-orange-200 rounded-lg shadow-xl p-4 z-50 animate-in fade-in slide-in-from-top-2">
                          <p className="text-sm text-gray-700 mb-3 font-bold">Sei sicuro?</p>
                          <p className="text-xs text-gray-600 mb-3">Eliminerai TUTTI gli stand e la piantina caricata. Questa operazione non è annullabile.</p>
                          <div className="flex gap-2">
                            <button 
                              onClick={clearAllStands}
                              className="flex-1 bg-orange-600 text-white text-xs py-2 rounded hover:bg-orange-700"
                            >
                              Sì, Svuota Tutto
                            </button>
                            <button 
                              onClick={() => setShowClearAllConfirm(false)}
                              className="flex-1 bg-gray-100 text-gray-700 text-xs py-2 rounded hover:bg-gray-200"
                            >
                              Annulla
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-gray-500 italic">
              Puoi inserire un URL o caricare un file per sfondo mappa.
            </p>
          </div>
          <div className="flex gap-2">
            {!isDrawing ? (
              <>
                <button
                  onClick={() => {
                    if (isDrawing && currentPolygon.length >= 3) {
                      const confirmSave = confirm(`Hai un poligono incompleto (${currentPolygon.length} punti). Vuoi salvarlo prima di iniziarne uno nuovo?`);
                      if (confirmSave) {
                        finishDrawing();
                      } else {
                        setCurrentPolygon([]);
                        setIsDrawing(false);
                      }
                    }
                    setIsDrawing(true);
                    setEditingStandId(null);
                    setCurrentPolygon([]);
                  }}
                  className="bg-[#e67e22] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#d35400] transition-colors flex items-center gap-2"
                >
                  <MousePointer2 className="w-4 h-4" />
                  Disegna Stand sulla Mappa
                </button>
                <button 
                  onClick={() => setShowAddBookedModal(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  + Aggiungi Stand Prenotato
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3 w-full bg-white p-3 rounded-md border border-orange-200 shadow-sm">
                <div className="flex-1 flex items-center gap-2 text-sm text-orange-700 font-medium">
                  <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                  Clicca sulla mappa per tracciare i vertici dello stand ({currentPolygon.length} punti)
                </div>
                <button 
                  onClick={cancelDrawing}
                  className="text-gray-500 hover:text-gray-700 px-3 py-1.5 text-sm font-medium"
                >
                  Annulla
                </button>
                <button 
                  onClick={finishDrawing}
                  disabled={currentPolygon.length < 3}
                  className="bg-green-600 text-white px-4 py-1.5 rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Salva Poligono
                </button>
              </div>
            )}
          </div>

          {/* Admin: Manage Config Templates */}
          {effectiveIsAdmin && (
            <div className="mt-6 p-4 bg-indigo-50/50 rounded-lg border border-indigo-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2">
                  <Save className="w-4 h-4 text-indigo-600" />
                  Template Configurazioni (Opzioni e Prezzi)
                </h4>
                <div className="flex items-center gap-2">
                  <select
                    className="text-sm border border-indigo-200 rounded-md px-3 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 bg-white min-w-[200px]"
                    onChange={(e) => {
                      if (e.target.value) {
                        loadConfigTemplate(e.target.value);
                        e.target.value = ''; // Reset select after loading
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>Carica da template...</option>
                    {configTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowSaveTemplateModal(true)}
                    className="bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                  >
                    <Save className="w-4 h-4" />
                    Salva Corrente
                  </button>
                </div>
              </div>
              {configTemplates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {configTemplates.map(t => (
                    <div key={t.id} className="flex items-center gap-2 bg-white border border-indigo-100 px-3 py-1.5 rounded-full text-xs text-indigo-700 group shadow-sm">
                      <span className="font-medium">{t.name}</span>
                      <button 
                        onClick={() => deleteConfigTemplate(t.id)}
                        className="text-indigo-300 hover:text-red-500 transition-colors"
                        title="Elimina template"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Admin: Manage Available Options */}
          {effectiveIsAdmin && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Gestione Opzioni Stand (per Sponsor)
              </h4>
              <div className="flex gap-2 mb-3">
                <input 
                  type="text" 
                  value={newOptionText}
                  onChange={(e) => setNewOptionText(e.target.value)}
                  placeholder="Esempio: Allacciamento elettrico 6kW"
                  className="flex-1 text-sm border border-slate-300 rounded-md px-3 py-2 outline-none focus:ring-1 focus:ring-[#676767]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newOptionText.trim()) {
                      setAvailableOptions([...availableOptions, { name: newOptionText.trim(), price: newOptionPrice }]);
                      setNewOptionText('');
                      setNewOptionPrice(0);
                    }
                  }}
                />
                <div className="relative w-24">
                  <input 
                    type="number" 
                    value={newOptionPrice}
                    onChange={(e) => setNewOptionPrice(Number(e.target.value))}
                    className="w-full text-sm border border-slate-300 rounded-md pl-6 pr-2 py-2 outline-none focus:ring-1 focus:ring-[#676767]"
                  />
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">€</span>
                </div>
                <button 
                  onClick={() => {
                    if (newOptionText.trim()) {
                      setAvailableOptions([...availableOptions, { name: newOptionText.trim(), price: newOptionPrice }]);
                      setNewOptionText('');
                      setNewOptionPrice(0);
                    }
                  }}
                  className="bg-[#676767] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#1d2127]"
                >
                  Aggiungi
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {availableOptions.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-full text-xs text-slate-600 group">
                    <span className="font-medium">{option.name}</span>
                    <span className="text-[#676767] font-bold">+{option.price}€</span>
                    <button 
                      onClick={() => setAvailableOptions(availableOptions.filter((_, i) => i !== idx))}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin: Manage Allowed Stand Templates */}
          {effectiveIsAdmin && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <LayoutGrid className="w-4 h-4" />
                  Stand Disponibili (per Sponsor)
                </h4>
                <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-md p-1 shadow-sm w-fit">
                  <span className="text-xs text-gray-500 font-medium px-2">Disegna in mappa:</span>
                  <div className="flex items-center gap-1 px-1">
                    <input type="number" min="1" value={manualWidth} onChange={e=>setManualWidth(Number(e.target.value))} className="w-12 px-1 py-1 border rounded text-xs outline-none focus:ring-1 focus:ring-[#676767] text-center" title="Larghezza (m)" />
                    <span className="text-gray-400 text-xs font-bold">x</span>
                    <input type="number" min="1" value={manualLength} onChange={e=>setManualLength(Number(e.target.value))} className="w-12 px-1 py-1 border rounded text-xs outline-none focus:ring-1 focus:ring-[#676767] text-center" title="Lunghezza (m)" />
                    <span className="text-gray-400 text-xs font-bold mr-1">m</span>
                  </div>
                  <button 
                    onClick={handleAddStand}
                    className="bg-[#676767] text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-[#1d2127] transition-colors whitespace-nowrap"
                  >
                    + Aggiungi
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {STANDS_TEMPLATES.map((template) => {
                  const isAllowed = allowedStandTemplates.includes(template.id);
                  return (
                    <div key={template.id} className="flex flex-col gap-2 p-3 rounded-lg border bg-white border-slate-200">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => {
                            if (isAllowed) {
                              setAllowedStandTemplates(allowedStandTemplates.filter(id => id !== template.id));
                            } else {
                              setAllowedStandTemplates([...allowedStandTemplates, template.id]);
                            }
                          }}
                          className={`flex items-center gap-2 transition-all ${
                            isAllowed ? 'text-[#676767]' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${isAllowed ? 'bg-[#676767] border-[#676767]' : 'border-slate-300'}`}>
                            {isAllowed && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-xs font-bold leading-tight">{template.name.split(' – ')[0]}</span>
                        </button>
                        
                        <div className="relative w-24">
                          <input 
                            type="number" 
                            value={templatePrices[template.id] || 0}
                            onChange={(e) => setTemplatePrices({ ...templatePrices, [template.id]: Number(e.target.value) })}
                            className="w-full text-xs border border-slate-300 rounded-md pl-6 pr-2 py-1.5 outline-none focus:ring-1 focus:ring-[#676767]"
                            placeholder={template.id === 'custom' ? "Prezzo/mq" : "Costo"}
                          />
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]">€</span>
                        </div>
                      </div>
                      {template.id === 'custom' && (
                        <div className="text-[10px] text-slate-500 text-right mt-1">
                          Prezzo calcolato per metro quadro (mq)
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin: Manage Event Documents */}
          {effectiveIsAdmin && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documentazione Utile (per Sponsor)
              </h4>
              <p className="text-xs text-gray-500 mb-4">Carica i documenti in bianco che gli sponsor potranno scaricare (Planimetria, Regolamenti, Moduli vuoti).</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EVENT_DOCUMENT_TYPES.map(docType => {
                  const eventDoc = event.documents?.[docType.id];
                  return (
                    <div key={docType.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">{docType.label}</span>
                        {eventDoc ? (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Caricato
                            </span>
                            <a href={eventDoc.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline">Vedi</a>
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400 mt-1">Non caricato</span>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !selectedEventId) return;
                            setIsUploading(true);
                            try {
                              // Converti in base64 inline (production-ready)
                              const reader = await new Promise<string>((resolve, reject) => {
                                const readerObj = new FileReader();
                                readerObj.onload = (e) => resolve(e.target?.result as string);
                                readerObj.onerror = reject;
                                readerObj.readAsDataURL(file);
                              });

                              const compressedBase64 = await compressImage(reader, 1920, 1080, 0.85);
                              const url = compressedBase64;

                              await apiPatch(`/api/events/${selectedEventId}`, {
                                [`documents.${docType.id}`]: {
                                  url,
                                  name: file.name,
                                  uploadedAt: new Date().toISOString()
                                }
                              });
                              toast.success(`Documento ${docType.label} caricato con successo.`);
                            } catch (error) {
                              console.error("Error uploading document:", error);
                              toast.error("Errore durante il caricamento del documento.");
                            } finally {
                              setIsUploading(false);
                            }
                          }}
                          disabled={isUploading}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <button
                          type="button"
                          disabled={isUploading}
                          className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 rounded-md shadow-sm text-xs font-medium hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#676767] disabled:opacity-50 flex items-center gap-2 transition-colors"
                        >
                          <Upload className="w-3 h-3" />
                          {eventDoc ? 'Aggiorna' : 'Carica'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sponsor Menu: My Reservations */}
          {!effectiveIsAdmin && (
            <div className="mt-6 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
              <h4 className="text-sm font-bold text-[#676767] mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  <span>Carrello</span>
                </div>
                {stands.filter(s => s.status !== 'available').length > 0 && (
                  <span className="bg-[#676767] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {stands.filter(s => s.status !== 'available').length}
                  </span>
                )}
              </h4>
              <div className="space-y-2">
                {stands.filter(s => s.status !== 'available').length === 0 ? (
                  <p className="text-xs text-gray-500 italic py-2">Nessuna prenotazione effettuata.</p>
                ) : (
                  stands.filter(s => s.status !== 'available').map(s => (
                    <div 
                      key={s.id} 
                      onClick={() => setViewingStand(s)}
                      className="flex items-center justify-between p-3 bg-white border border-blue-100 rounded-xl hover:border-[#676767] transition-all cursor-pointer group shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${s.status === 'occupied' ? 'bg-green-500' : 'bg-amber-500'} animate-pulse`}></div>
                        <div>
                          <p className="text-xs font-bold text-gray-900">Stand {s.label}</p>
                          <p className="text-[10px] text-gray-500 truncate max-w-[120px]">{s.company}</p>
                        </div>
                      </div>
                      <div className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                        s.status === 'occupied' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {s.status === 'occupied' ? 'Confermato' : 'In Revisione'}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <p className="mt-4 text-[10px] text-gray-400 leading-relaxed">
                Clicca su uno stand per visualizzare i dettagli o sullo stand sulla mappa per prenotare.
              </p>
            </div>
          )}

          {/* Sponsor: Useful Documentation */}
          {!effectiveIsAdmin && event.documents && Object.keys(event.documents).length > 0 && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documentazione Utile
              </h4>
              <p className="text-xs text-gray-500 mb-4">Scarica i documenti informativi e i moduli vuoti forniti dall'organizzatore.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EVENT_DOCUMENT_TYPES.map(docType => {
                  const eventDoc = event.documents?.[docType.id];
                  if (!eventDoc) return null;
                  return (
                    <div key={docType.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                      <span className="text-xs font-bold text-slate-700">{docType.label}</span>
                      <a 
                        href={eventDoc.url} 
                        target="_blank" 
                        rel="noreferrer"
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Scarica Documento"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-200 flex justify-end">
            <button 
              onClick={async () => {
                if (!selectedEventId) return;
                try {
                  await apiPatch(`/api/events/${selectedEventId}`, {
                    availableOptions,
                    allowedStandTemplates,
                    bgUrl
                  });
                  
                  // Feedback all'utente
                  const btn = document.getElementById('save-config-btn');
                  if (btn) {
                    const originalText = btn.innerHTML;
                    btn.innerHTML = '✅ Configurazione Salvata!';
                    btn.classList.remove('bg-[#676767]');
                    btn.classList.add('bg-green-600');
                    
                    setTimeout(() => {
                      btn.innerHTML = originalText;
                      btn.classList.remove('bg-green-600');
                      btn.classList.add('bg-[#676767]');
                    }, 2000);
                  }
                } catch (error) {
                  console.error('Error saving event config:', error);
                }
              }}
              id="save-config-btn"
              className="bg-[#676767] text-white px-8 py-3 rounded-lg font-bold shadow-lg hover:bg-[#1d2127] transition-all transform hover:scale-105 flex items-center gap-2"
            >
              Salva Configurazione Finale
            </button>
          </div>
        </div>
      )}

      {effectiveIsAdmin && editingStand && (
        <div className="mb-6 p-4 bg-white border border-gray-200 shadow-sm rounded-lg grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="col-span-2 sm:col-span-4 flex justify-between items-center border-b pb-2 mb-2">
            <h3 className="font-bold text-gray-700">Modifica Stand: {editingStand.label}</h3>
            <button onClick={() => setEditingStandId(null)} className="text-gray-400 hover:text-gray-600">Chiudi</button>
          </div>
          
          <div>
            <label className="block text-xs text-gray-500 mb-1">Etichetta</label>
            <input type="text" value={editingStand.label} onChange={e => updateStand(editingStand.id, { label: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Prezzo (€)</label>
            <input 
              type="number" 
              value={editingStand.price} 
              onChange={e => {
                const newPrice = Number(e.target.value);
                updateStand(editingStand.id, { price: newPrice });
                setLastConfig(prev => ({ ...prev, price: newPrice }));
              }} 
              className="w-full px-2 py-1 border rounded text-sm" 
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Stato</label>
            <select 
              value={editingStand.status} 
              onChange={e => {
                const newStatus = e.target.value as 'available'|'occupied'|'pending';
                updateStand(editingStand.id, { status: newStatus });
                setLastConfig(prev => ({ ...prev, status: newStatus }));
              }} 
              className="w-full px-2 py-1 border rounded text-sm"
            >
              <option value="available">Disponibile</option>
              <option value="pending">In Revisione</option>
              <option value="occupied">Occupato</option>
            </select>
          </div>
          {(editingStand.status === 'occupied' || editingStand.status === 'pending') && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Azienda</label>
              <input type="text" value={editingStand.company || ''} onChange={e => updateStand(editingStand.id, { company: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" />
            </div>
          )}
          
          <div>
            <label className="block text-xs text-gray-500 mb-1">Larghezza (m)</label>
            <input 
              type="number" 
              value={editingStand.custom_dimensions?.width || ''} 
              onChange={e => {
                const val = e.target.value ? Number(e.target.value) : 0;
                const currentLength = editingStand.custom_dimensions?.length || 0;
                if (val === 0 && currentLength === 0) {
                  updateStand(editingStand.id, { custom_dimensions: null });
                } else {
                  updateStand(editingStand.id, { custom_dimensions: { width: val, length: currentLength } });
                }
              }} 
              className="w-full px-2 py-1 border rounded text-sm" 
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Lunghezza (m)</label>
            <input 
              type="number" 
              value={editingStand.custom_dimensions?.length || ''} 
              onChange={e => {
                const val = e.target.value ? Number(e.target.value) : 0;
                const currentWidth = editingStand.custom_dimensions?.width || 0;
                if (val === 0 && currentWidth === 0) {
                  updateStand(editingStand.id, { custom_dimensions: null });
                } else {
                  updateStand(editingStand.id, { custom_dimensions: { width: currentWidth, length: val } });
                }
              }} 
              className="w-full px-2 py-1 border rounded text-sm" 
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Punti SVG (x,y x,y ...)</label>
            <input type="text" value={editingStand.svg?.points ?? ''} onChange={e => updateStand(editingStand.id, { svg: { ...(editingStand.svg ?? { text: [0, 0] }), points: e.target.value } })} className="w-full px-2 py-1 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Testo X</label>
            <input type="number" value={editingStand.svg?.text?.[0] ?? 0} onChange={e => updateStand(editingStand.id, { svg: { ...(editingStand.svg ?? { points: '' }), text: [Number(e.target.value), editingStand.svg?.text?.[1] ?? 0] } })} className="w-full px-2 py-1 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Testo Y</label>
            <input type="number" value={editingStand.svg?.text?.[1] ?? 0} onChange={e => updateStand(editingStand.id, { svg: { ...(editingStand.svg ?? { points: '' }), text: [editingStand.svg?.text?.[0] ?? 0, Number(e.target.value)] } })} className="w-full px-2 py-1 border rounded text-sm" />
          </div>

          <div className="col-span-2 sm:col-span-4 mt-2 flex gap-4">
            <button onClick={() => duplicateStand(editingStand)} className="text-[#676767] text-sm hover:underline font-medium">Duplica Stand</button>
            <button onClick={() => deleteStand(editingStand.id)} className="text-red-600 text-sm hover:underline">Elimina Stand</button>
          </div>
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* List of Stands (25% width on large screens) */}
        <div className="w-full lg:w-1/4 overflow-hidden">
          <h3 className="text-lg font-bold text-[#676767] mb-4">Lista degli Stand</h3>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm text-left min-w-[300px]">
              <thead className="bg-gray-50 text-gray-700 uppercase">
                <tr>
                  <th className="px-4 py-3 border-b">Nome</th>
                  <th className="px-4 py-3 border-b">Stato</th>
                  {effectiveIsAdmin && <th className="px-4 py-3 border-b text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stands.map(stand => (
                  <tr 
                    key={stand.id} 
                    className={`hover:bg-gray-50 transition-colors ${stand.status === 'available' ? 'bg-green-50/30' : ''}`}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{stand.label}</td>
                    <td className="px-4 py-3">
                      {stand.status === 'available' ? (
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">Disponibile</span>
                      ) : stand.status === 'pending' ? (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded">In Revisione</span>
                      ) : (
                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded">Occupato</span>
                      )}
                    </td>
                    {effectiveIsAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={async () => {
                              // Se stiamo disegnando un poligono completo, salvalo automaticamente
                              if (isDrawing && currentPolygon.length >= 3) {
                                const confirmSave = confirm(`Poligono completo (${currentPolygon.length} punti). Salvarlo prima di ridisegnare?`);
                                if (confirmSave) {
                                  await finishDrawing();
                                  // Small delay before starting new drawing
                                  setTimeout(() => {
                                    setIsDrawing(true);
                                    setRedrawingStandId(stand.id);
                                    setEditingStandId(null);
                                    setCurrentPolygon([]);
                                    toast.info(`Clicca sulla mappa per ridisegnare lo ${stand.label}.`);
                                  }, 500);
                                  return;
                                } else {
                                  setCurrentPolygon([]);
                                  setIsDrawing(false);
                                }
                              }

                              setIsDrawing(true);
                              setRedrawingStandId(stand.id);
                              setEditingStandId(null);
                              setCurrentPolygon([]);
                              toast.info(`Clicca sulla mappa per ridisegnare lo ${stand.label}.`);
                            }}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                            title="Disegna Forma"
                          >
                            <PenTool className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => setEditingStandId(stand.id)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Modifica Proprietà"
                          >
                            <MousePointer2 className="w-4 h-4" />
                          </button>
                          <div className="relative">
                            <button 
                              onClick={() => setStandToDelete(stand.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            {standToDelete === stand.id && (
                              <div className="absolute bottom-full right-0 mb-2 w-48 bg-white border border-red-200 rounded-lg shadow-xl p-3 z-50 text-left">
                                <p className="text-xs text-gray-700 mb-2">Eliminare lo {stand.label}?</p>
                                <div className="flex gap-2">
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteStand(stand.id);
                                      setStandToDelete(null);
                                    }}
                                    className="flex-1 bg-red-600 text-white text-[10px] py-1 rounded hover:bg-red-700 transition-colors"
                                  >
                                    Elimina
                                  </button>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setStandToDelete(null);
                                    }}
                                    className="flex-1 bg-gray-100 text-gray-700 text-[10px] py-1 rounded hover:bg-gray-200 transition-colors"
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Map Container (75% width on large screens) */}
        <div className="w-full lg:w-3/4">
          <div className="bg-[#f8f9fa] rounded-xl p-6 border border-gray-100">
            <div className="flex justify-end gap-4 mb-4 text-sm font-medium">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#676767]"></div>
                <span className="text-gray-700">Disponibile</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#f1c40f]"></div>
                <span className="text-gray-700">In Revisione</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#e11d48]"></div>
                <span className="text-gray-700">Occupato</span>
              </div>
            </div>

            <TransformWrapper
              initialScale={typeof window !== 'undefined' && window.innerWidth < 768 ? 1.5 : 1}
              minScale={typeof window !== 'undefined' && window.innerWidth < 768 ? 1.5 : 1}
              maxScale={8}
              centerOnInit={true}
              panning={{ disabled: isDrawing || draggingVertex !== null || draggingStand !== null }}
              doubleClick={{ disabled: true }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <div className="relative w-full bg-slate-100 border border-gray-200 rounded-xl overflow-hidden">
                  <div className="absolute top-4 right-4 z-20 flex flex-col gap-2 bg-white/90 backdrop-blur-sm p-1.5 rounded-md shadow-sm border border-gray-200">
                    <button onClick={() => zoomIn()} className="p-1.5 hover:bg-gray-100 rounded text-gray-700 transition-colors" title="Zoom In">
                      <ZoomIn className="w-5 h-5" />
                    </button>
                    <button onClick={() => zoomOut()} className="p-1.5 hover:bg-gray-100 rounded text-gray-700 transition-colors" title="Zoom Out">
                      <ZoomOut className="w-5 h-5" />
                    </button>
                    <button onClick={() => resetTransform()} className="p-1.5 hover:bg-gray-100 rounded text-gray-700 transition-colors" title="Reset Zoom">
                      <Maximize className="w-5 h-5" />
                    </button>
                  </div>
                  <TransformComponent wrapperClass="w-full h-[80vh] md:h-[85vh]" contentClass="w-full flex items-center justify-center">
                    <div className={`relative w-full max-w-container mx-auto shadow-sm bg-white ${bgUrl ? '' : 'min-h-[400px]'}`}>
              {/* Background Map Image (Admin configurable) */}
              {bgUrl ? (
                <img 
                  src={bgUrl} 
                  alt="Planimetria" 
                  className="w-full h-auto block pointer-events-none" 
                />
              ) : (
                <div className="absolute inset-0 w-full h-full flex items-center justify-center border-2 border-dashed border-slate-200 m-4 rounded-xl" style={{ width: 'calc(100% - 32px)', height: 'calc(100% - 32px)' }}>
                  <span className="text-slate-400 font-medium text-lg text-center px-4">
                    {effectiveIsAdmin ? "Inserisci l'URL dell'immagine qui sopra per caricare la planimetria" : "Planimetria in attesa di caricamento"}
                  </span>
                </div>
              )}
              
              {/* SVG Stands Overlay */}
              <svg 
                viewBox={`0 0 ${bgDimensions.width} ${bgDimensions.height}`}
                preserveAspectRatio="xMidYMid meet"
                className={`absolute inset-0 w-full h-full z-10 ${isDrawing ? 'cursor-crosshair' : ''}`}
                onClick={handleSvgClick}
                onMouseMove={handleSvgMouseMove}
                onMouseUp={handleSvgMouseUp}
                onMouseLeave={handleSvgMouseUp}
              >
                {/* Current Polygon being drawn */}
                {isDrawing && currentPolygon.length > 0 && (
                  <g>
                    <polygon 
                      points={currentPolygon.map(p => `${p[0]},${p[1]}`).join(' ')} 
                      className="fill-[#e67e22]/30 stroke-[#e67e22] stroke-[1px] stroke-dashed"
                    />
                    {currentPolygon.map((p, i) => (
                      <circle key={i} cx={p[0]} cy={p[1]} r="1.5" className="fill-[#e67e22]" />
                    ))}
                  </g>
                )}

                {stands.map(stand => (
                  <g 
                    key={stand.id} 
                    onClick={() => handleStandClick(stand)}
                    className={`transition-all ${
                      stand.status === 'available' 
                        ? 'cursor-pointer hover:opacity-80' 
                        : (!effectiveIsAdmin ? 'cursor-pointer hover:opacity-90' : 'cursor-pointer')
                    }`}
                  >
                    <polygon
                      points={stand.svg?.points || ''}
                      onClick={() => {
                        // Suppress the click that follows a drag operation.
                        if (justDraggedRef.current) {
                          justDraggedRef.current = false;
                          return;
                        }
                        if (effectiveIsAdmin) {
                          setSelectedStandId(selectedStandId === stand.id ? null : stand.id);
                        }
                      }}
                      onMouseDown={(e) => handlePolygonMouseDown(e, stand)}
                      strokeDasharray={
                        effectiveIsAdmin && selectedStandId === stand.id && editingStandId !== stand.id
                          ? '5,5'
                          : undefined
                      }
                      className={`${
                        stand.status === 'available'
                          ? 'fill-[#676767]/40 stroke-[#676767] stroke-2 hover:fill-[#676767]/60'
                          : stand.status === 'pending'
                            ? 'fill-[#f1c40f]/50 stroke-[#f39c12] stroke-2'
                            : 'fill-[#e11d48]/40 stroke-[#e11d48] stroke-2'
                      } ${
                        effectiveIsAdmin && editingStandId === stand.id
                          ? 'stroke-[#ffa064] stroke-[4px]'
                          : effectiveIsAdmin && selectedStandId === stand.id
                            ? 'stroke-[#f59e0b] stroke-[5px]'
                            : ''
                      } ${effectiveIsAdmin ? 'cursor-move' : ''}`}
                    />
                    
                        {stand.status === 'occupied' && stand.svg ? (
                      <g>
                        {stand.company_logo ? (
                          <image
                            href={stand.company_logo}
                            x={stand.svg.text?.[0] - 20 || 0}
                            y={stand.svg.text?.[1] - 20 || 0}
                            height="40px"
                            width="40px"
                            preserveAspectRatio="xMidYMid meet"
                          />
                        ) : (
                          <text
                            x={stand.svg.text?.[0] || 0}
                            y={stand.svg.text?.[1] || 0} 
                            className="fill-[#e11d48] text-[10px] font-bold"
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            {stand.company || stand.label}
                          </text>
                        )}
                      </g>
                    ) : null}

                    {stand.svg && (
                      <g>
                        <text
                          x={stand.svg.text?.[0] || 0}
                          y={stand.svg.text?.[1] || 0}
                          className={`font-bold text-[8px] ${
                            stand.status === 'available'
                              ? 'fill-[#676767]'
                              : 'fill-[#d35400]'
                          }`}
                          textAnchor="middle"
                        >
                          {stand.label}
                        </text>

                        <text
                          x={stand.svg.text?.[0] || 0}
                          y={(stand.svg.text?.[1] || 0) + 10}
                          className={`font-bold text-[7px] ${
                            stand.status === 'available'
                              ? 'fill-[#676767]'
                              : 'fill-[#d35400]'
                          }`}
                          textAnchor="middle"
                        >
                          {stand.price.toLocaleString('it-IT')} €
                        </text>
                      </g>
                    )}

                    {/* Draggable vertices for editing */}
                    {effectiveIsAdmin && editingStandId === stand.id && !isDrawing && stand.svg?.points && stand.svg.points.length > 0 && (
                      <g>
                        {stand.svg.points.split(' ').map((p, i) => {
                          const [x, y] = p.split(',').map(Number);
                          return (
                            <circle 
                              key={i} 
                              cx={x} 
                              cy={y} 
                              r="2.5" 
                              className="fill-white stroke-[#e67e22] stroke-[1px] cursor-move hover:r-4 transition-all"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                setDraggingVertex({ standId: stand.id, index: i });
                              }}
                            />
                          );
                        })}
                      </g>
                    )}
                  </g>
                ))}
              </svg>
                    </div>
                  </TransformComponent>
                </div>
              )}
            </TransformWrapper>
          </div>
        </div>
      </div>

      {/* Reservation Modal */}
      {reservingStand && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full relative my-auto max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50 rounded-t-2xl flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-[#676767]">Prenota Stand: {reservingStand.label}</h3>
                <p className="text-xs text-gray-500">Completa i dati per riservare questo spazio</p>
              </div>
              <button 
                onClick={() => setReservingStand(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 md:p-8 overflow-y-auto flex-grow min-h-0 space-y-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wider">Nome Azienda <span className="text-red-500">*</span></label>
                  <input 
                    type="text" 
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#676767] focus:border-transparent outline-none transition-all" 
                    placeholder="La tua azienda S.p.A." 
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wider">Logo Azienda</label>
                  <div className="flex items-center gap-4 p-4 border border-gray-100 rounded-xl bg-gray-50">
                    {companyLogo ? (
                      <div className="relative w-20 h-20 border-2 border-white rounded-xl overflow-hidden shadow-sm bg-white flex items-center justify-center">
                        <img src={companyLogo} alt="Logo Preview" className="max-w-full max-h-full object-contain" referrerPolicy="no-referrer" />
                        <button 
                          onClick={() => setCompanyLogo('')}
                          className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-bl-xl hover:bg-red-600 transition-colors shadow-sm"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center bg-white text-gray-300">
                        <Upload className="w-8 h-8" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = async () => {
                              const compressed = await compressImage(reader.result as string, 400, 400, 0.8);
                              setCompanyLogo(compressed);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden" 
                        id="logo-upload"
                      />
                      <label 
                        htmlFor="logo-upload"
                        className="inline-flex items-center px-5 py-2.5 bg-[#676767] text-white text-sm font-bold rounded-xl hover:bg-[#1d2127] cursor-pointer transition-all shadow-sm"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {companyLogo ? 'Cambia Logo' : 'Scegli File'}
                      </label>
                      <p className="text-[10px] text-gray-400 mt-2 italic">
                        {companyLogo ? 'Logo caricato correttamente' : 'Carica un file immagine (PNG, JPG)'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wider">Opzioni Aggiuntive</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {availableOptions.map(option => (
                      <label key={option.name} className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${selectedOptions.includes(option.name) ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200' : 'bg-white border-gray-100 hover:border-gray-200'}`}>
                        <div className="flex items-center gap-3">
                          <input 
                            type="checkbox" 
                            checked={selectedOptions.includes(option.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedOptions([...selectedOptions, option.name]);
                              } else {
                                setSelectedOptions(selectedOptions.filter(o => o !== option.name));
                              }
                            }}
                            className="w-4 h-4 text-[#676767] rounded border-gray-300 focus:ring-[#676767]"
                          />
                          <span className="text-xs font-medium text-gray-700">{option.name}</span>
                        </div>
                        <span className="text-[10px] font-bold text-[#676767]">+{option.price}€</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Total Price Summary */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <div className="flex justify-between items-center mb-2 text-xs text-gray-500">
                    <span>Prezzo Stand</span>
                    <span>{reservingStand?.price.toLocaleString('it-IT')} €</span>
                  </div>
                  <div className="flex justify-between items-center mb-2 text-xs text-gray-500">
                    <span>Modello Stand ({selectedTemplate.name})</span>
                    <span>+ {(selectedTemplate.id === 'custom' ? (templatePrices['custom'] || 0) * (customWidth * customLength) : (templatePrices[selectedTemplate.id] || 0)).toLocaleString('it-IT')} €</span>
                  </div>
                  {selectedOptions.length > 0 && (
                    <div className="flex justify-between items-center mb-2 text-xs text-gray-500">
                      <span>Opzioni ({selectedOptions.length})</span>
                      <span>+ {selectedOptions.reduce((acc, optName) => {
                        const opt = availableOptions.find(o => o.name === optName);
                        return acc + (opt?.price || 0);
                      }, 0).toLocaleString('it-IT')} €</span>
                    </div>
                  )}
                  <div className="h-px bg-slate-200 my-2" />
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-700">Totale Preventivato</span>
                    <span className="text-lg font-black text-[#676767]">
                      {((reservingStand?.price || 0) + 
                        (selectedTemplate.id === 'custom' ? (templatePrices['custom'] || 0) * (customWidth * customLength) : (templatePrices[selectedTemplate.id] || 0)) + 
                        selectedOptions.reduce((acc, optName) => {
                          const opt = availableOptions.find(o => o.name === optName);
                          return acc + (opt?.price || 0);
                        }, 0)).toLocaleString('it-IT')} €
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wider">Richieste Speciali</label>
                  <textarea 
                    value={managerRequests}
                    onChange={(e) => setManagerRequests(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#676767] focus:border-transparent outline-none text-sm transition-all" 
                    rows={3}
                    placeholder="Inserisci qui eventuali richieste particolari per l'Event Manager..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wider">Aziende Competitor</label>
                  <textarea 
                    value={competitors}
                    onChange={(e) => setCompetitors(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#676767] focus:border-transparent outline-none text-sm transition-all" 
                    rows={2}
                    placeholder="Elenca qui le aziende competitor per evitare che prendano stand vicini..."
                  />
                </div>

                {/* Stand Preview AI Section */}
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 bg-blue-100 rounded-lg">
                      <ImageIcon className="w-4 h-4 text-[#676767]" />
                    </div>
                    <h4 className="text-sm font-bold text-gray-900">Stand Preview AI</h4>
                  </div>

                  {/* Template Selection */}
                  <div className="space-y-3">
                    {(() => {
                      const availableTpls = STANDS_TEMPLATES.filter(t => allowedStandTemplates.includes(t.id))
                        .filter(t => reservingStand?.custom_dimensions ? t.id === 'custom' : t.id !== 'custom');
                      
                      if (availableTpls.length > 1) {
                        return (
                          <>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">1. Seleziona Modello Stand</label>
                            <div className="grid grid-cols-2 gap-3">
                              {availableTpls.map((template) => (
                                <button
                                  key={template.id}
                                  onClick={() => setSelectedTemplate(template)}
                                  className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                                    selectedTemplate.id === template.id 
                                      ? 'border-[#676767] bg-blue-50 shadow-sm' 
                                      : 'border-gray-100 bg-white hover:border-gray-200'
                                  }`}
                                >
                                  <template.icon className={`w-12 h-12 mb-2 ${selectedTemplate.id === template.id ? 'text-[#676767]' : 'text-gray-400'}`} />
                                  <span className={`text-[10px] font-bold text-center leading-tight ${selectedTemplate.id === template.id ? 'text-[#676767]' : 'text-gray-500'}`}>
                                    {template.name}
                                  </span>
                                  <span className={`text-[10px] font-black mt-1 ${selectedTemplate.id === template.id ? 'text-[#676767]' : 'text-gray-400'}`}>
                                    {templatePrices[template.id]?.toLocaleString('it-IT') || '0'} €{template.id === 'custom' ? '/mq' : ''}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </>
                        );
                      } else if (availableTpls.length === 1 && !reservingStand?.custom_dimensions) {
                        const template = availableTpls[0];
                        return (
                          <>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">1. Modello Stand (Misura Fissa)</label>
                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                              <template.icon className="w-10 h-10 text-[#676767]" />
                              <div>
                                <div className="font-bold text-[#676767]">{template.name}</div>
                                <div className="text-xs text-gray-500">{templatePrices[template.id]?.toLocaleString('it-IT') || '0'} €</div>
                              </div>
                            </div>
                          </>
                        );
                      } else if (availableTpls.length === 0) {
                        return (
                          <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl text-center">
                            <p className="text-xs text-amber-700 font-medium">Nessun template disponibile per questo evento.</p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {selectedTemplate.id === 'custom' && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl space-y-3">
                        <label className="block text-xs font-bold text-[#676767] uppercase tracking-wider">Dimensioni Personalizzate</label>
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <label className="block text-xs text-slate-600 mb-1">Larghezza (m)</label>
                            <input
                              type="number"
                              min="1"
                              value={customWidth}
                              onChange={(e) => setCustomWidth(Math.max(1, Number(e.target.value)))}
                              disabled={!!reservingStand?.custom_dimensions}
                              className={`w-full px-3 py-2 border rounded-lg outline-none text-sm ${reservingStand?.custom_dimensions ? 'bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-200 focus:ring-2 focus:ring-[#676767]'}`}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-slate-600 mb-1">Lunghezza (m)</label>
                            <input
                              type="number"
                              min="1"
                              value={customLength}
                              onChange={(e) => setCustomLength(Math.max(1, Number(e.target.value)))}
                              disabled={!!reservingStand?.custom_dimensions}
                              className={`w-full px-3 py-2 border rounded-lg outline-none text-sm ${reservingStand?.custom_dimensions ? 'bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-200 focus:ring-2 focus:ring-[#676767]'}`}
                            />
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 text-right">
                          Area totale: <span className="font-bold text-[#676767]">{customWidth * customLength} mq</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wider">Note per il Manager <span className="text-gray-400 font-normal">(opzionale)</span></label>
                    <textarea
                      value={managerRequests}
                      onChange={(e) => setManagerRequests(e.target.value)}
                      placeholder="Es. richieste particolari, domande, risposta al rifiuto precedente..."
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#676767] focus:border-transparent outline-none transition-all resize-none"
                    />
                  </div>

                  <div className="flex items-start gap-3 mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <input
                      type="checkbox"
                      id="booking-gdpr-consent"
                      checked={gdprConsent}
                      onChange={(e) => setGdprConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 text-[#676767] rounded border-gray-300 focus:ring-[#676767]"
                    />
                    <label htmlFor="booking-gdpr-consent" className="text-xs text-gray-600 leading-relaxed">
                      Dichiaro di aver letto l'<a href="/Template/Summeet_Informativa_Privacy_ECM.pdf" target="_blank" rel="noopener noreferrer" className="text-[#676767] underline hover:text-[#1d2127]">Informativa sulla Privacy</a> e acconsento al trattamento dei miei dati personali (inclusi i dati aziendali forniti) in conformità con il Regolamento (UE) 2016/679 (GDPR) per le finalità di gestione della prenotazione dello stand e comunicazioni relative all'evento. <span className="text-red-500">*</span>
                    </label>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <input
                      type="checkbox"
                      id="booking-gdpr-marketing"
                      checked={gdprMarketingConsent}
                      onChange={(e) => setGdprMarketingConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 text-[#676767] rounded border-gray-300 focus:ring-[#676767]"
                    />
                    <label htmlFor="booking-gdpr-marketing" className="text-xs text-gray-600 leading-relaxed">
                      (Facoltativo) Acconsento al trattamento dei miei dati per finalità di marketing e comunicazioni promozionali da parte dell'organizzatore dell'evento.
                    </label>
                  </div>
                </div>

                <div className="bg-[#676767]/5 p-5 rounded-2xl border border-[#676767]/10 flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-600">Investimento Totale:</span>
                  <span className="text-2xl font-black text-[#676767]">
                    {((reservingStand.price || 0) + 
                      (selectedTemplate.id === 'custom' ? (templatePrices['custom'] || 0) * (customWidth * customLength) : (templatePrices[selectedTemplate.id] || 0)) + 
                      selectedOptions.reduce((acc, optName) => {
                        const opt = availableOptions.find(o => o.name === optName);
                        return acc + (opt?.price || 0);
                      }, 0)).toLocaleString('it-IT')} €
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-4 bg-slate-50 rounded-b-2xl flex-shrink-0">
              <button 
                onClick={() => setReservingStand(null)} 
                className="px-6 py-3 text-gray-500 hover:text-gray-700 font-bold transition-colors text-sm"
              >
                Annulla
              </button>
              <button 
                onClick={handleConfirmReservation} 
                className="px-8 py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] shadow-lg shadow-blue-900/20 transition-all text-sm active:scale-95"
              >
                Invia Richiesta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stand Detail Modal (Occupied) */}
      {viewingStand && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full relative my-auto max-h-[95vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-[#676767]">Dettagli Stand: {viewingStand.label}</h3>
                <p className="text-xs text-gray-500">Informazioni sull'azienda espositrice</p>
              </div>
              <div className="flex items-center gap-2">
                {effectiveIsAdmin && !isEditingRequest && (
                  <button 
                    onClick={() => {
                      setIsEditingRequest(true);
                      setCompanyName(viewingStand.company || '');
                      setManagerRequests(viewingStand.manager_requests || '');
                      setCompetitors(viewingStand.competitors || '');
                      setNewPrice(viewingStand.price || 0);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-[#676767] rounded-lg text-xs font-bold hover:bg-blue-100 transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Modifica
                  </button>
                )}
                <button 
                  onClick={() => {
                    setViewingStand(null);
                    setIsEditingRequest(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-8 overflow-y-auto flex-grow space-y-6">
              <div className="text-center space-y-2">
                {isEditingRequest ? (
                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider text-left">Ragione Sociale</label>
                    <input 
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full text-lg font-bold text-gray-900 border-b-2 border-blue-200 focus:border-[#676767] outline-none pb-1 text-center"
                      placeholder="Nome Azienda"
                    />
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-black text-gray-900 leading-tight">{viewingStand.company}</h3>
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-[#676767] text-xs font-bold uppercase tracking-wider">
                      Stand {viewingStand.label}
                    </div>
                  </>
                )}
                {viewingStand.custom_dimensions && (
                  <div className="text-xs font-bold text-slate-500 mt-1">
                    Dimensioni: {viewingStand.custom_dimensions.width}m x {viewingStand.custom_dimensions.length}m ({viewingStand.custom_dimensions.width * viewingStand.custom_dimensions.length} mq)
                  </div>
                )}
              </div>
              

              <div className="space-y-6">
                {Array.isArray(viewingStand.options) && viewingStand.options.length > 0 && (
                  <div className="bg-white p-5 border border-gray-100 rounded-2xl shadow-sm">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Servizi Selezionati</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {viewingStand.options.map(opt => {
                        const option = availableOptions.find(o => o.name === opt);
                        return (
                          <div key={opt} className="flex items-center justify-between p-2.5 rounded-xl bg-green-50/50 border border-green-100/50">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm shadow-green-200"></div>
                              <span className="text-xs font-medium text-gray-700">{opt}</span>
                            </div>
                            {option && <span className="text-[10px] font-bold text-green-700">+{option.price}€</span>}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span>Prezzo Base Stand</span>
                        {isEditingRequest ? (
                          <div className="relative w-28">
                            <input 
                              type="number" 
                              value={newPrice}
                              onChange={(e) => setNewPrice(Number(e.target.value))}
                              className="w-full text-xs border border-blue-200 rounded-md pl-6 pr-2 py-1 outline-none focus:ring-1 focus:ring-[#676767]"
                            />
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]">€</span>
                          </div>
                        ) : (
                          <span>{viewingStand.price.toLocaleString('it-IT')} €</span>
                        )}
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Totale Opzioni</span>
                        <span>+ {viewingStand.options.reduce((acc, optName) => {
                          const opt = availableOptions.find(o => o.name === optName);
                          return acc + (opt?.price || 0);
                        }, 0).toLocaleString('it-IT')} €</span>
                      </div>
                      <div className="flex justify-between text-sm font-black text-[#676767] pt-1">
                        <span>Totale Complessivo</span>
                        <span>{((isEditingRequest ? newPrice : (viewingStand.price || 0)) + viewingStand.options.reduce((acc, optName) => {
                          const opt = availableOptions.find(o => o.name === optName);
                          return acc + (opt?.price || 0);
                        }, 0)).toLocaleString('it-IT')} €</span>
                      </div>
                    </div>
                  </div>
                )}

                {(isEditingRequest || viewingStand.manager_requests) && (
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Richieste Speciali</h4>
                    {isEditingRequest ? (
                      <textarea 
                        value={managerRequests}
                        onChange={(e) => setManagerRequests(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#676767] focus:border-transparent outline-none text-sm transition-all bg-white"
                        rows={3}
                      />
                    ) : (
                      <p className="text-sm text-gray-700 italic leading-relaxed font-medium">"{viewingStand.manager_requests}"</p>
                    )}
                  </div>
                )}

                {(isEditingRequest || viewingStand.competitors) && (
                  <div className={`p-5 rounded-2xl border ${isEditingRequest ? 'bg-slate-50 border-slate-100' : 'bg-red-50 border-red-100'}`}>
                    <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 flex items-center gap-2 ${isEditingRequest ? 'text-gray-400' : 'text-red-400'}`}>
                      <ShieldAlert className="w-3 h-3" /> Aziende Competitor
                    </h4>
                    {isEditingRequest ? (
                      <textarea 
                        value={competitors}
                        onChange={(e) => setCompetitors(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#676767] focus:border-transparent outline-none text-sm transition-all bg-white"
                        rows={2}
                      />
                    ) : (
                      <p className="text-sm text-red-700 italic leading-relaxed font-medium">"{viewingStand.competitors}"</p>
                    )}
                    
                    {effectiveIsAdmin && !isEditingRequest && viewingStand.status === 'pending' && (
                      <div className="mt-4 pt-4 border-t border-red-100 flex items-center justify-between">
                        <span className="text-xs font-bold text-red-700">Attiva Protezione Competitor</span>
                        <button
                          onClick={() => setProtectionActive(!protectionActive)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            protectionActive ? 'bg-red-500' : 'bg-gray-200'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            protectionActive ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    )}
                    {viewingStand.competitor_protection_active && viewingStand.status === 'occupied' && (
                      <div className="mt-2 flex items-center gap-2 text-[10px] font-black text-red-600 uppercase tracking-wider">
                        <CheckCircle2 className="w-3 h-3" /> Protezione Attiva
                      </div>
                    )}
                  </div>
                )}

                {effectiveIsAdmin && (viewingStand.gdprConsent !== undefined || viewingStand.gdprMarketingConsent !== undefined) && (
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 space-y-3">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Consensi Legali (GDPR)</h4>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${viewingStand.gdprConsent ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {viewingStand.gdprConsent ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                      </div>
                      <span className="text-xs text-gray-600">Privacy Policy e Trattamento Dati Personali</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${viewingStand.gdprMarketingConsent ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        {viewingStand.gdprMarketingConsent ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                      </div>
                      <span className="text-xs text-gray-600">Consenso Marketing e Comunicazioni Promozionali</span>
                    </div>
                  </div>
                )}

                {viewingStand.stand_preview_image && (
                  <div className="space-y-3">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Anteprima 3D Stand</h4>
                    <div className="relative aspect-video rounded-2xl overflow-hidden shadow-lg border border-gray-100 bg-slate-100">
                      <img 
                        src={viewingStand.stand_preview_image} 
                        alt="Stand Preview" 
                        className="w-full h-full object-contain" 
                        referrerPolicy="no-referrer" 
                      />
                      <div className="absolute top-4 right-4">
                        <a 
                          href={viewingStand.stand_preview_image} 
                          download={`stand-${viewingStand.label}-preview.png`}
                          className="p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-white transition-colors text-gray-700"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Document Section (Unified for Sponsor and Admin) */}
                {viewingStand && viewingStand.status !== 'available' && (viewingStand.sponsorId === user?.uid || getCurrentUserId(auth, user) || effectiveIsAdmin) && (
                  <div className="space-y-4">
                    {/* Traffic Light Component */}
                    <StatusTrafficLight stand={viewingStand} />
                    
                    <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100">
                      <h4 className="text-[10px] font-black text-[#676767] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> 
                        {effectiveIsAdmin ? 'Revisione Documenti Sponsor' : 'Documenti da Inviare'}
                      </h4>
                      <p className="text-xs text-gray-500 mb-4">
                        {effectiveIsAdmin 
                          ? 'Verifica i documenti caricati dallo sponsor. Puoi approvarli singolarmente o caricare file per conto dello sponsor.' 
                          : 'Carica i documenti richiesti per completare la partecipazione. I file in revisione sono evidenziati in giallo.'}
                      </p>
                      
                      <div className="space-y-2">
                        {STAND_DOCUMENT_TYPES.map(docType => {
                          const standDoc = viewingStand.documents?.[docType.id];
                          const isDisabled = viewingStand.status === 'occupied' || 
                            (viewingStand.status === 'payment_pending' && docType.id !== 'bank_receipt') ||
                            (viewingStand.status === 'payment_verification') ||
                            (viewingStand.status === 'expired');
                          
                          return (
                            <div key={docType.id} className="flex items-center justify-between p-3 bg-white border border-blue-100 rounded-xl shadow-sm">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-base flex-shrink-0">
                                  {standDoc ? (standDoc.status === 'approved' ? '✅' : standDoc.status === 'rejected' ? '❌' : '🟡') : '⬜'}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-gray-800 truncate">{docType.label}</p>
                                  {docType.required
                                    ? <span className="text-[9px] text-red-500 font-bold">Obbligatorio</span>
                                    : <span className="text-[9px] text-gray-400">Facoltativo</span>}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                {standDoc && (
                                  <a
                                    href={standDoc.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={standDoc.name || `${docType.id}.pdf`}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title={effectiveIsAdmin ? `Scarica: ${standDoc.name || docType.label}` : 'Vedi documento'}
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                
                                {docType.id === 'participation_app' && !standDoc && !effectiveIsAdmin && (
                                  <button
                                    onClick={generateParticipationApp}
                                    className="px-2 py-1 bg-[#676767] text-white rounded-lg text-[10px] font-bold hover:bg-[#1d2127] transition-colors whitespace-nowrap"
                                  >
                                    Genera Bozza
                                  </button>
                                )}
                                
                                {/* Upload Button (Visible to Owner and Admin) */}
                                {(!standDoc || standDoc.status === 'rejected' || effectiveIsAdmin) && (
                                  <div className="relative">
                                    <input
                                      type="file"
                                      accept=".pdf"
                                      disabled={isDisabled || isUploadingDoc}
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) await handleStandDocumentUpload(docType.id, file);
                                        e.target.value = '';
                                      }}
                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <button
                                      type="button"
                                      disabled={isDisabled || isUploadingDoc}
                                      className="px-2 py-1 bg-white border border-slate-300 text-slate-700 rounded-lg text-[10px] font-bold hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1 transition-colors whitespace-nowrap"
                                    >
                                      <Upload className="w-3 h-3" />
                                      {standDoc?.status === 'rejected' ? 'Ricarica' : 'Carica'}
                                    </button>
                                  </div>
                                )}

                                {/* Admin Approval Buttons */}
                                {effectiveIsAdmin && standDoc && standDoc.status === 'pending' && (
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handleApproveStandDocument(docType.id)}
                                      className="px-2 py-1 bg-green-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-green-700 transition-all shadow-sm"
                                      title="Approva"
                                    >
                                      <Check className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleRejectStandDocument(docType.id)}
                                      className="px-2 py-1 bg-red-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-red-700 transition-all shadow-sm"
                                      title="Rifiuta"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}

                                {standDoc && standDoc.status === 'pending' && !effectiveIsAdmin && (
                                  <span className="text-[10px] text-yellow-600 font-medium flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> In verifica
                                  </span>
                                )}
                                
                                {standDoc && standDoc.status === 'approved' && (
                                  <span className="text-[10px] text-green-600 font-bold flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" /> OK
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Contractor Name Section */}
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-bold text-[#676767] mb-1">Appaltatore / Fornitore Tecnico</p>
                        <p className="text-[10px] text-gray-500 mb-2">Specifica il nome del tuo appaltatore per i documenti tecnici (Moduli A/B, Progetto Stand, DURC).</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={contractorNameInput}
                            onChange={(e) => setContractorNameInput(e.target.value)}
                            placeholder="Ragione sociale / nome tecnico"
                            className="flex-1 px-3 py-2 bg-white border border-blue-200 rounded-xl text-xs focus:ring-2 focus:ring-[#676767] outline-none"
                          />
                          <button
                            onClick={async () => {
                              if (!viewingStand) return;
                              await updateStand(viewingStand.id, { contractorName: contractorNameInput });
                              setViewingStand(prev => prev ? { ...prev, contractorName: contractorNameInput } : null);
                              toast.success('Appaltatore salvato.');
                            }}
                            className="px-3 py-2 bg-[#676767] text-white rounded-xl text-xs font-bold hover:bg-[#1d2127] transition-colors"
                          >
                            Salva
                          </button>
                        </div>
                        {viewingStand.contractorName && (
                          <p className="text-[10px] text-green-600 mt-1.5 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> <span className="font-bold">{viewingStand.contractorName}</span>
                          </p>
                        )}
                      </div>

                      {/* Admin Global Actions */}
                      {effectiveIsAdmin && (
                        <div className="mt-4 pt-4 border-t border-blue-200 space-y-3">
                          {(viewingStand.status === 'document_review' || viewingStand.status === 'pending') && (
                            <button
                              onClick={() => handleApproveDocuments(viewingStand.id)}
                              className="w-full py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-900/10 flex items-center justify-center gap-2"
                            >
                              <CheckCircle2 className="w-4 h-4" /> 
                              {viewingStand.status === 'pending' ? 'Approvazione Forzata: Passa a Pagamento' : 'Approva Tutti i Documenti e Attiva Timer 96h'}
                            </button>
                          )}
                          
                          {viewingStand.status === 'payment_verification' && (
                            <button
                              onClick={handleConfirmPayment}
                              className="w-full py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-all shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2"
                            >
                              <CheckCircle2 className="w-4 h-4" /> Conferma Pagamento e Assegna Stand
                            </button>
                          )}

                          {viewingStand.status === 'payment_pending' && (
                            <div className="p-3 bg-orange-50 border border-orange-100 rounded-xl">
                              <p className="text-[10px] text-orange-700 font-bold flex items-center gap-2">
                                <Clock className="w-3 h-3" /> In attesa di pagamento (Timer 96h attivo)
                              </p>
                              {viewingStand.paymentExpiresAt && (
                                <p className="text-[9px] text-orange-600 mt-1">
                                  Scadenza: {new Date(viewingStand.paymentExpiresAt).toLocaleString('it-IT')}
                                </p>
                              )}
                            </div>
                          )}

                          {isEditingRequest && (
                            <div className="pt-4 border-t border-gray-200 flex gap-3">
                              <button
                                onClick={() => setIsEditingRequest(false)}
                                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
                              >
                                Annulla
                              </button>
                              <button
                                onClick={async () => {
                                  if (!viewingStand) return;
                                  try {
                                    const updateData = {
                                      company: companyName,
                                      manager_requests: managerRequests,
                                      competitors: competitors,
                                      price: newPrice
                                    };
                                    await updateStand(viewingStand.id, updateData);
                                    setViewingStand({ ...viewingStand, ...updateData });
                                    setIsEditingRequest(false);
                                    toast.success("Modifiche salvate con successo");
                                  } catch (error) {
                                    console.error("Error updating stand info:", error);
                                    toast.error("Errore nel salvataggio");
                                  }
                                }}
                                className="flex-1 py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-all shadow-lg shadow-blue-900/10"
                              >
                                Salva Modifiche
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {effectiveIsAdmin && viewingStand.status !== 'available' && (
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                      <UserIcon className="w-3.5 h-3.5" /> Informazioni Sponsor
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Azienda</span>
                        <span className="text-xs font-bold text-gray-800">{viewingStand.company}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Referente</span>
                        <span className="text-xs font-bold text-gray-800">{viewingStand.sponsorName || 'N/D'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Email</span>
                        <span className="text-xs font-bold text-[#676767]">{viewingStand.sponsorEmail || 'N/D'}</span>
                      </div>
                      {viewingStand.contractorName && (
                        <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                          <span className="text-xs text-gray-500">Appaltatore Tecnico</span>
                          <span className="text-xs font-bold text-gray-800">{viewingStand.contractorName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Admin: Document Review Section (Phase 4) - REMOVED AS UNIFIED ABOVE */}

                {viewingStand.status === 'pending' ? (
                  <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl">
                    <div className="flex items-center gap-3 text-amber-700 font-black text-sm mb-2">
                      <AlertCircle className="w-5 h-5" />
                      {effectiveIsAdmin ? 'RICHIESTA IN REVISIONE' : 'NEL CARRELLO'}
                    </div>
                    <p className="text-xs text-amber-600 leading-relaxed font-medium">
                      {effectiveIsAdmin
                        ? 'Lo sponsor ha inviato una richiesta di prenotazione. Come Event Manager, devi approvare o rifiutare questa richiesta.'
                        : 'Hai aggiunto questo stand al carrello. In attesa di approvazione da parte del manager.'}
                    </p>
                    {viewingStand.cartExpiresAt && (
                      <div className="mt-3 p-3 bg-amber-100 rounded-lg border border-amber-200">
                        <p className="text-xs font-bold text-amber-800 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Scadenza carrello: {new Date(viewingStand.cartExpiresAt).toLocaleString('it-IT')}
                        </p>
                        <p className="text-[10px] text-amber-700 mt-1">
                          Se non completi l'acquisto entro questa data, lo stand tornerà disponibile.
                        </p>
                      </div>
                    )}
                  </div>
                ) : viewingStand.status === 'document_review' ? (
                  <div className="p-5 bg-blue-50 border border-blue-200 rounded-2xl">
                    <div className="flex items-center gap-3 text-blue-700 font-black text-sm mb-2">
                      <FileText className="w-5 h-5" />
                      {effectiveIsAdmin ? 'FASE DOCUMENTI' : 'PROPOSTA ACCETTATA'}
                    </div>
                    <p className="text-xs text-blue-600 leading-relaxed font-medium">
                      {effectiveIsAdmin
                        ? 'Lo sponsor deve caricare i documenti richiesti. Verifica lo stato dei documenti nella sezione sopra.'
                        : 'La tua proposta è stata accettata! Carica i documenti richiesti per procedere alla fase di pagamento.'}
                    </p>
                  </div>
                ) : viewingStand.status === 'payment_pending' ? (
                  <div className="p-5 bg-orange-50 border border-orange-200 rounded-2xl">
                    <div className="flex items-center gap-3 text-orange-700 font-black text-sm mb-2">
                      <AlertCircle className="w-5 h-5" />
                      {effectiveIsAdmin ? 'IN ATTESA DI PAGAMENTO' : 'PROCEDI AL PAGAMENTO'}
                    </div>
                    <p className="text-xs text-orange-600 leading-relaxed font-medium">
                      {effectiveIsAdmin
                        ? 'I documenti sono stati approvati. Lo sponsor deve ora effettuare il pagamento.'
                        : 'I tuoi documenti sono stati approvati! Procedi al pagamento caricando la distinta di bonifico.'}
                    </p>
                  </div>
                ) : viewingStand.status === 'payment_verification' ? (
                  <div className="p-5 bg-purple-50 border border-purple-200 rounded-2xl">
                    <div className="flex items-center gap-3 text-purple-700 font-black text-sm mb-2">
                      <Clock className="w-5 h-5" />
                      {effectiveIsAdmin ? 'VERIFICA PAGAMENTO' : 'PAGAMENTO IN VERIFICA'}
                    </div>
                    <p className="text-xs text-purple-600 leading-relaxed font-medium">
                      {effectiveIsAdmin
                        ? 'Lo sponsor ha caricato la distinta di bonifico. Verifica il pagamento e conferma.'
                        : 'Il tuo pagamento è in fase di verifica da parte del manager. Ti contatteremo a breve.'}
                    </p>
                  </div>
                ) : viewingStand.status === 'occupied' ? (
                  <div className="p-5 bg-green-50 border border-green-200 rounded-2xl">
                    <div className="flex items-center gap-3 text-green-700 font-black text-sm mb-2">
                      <CheckCircle2 className="w-5 h-5" />
                      {effectiveIsAdmin ? 'STAND CONFERMATO' : 'PRENOTAZIONE CONFERMATA'}
                    </div>
                    <p className="text-xs text-green-600 leading-relaxed font-medium">
                      {effectiveIsAdmin
                        ? 'Il pagamento è stato confermato. Lo stand è assegnato a questa azienda.'
                        : 'Ottimo! La tua prenotazione è confermata. Lo stand è assegnato alla tua azienda.'}
                    </p>
                  </div>
                ) : viewingStand.status === 'available' && !effectiveIsAdmin ? (() => {
                  const rejectNotif = notifications.find(n =>
                    (n.type as string) === 'reservation_rejected' &&
                    ((n as any).relatedStandId === viewingStand.id || (n as any).related_stand_id === viewingStand.id)
                  );
                  if (!rejectNotif) return null;
                  const [baseMsg, reasonPart] = rejectNotif.message.split('\nMotivazione: ');
                  return (
                    <div className="p-5 bg-red-50 border border-red-200 rounded-2xl space-y-3">
                      <div className="flex items-center gap-3 text-red-700 font-black text-sm">
                        <XCircle className="w-5 h-5" />
                        PRENOTAZIONE RIFIUTATA
                      </div>
                      <p className="text-xs text-red-600 leading-relaxed font-medium">{baseMsg.replace('❌ ', '')}</p>
                      {reasonPart && (
                        <div className="p-3 bg-red-100 rounded-xl border border-red-200">
                          <p className="text-[10px] font-black text-red-800 uppercase tracking-wider mb-1">Motivazione admin</p>
                          <p className="text-xs text-red-700 leading-relaxed">{reasonPart}</p>
                        </div>
                      )}
                      <p className="text-xs text-red-500 font-medium">Lo stand è tornato disponibile. Puoi inviare una nuova richiesta.</p>
                    </div>
                  );
                })() : null}
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex flex-col sm:flex-row gap-3 bg-slate-50 rounded-b-2xl">
              {effectiveIsAdmin && ['pending', 'document_review', 'payment_pending', 'payment_verification'].includes(viewingStand.status) && (
                <>
                  <button
                    onClick={() => setRejectingStand(viewingStand)}
                    className="flex-1 px-6 py-3 border border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-50 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Rifiuta Prenotazione
                  </button>
                  <button
                    onClick={async () => {
                      // Accetta proposta → passa a document_review per caricare documenti
                      const documentsExpiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 giorni
                      await updateStand(viewingStand.id, {
                        status: 'document_review',
                        price: newPrice,
                        competitor_protection_active: protectionActive,
                        documentsExpiresAt: documentsExpiresAt.toISOString()
                      });

                      // Notifica sponsor
                      if (viewingStand.sponsorId) {
                        await api.notifications.create({
                          userId: viewingStand.sponsorId,
                          type: 'reservation_approved',
                          message: `✅ La tua proposta per lo stand ${viewingStand.label} è stata accettata! Hai 7 giorni per caricare i documenti obbligatori.`,
                          read: false,
                          relatedEventId: selectedEventId,
                          relatedStandId: viewingStand.id
                        });
                      }

                      // Email conferma accettazione
                      if (viewingStand.sponsorEmail) {
                        const replyToEmail = event.managerEmails ? event.managerEmails.split(',')[0]?.trim() : 'no-reply@summeet.it';

                        await apiPost('/api/mail', {
                          to: viewingStand.sponsorEmail,
                          replyTo: replyToEmail,
                          message: {
                            subject: `✅ Proposta Accettata — Stand ${viewingStand.label}`,
                            text: `La tua proposta per lo stand ${viewingStand.label} è stata accettata! Hai 7 giorni di tempo per caricare i documenti obbligatori (Modulo A, Modulo B, planimetria, ecc.). Accedi alla piattaforma per procedere.`,
                            html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
                              <div style="background:#676767;color:#fff;padding:24px;text-align:center"><h1 style="margin:0">Summeet</h1></div>
                              <div style="padding:24px;background:#f9fafb">
                                <h2 style="color:#676767">🎉 Congratulazioni!</h2>
                                <p>La tua proposta per lo stand <b>${viewingStand.label}</b> è stata <b>ACCETTATA</b>!</p>
                                <p>📋 <b>Prossimi passi:</b></p>
                                <ul style="line-height:1.8">
                                  <li>Carica i documenti obbligatori entro 7 giorni</li>
                                  <li>Attendi approvazione documenti da parte dell'organizzatore</li>
                                  <li>Procedi al pagamento entro 96 ore dall'approvazione</li>
                                </ul>
                                <p style="margin-top:20px"><a href="${window.location.origin}/?eventId=${selectedEventId}" style="display:inline-block;padding:12px 24px;background:#676767;color:#fff;text-decoration:none;border-radius:8px;font-weight:bold">Vai alla Piattaforma →</a></p>
                              </div>
                            </div>`
                          }
                        });
                      }

                      generateStandSummary();
                      setViewingStand(null);
                      toast.success('Proposta accettata! Sponsor notificato.');
                    }}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-900/20 active:scale-95 text-sm"
                  >
                    Approva Prenotazione
                  </button>
              </>)}
              {!effectiveIsAdmin && viewingStand.status === 'available' && (() => {
                const hasRejectNotif = notifications.some(n =>
                  (n.type as string) === 'reservation_rejected' &&
                  ((n as any).relatedStandId === viewingStand.id || (n as any).related_stand_id === viewingStand.id)
                );
                if (!hasRejectNotif) return null;
                return (
                  <button
                    onClick={() => {
                      setViewingStand(null);
                      setReservingStand({ ...viewingStand, price: viewingStand.price || 0 });
                      setCompanyName('');
                      setCompanyLogo('');
                      setSelectedOptions([]);
                      setManagerRequests('');
                      setGdprConsent(false);
                      setGdprMarketingConsent(false);
                    }}
                    className="flex-1 px-6 py-3 bg-[#676767] text-white rounded-xl font-bold hover:bg-[#1d2127] transition-all text-sm shadow-lg active:scale-95"
                  >
                    Invia Nuova Richiesta
                  </button>
                );
              })()}
              <button
                onClick={() => setViewingStand(null)}
                className="flex-1 px-6 py-3 bg-white border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-all text-sm shadow-sm"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
// Force rebuild ven  8 mag 2026 15:06:50
// Force rebuild ven  8 mag 2026 16:22:34
// FORCE_REBUILD_1778250334
